/*
 * bc-segment-diagram — <bc-segment-scene>, the Segment Builder's single
 * interactive diagram. The scene is the PRIMARY control surface of the page:
 *
 *   full view : top view of the whole line (top-half cross-section
 *               projection extruded along the length, rounded fold caps on
 *               looped ends, sleeve footprints, tail seams, and the sewing
 *               patch between each throat and tail seam) stacked over a
 *               side view of the whole configured segment (PDP webbing
 *               language: two-tone line, hairpin sewn loops, sleeve
 *               overlays, overlap tails, length dimension) — top-first,
 *               the same view order as the zoomed end view. A BACKUP line
 *               (and, sharing its rows, an EXTENDER) appends a band below
 *               that: the backup's side view drawn the other way up
 *               (mirrored — tails ABOVE the line), its own length
 *               dimension, and a bottom-view strip. The canvas height
 *               follows the model (see fullLayout); a mainline-only build
 *               renders exactly as it did before backups existed.
 *               Clickable hotspots (each END, and nothing else) dispatch
 *               `bc:segment:part-click` with detail.part —
 *               bc-segment-builder.js opens the matching editor panel.
 *               A line's MIDDLE is a hotspot on no role: the mainline's
 *               went 2026-07-31, the backup's and extender's 2026-08-01
 *               (once the stage row grew a per-line Change pill with a
 *               fused ×). Each length dimension carries ONE small hotspot
 *               — its own VALUE, drawn as a tappable chip — which opens
 *               that line's HTML editor pill in the builder's overlay.
 *
 *               FIVE ROUNDS on where the length is entered, because it is
 *               an HTML control at a fixed CSS size over a canvas that
 *               scales:
 *                 v1 the box sat IN a measured gap cut out of the
 *                    dimension line; on a tight band it covered its own
 *                    arrows.
 *                 v2 a stacked dimension row per band piece so two boxes
 *                    could not collide — still on the row ("the length box
 *                    covers the dimension arrow totally").
 *                 v3 boxes off the canvas into a strip below the stage;
 *                    rows display-only, value inline in the line.
 *                 v4 boxes back on the canvas, every row drawn complete
 *                    and a box floating permanently beside each — which
 *                    cost a reserved rung per line (+42% canvas height on
 *                    a three-line build).
 *                 v5 (current, Jerry's pick from an interactive mockup)
 *                    TAP-TO-EDIT: the drawing is v3's compact drafting
 *                    again — value inline in a text-sized gap in the line,
 *                    floated clear when the span is too short, band pieces
 *                    sharing ONE dimension row — and the VALUE is the
 *                    control. Tapping it opens THE editor pill (exactly
 *                    one on the whole scene) at that dimension, and while
 *                    it is open this row draws no value of its own: the
 *                    pill REPLACES the number rather than restating it.
 *               Orientation is per sewn loop and edits in the end editor;
 *               the webbing edits from the stage button or the build
 *               sheet's Webbing row; the length edits in the pill, opened
 *               by its own value or from the sheet's Length row.
 *   end view  : zoomed detail of ONE end (view 'end1' | 'end2') — the old
 *               <bc-segment-loop-detail> composition folded into the scene:
 *               top view strip with the sewing patch + side view loop with
 *               opening / fold / arc-length sleeve dimensions, mirrored so
 *               End 1 keeps its fold on the left like the full view. All
 *               dimension values here are STATIC labels — editing lives in
 *               the builder's end panel below the diagram. The opening
 *               dimension sits ABOVE the loop, the fold BELOW it, and the
 *               sleeve is a plain value inside the eye (no arc, no
 *               arrows), dropping to its own row under the fold when the
 *               eye is too small to hold it. Both views share ONE mm→px
 *               scale and the fold cap is parked at the canvas edge. The
 *               full→end transition animates the viewBox into the clicked
 *               loop before swapping compositions (skipped under
 *               prefers-reduced-motion).
 *
 * Geometry lineage: the LINE language stays in lockstep with
 * snippets/bc-diagram-webbing-svg.liquid (the PDP source of truth) — the
 * split-halves two-tone line construction, the hard-50%-stop sleeve
 * gradient, and the flipped-color overlap stripe at y = LINE_Y + t
 * (constants copied, not shared — mirror upstream changes here). The LOOPS
 * deliberately DIVERGE from the PDP teardrop (Jerry, 2026-07-29): both
 * views draw them as open HAIRPIN paths (see drawHairpin) whose strands
 * are literal continuations of the straight strokes, because at builder
 * scale the teardrop read as a detached oval.
 *
 * The element is render()-driven by bc-segment-builder.js with a plain
 * model:
 *
 *   { lines: [{ role:'main'|'backup'|'extender', entry, entryFlipped?,
 *               lengthText, lengthM?, end1, end2 }],
 *     qty, view, activePart, recommendation, lengthEditors, lengthOpen }
 *
 * with each end carrying
 *   { treatment, pattern, openingMm, sleeveLengthMm, foldLiveMm,
 *     tailUp, zoomFlipped, sleeveAvailable }
 *
 * `view` is 'full' or '<role>:end1|end2', `activePart` a '<role>:<part>'
 * id — both BARE for the mainline ('end1', 'length'), which is the
 * spelling the builder mints for it (its _partId) and the one the scene
 * emits back. Everything drawn comes from that object: the scene reaches
 * into nothing, and it draws only the lines it is handed.
 * `tailUp` is which way that END's loop folds (orientation is per sewn
 * loop since round 3 — the LINE never flips, its faces arrive resolved).
 * `zoomFlipped` + the line's `entryFlipped` are the pair the ZOOMED end
 * view draws from when that end's chosen face is the entry's other one;
 * the full view always draws `entry`, and a missing `entryFlipped` falls
 * back to it.
 * `lengthEditors` says there IS an overlay layer for a length editor to
 * open into, so each dimension's value draws as a tappable chip. False —
 * any embed of this element outside the builder — draws the value as plain
 * dimension text with no target: the figure still states every number it
 * ever did, it just isn't a control.
 * `lengthOpen` is the ONE anchor name whose editor pill is currently open
 * ('length' | 'backup:length' | 'extender:length' | null). That row SHOWS
 * no value: the pill is holding it, and one number in two places is a
 * proofreading exercise. Every row still DRAWS its chip and both of its
 * dimension-line variants on every pass — the open row's are hidden by a
 * class (see setLengthOpen / _drawLengthDim), not left out — which is what
 * makes opening and closing a pill free of a redraw.
 *
 * Outbound channels are the bubbling part-click / dim-drag / end-action /
 * scene-rendered events, plus ONE pull-only lookup: anchorPoint(name)
 * resolves a NAMED ANCHOR ('length', 'backup:length', 'extender:length')
 * to client pixels, which is how the builder pins the open pill at the
 * dimension it belongs to. Every row publishes its anchor on every draw,
 * open or not — the pill is re-pinned after each redraw and the span moves
 * under it on every keystroke — offset ED_CLEAR off the line, so the
 * drawing owns where the pill goes.
 *
 * INBOUND, besides render(): setLengthOpen(anchor|null) moves the open
 * state on the ALREADY DRAWN svg (2026-08-02, Jerry: "the custom length
 * loads very slowly with this new setup"). Opening a pill changes nothing
 * a redraw would compute — the anchors, the bands, the row layout are all
 * independent of it — so the builder no longer pays a full _draw
 * (replaceChildren + every path, per-piece, on a heavy page) to hide one
 * chip. The model it holds is updated with it, so the next real render
 * agrees with what the class toggle already did.
 *
 * There is deliberately NO measurement channel back the other way. Until
 * 2026-08-02 the builder fed each editor's measured width back as the gap
 * to leave in its dimension line, and the re-render that caused fired
 * scene-rendered straight back into the measurement. The gap a row cuts is
 * sized from its own VALUE now — text this file draws and can measure with
 * arithmetic — so that loop is gone for good; resurrecting it would be
 * resurrecting the bug it was built to serve.
 *
 * Also defines <bc-sewing-figure> (bottom of file): the PDP sewing-docs
 * figure that re-renders the zoomed end composition (loops, at default
 * dimensions) or a join-stitch top view on the product pages — the reason
 * this file is also loaded by snippets/bc-sewing.liquid.
 */
(() => {
  const SVG_NS = 'http://www.w3.org/2000/svg';

  // ---- shared helpers ------------------------------------------------------

  function el(tag, attrs) {
    const node = document.createElementNS(SVG_NS, tag);
    if (attrs) {
      Object.keys(attrs).forEach((k) => {
        if (k === 'text') node.textContent = attrs[k];
        else node.setAttribute(k, attrs[k]);
      });
    }
    return node;
  }

  function clamp(min, v, max) {
    return Math.min(max, Math.max(min, v));
  }

  // ---- thread-color contrast ------------------------------------------------
  // The stitching is always drawn in the thread's color, but the SHADE is
  // pushed lighter (on dark webbing) or darker (on light webbing) until it
  // reads against whatever it sits on — hue preserved, visibility guaranteed.

  function hexRgb(c) {
    if (typeof c !== 'string') return null;
    const m = c.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (!m) return null;
    let h = m[1];
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  }

  function relLum(rgb) {
    const f = (v) => {
      const x = v / 255;
      return x <= 0.04045 ? x / 12.92 : (((x + 0.055) / 1.055) ** 2.4);
    };
    return 0.2126 * f(rgb[0]) + 0.7152 * f(rgb[1]) + 0.0722 * f(rgb[2]);
  }

  function contrastRatio(a, b) {
    const hi = Math.max(a, b);
    const lo = Math.min(a, b);
    return (hi + 0.05) / (lo + 0.05);
  }

  // Shade `color` (hex) toward white or black until it clears ~2.2:1 on
  // `bg`, moving in the color's NATURAL direction — darker-than-the-bg
  // threads darken, lighter ones lighten — so a red stays recognisably
  // red instead of washing out to near-white on a mid-luminance webbing.
  // Non-hex inputs (CSS color names from the emitter fallback) pass
  // through untouched — no parsing, no adjustment.
  function contrastInk(color, bg) {
    const c = hexRgb(color);
    const g = bg && hexRgb(bg);
    if (!c || !g) return color;
    const bgLum = relLum(g);
    const lighten = relLum(c) >= bgLum;
    let cur = c;
    for (let i = 0; i < 10 && contrastRatio(relLum(cur), bgLum) < 2.2; i += 1) {
      cur = cur.map((v) => (lighten
        ? Math.min(255, Math.round(v + (255 - v) * 0.22))
        : Math.round(v * 0.78)));
    }
    return `#${cur.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
  }

  // Validated + contrast-shaded thread color; `fallback` when the raw value
  // isn't a usable CSS color at all.
  function threadInk(raw, bg, fallback) {
    const ok = raw && typeof CSS !== 'undefined' && CSS.supports('color', raw);
    if (!ok) return fallback;
    return contrastInk(raw, bg);
  }

  // Hard-50%-stop sleeve gradient (PDP construction). `sideOut` says which
  // horizontal half of the path bbox is the OUTER half the sleeve covers:
  // 'left' for a left-bulging loop, 'right' for a right-bulging one. The
  // stop can be moved off 50% (coverage 0..1 of the bbox from the outer
  // edge) for the sleeve-length-driven wrap.
  // The 0.02/0.98 rails are only there so a stop never lands exactly on an
  // edge — sleeveCoverage is already bounded by the real geometry, and the
  // old 0.95 rail sat INSIDE the reachable range (a 300 mm opening at the
  // builder's sleeve cap is 0.97), which would have re-flattened the side
  // view's sleeve at the top of the range while the top view kept growing
  // (Jerry, 2026-07-31).
  function sleeveGradient(id, color, sideOut, coverage) {
    const c = coverage == null ? 0.5 : clamp(0.02, coverage, 0.98);
    const g = el('linearGradient', { id });
    const stops = sideOut === 'left'
      ? [[0, 1], [c, 1], [c, 0], [1, 0]]
      : [[0, 0], [1 - c, 0], [1 - c, 1], [1, 1]];
    stops.forEach(([off, op]) => {
      g.appendChild(el('stop', { offset: `${off * 100}%`, 'stop-color': color, 'stop-opacity': op }));
    });
    return g;
  }

  // The eye's DRAWN length in mm: the end's own opening when it has one,
  // else the pattern's catalog default, else a nominal 150 (a pattern whose
  // opening is prose-only — `openingText` — carries no number to draw).
  // Floored at 30 so a pathological opening still leaves a hairpin with an
  // eye in it. ONE definition on purpose: endGeometry's eyeMm, the full
  // view's per-end scale and sleeveCoverage's denominator have to agree, or
  // the sleeve stops being drawn at the scale of the eye it sits on.
  function eyeMmFor(end) {
    const p = (end && end.pattern) || null;
    const opening = end && end.openingMm != null ? end.openingMm
      : (p && p.openingMm != null ? p.openingMm : 150);
    return Math.max(30, opening);
  }

  // The builder caps a sleeve at (opening − 10) × 2 mm, i.e. 10 mm of eye
  // stays bare on each side of the fold (sleeveCapForOpening in
  // bc-segment-builder.js — keep the two in step). As a coverage fraction
  // that cap is (eye − 10)/eye: 0.80 at a 50 mm opening (the smallest a
  // sleeve is allowed on at all), 0.92 at 130, 0.97 at 300.
  const SLEEVE_EYE_GAP_MM = 10;
  // Garbage guard only, deliberately set BELOW anything the builder can
  // reach: its smallest sleeve (the sewing type's sleeveMinMm — 50 mm when
  // the metaobject leaves it blank) against its widest opening bound (a
  // pattern's openingMaxMm — 500 mm on the same fallback) is
  // 50/(2 × 500) = 0.05, and the shipped loop patterns cap the opening at
  // 300, i.e. 0.083. So this never rounds off a real sleeve; it only stops
  // one that arrived from somewhere else (a stale permalink, a metaobject
  // edited past its own bounds) from drawing as a hairline that reads as a
  // rendering artifact rather than as a short sleeve.
  const SLEEVE_MIN_COVERAGE = 0.03;

  // Visual sleeve wrap: the fraction of the eye's x-extent — measured from
  // the fold — that the sleeve covers. TRUE SCALE (Jerry, 2026-07-31): a
  // sleeve of length S is folded over the fold along with the webbing, so
  // it runs S/2 along the eye, and the eye's x-extent IS the opening —
  //
  //     coverage = (S / 2) / eye
  //
  // Every call site multiplies this by its own view's eyePx (= eye ×
  // pxPerMm), so the drawn sleeve lands at (S / 2) × pxPerMm: the same
  // millimetre as everything else in that view, in the full scene, the
  // zoomed end and the PDP docs figure alike.
  //
  // This was clamp(0.25, …, 0.65), which lied at BOTH ends of the range
  // (Jerry, 2026-07-31). The floor bit at exactly S = eye/2, so every
  // shorter sleeve — half the builder's range — drew the same size and the
  // input read as broken; the ceiling flattened everything past
  // S = 1.3 × eye even though the builder allows up to (eye − 10) × 2. All
  // that survives is the geometric cap above plus the garbage floor, so the
  // whole allowed range is strictly monotonic: the cap is reached only AT
  // the builder's own maximum, where an out-of-bounds sleeve also parks
  // instead of swallowing the eye.
  //
  // Fallback: a sleeved end carrying no length reads as the 200 mm catalog
  // default the builder itself falls back to; the eye comes from eyeMmFor,
  // so the denominator is whatever the view actually drew.
  function sleeveCoverage(end) {
    const eyeMm = eyeMmFor(end);            // ≥ 30, so the cap below is ≥ 0.66
    const sleeveMm = (end && end.sleeveLengthMm) || 200;
    return clamp(SLEEVE_MIN_COVERAGE, (sleeveMm / 2) / eyeMm, (eyeMm - SLEEVE_EYE_GAP_MM) / eyeMm);
  }

  // The doubled-back tail stripe (colors flipped vs the main line — the
  // underside of the folded webbing), PDP overlap construction. x2 is the
  // tail's CUT END, drawn rounded (Jerry, 2026-07-30): the stripe clips
  // to a strip path rounded only at the x2 side, so the two-tone halves
  // share one smooth semicircular cap; the x1 (throat) side stays square
  // under the loop layers.
  // A tail folds to whichever side its loop does — the caller supplies
  // that y — but its FACES don't change with the direction: the webbing
  // turns over on itself either way, so the underside is what shows on the
  // tail's outer half. Hence no direction parameter here.
  function drawOverlap(parent, x1, x2, y, t, topColor, lowerColor, twoTone) {
    const g = el('g');
    const lo = Math.min(x1, x2);
    const hi = Math.max(x1, x2);
    const clipId = `bc-seg-ovclip-${(uidCounter += 1)}`;
    const clip = el('clipPath', { id: clipId });
    clip.appendChild(el('path', { d: stripPath(lo, hi, y - t / 2, t, x2 < x1, x2 > x1) }));
    g.appendChild(clip);
    const inner = el('g', { 'clip-path': `url(#${clipId})` });
    if (twoTone && lowerColor) {
      inner.appendChild(el('line', { x1, y1: y - t / 4, x2, y2: y - t / 4, stroke: lowerColor, 'stroke-width': t / 2, 'stroke-linecap': 'butt' }));
      inner.appendChild(el('line', { x1, y1: y + t / 4, x2, y2: y + t / 4, stroke: topColor, 'stroke-width': t / 2, 'stroke-linecap': 'butt' }));
    } else {
      inner.appendChild(el('line', { x1, y1: y, x2, y2: y, stroke: topColor, 'stroke-width': t, 'stroke-linecap': 'butt' }));
    }
    g.appendChild(inner);
    parent.appendChild(g);
    return g;
  }

  // Small filled dimension arrowhead, tip at (x, y), pointing along dir.
  function dimArrow(x, y, dir, size, cls) {
    const s = size || 8;
    return el('path', {
      class: cls || 'bc-segment__dim-arrow',
      d: `M ${x} ${y} L ${x - dir * s} ${y - s * 0.5} L ${x - dir * s} ${y + s * 0.5} Z`,
    });
  }

  // Small stroke-drawn pencil, ~`s` units square, top-left of its box at
  // (x, y). Feather's `edit-2` outline (a diagonal body, an eraser cap at
  // the top-right, a nib at the bottom-left), normalised out of its 24-unit
  // artboard — ink there spans x 3..19.6, y 3..20, so 3/17 is the origin
  // and 1/17 the scale — and re-emitted at the size asked for, in real
  // user units. Emitting the numbers rather than transform-scaling a
  // 24-unit path keeps stroke-width honest: brand.css can then say 1.2 and
  // mean 1.2 on the canvas, instead of 1.2 × whatever scale the call site
  // chose. (`vector-effect: non-scaling-stroke` would have been the other
  // way out, and it is wrong here: this viewBox scales with its container,
  // so a screen-px stroke would fatten the glyph as the stage narrows.)
  //
  // Sole user is the length-value chip (_drawLengthDim), where it sits
  // beside the number as the second half of the at-rest "this is editable"
  // cue (Jerry, 2026-08-02: "also throw a pencil icon on those lengths to
  // show that they are editable"). Note the precedent it is NOT repeating:
  // a bare pencil-in-a-circle badge on the loops was rejected on
  // 2026-07-31 as guesswork at loop-eye size, and replaced by the word
  // "Edit" (see editBadge). A glyph on its own has to carry the whole
  // meaning; this one is pinned to the number it edits and to an underline
  // already saying "control", so it only has to confirm what they imply.
  function penIcon(x, y, s, cls) {
    const n = (v) => +v.toFixed(2);
    const p = (u, v) => `${n(x + u * s)} ${n(y + v * s)}`;
    const r = n(0.125 * s);
    return el('path', {
      class: cls || 'bc-segment__lenchip-pen',
      fill: 'none',
      d: `M ${p(0.794, 0.029)} A ${r} ${r} 0 0 1 ${p(0.971, 0.206)}`
        + ` L ${p(0.235, 0.941)} L ${p(0, 1)} L ${p(0.059, 0.765)} Z`,
    });
  }

  // Curved (arc-length) dimension: a dimension curve concentric with a
  // stroked arc — ISO arc-length style, the value reading as "measured
  // along the curve". Used for the sleeve length, whose value IS the
  // length of material along the loop.
  //
  // The covered arc is derived by sampling the SAME loop path the sleeve
  // gradient masks (split at the same bbox x — `covered(p)` is the
  // per-sample predicate, mirroring the gradient's outer-half rule), so the
  // dimension always spans exactly the painted sleeve. Each sample is
  // offset along the PATH NORMAL (radial-from-centre hugs the strands of a
  // flattened loop instead of sitting outside it), signed away from the
  // eye centre; arrowheads are auto-oriented markers. Returns null when
  // the geometry APIs fail (e.g. some engines on display:none subtrees) —
  // caller falls back to a straight dimension. On success returns the
  // label anchor {labelX, labelY}; the label itself is only drawn when
  // opts.label is non-null (the builder overlays an input otherwise).
  //
  // Sole caller since 2026-07-31 is the PDP docs loop side view: the
  // builder's zoomed end view names its sleeve in words instead (see
  // drawEndDetail), so the "draw the arc inside the eye" variant this
  // briefly grew is gone with it.
  function drawArcDim(g, defs, opts) {
    const { d, cx, cy, covered, offset, label, uid, cls } = opts;
    const probe = el('path', { d, fill: 'none', stroke: 'none' });
    g.appendChild(probe);
    let total = 0;
    try { total = probe.getTotalLength(); } catch (_e) { total = 0; }
    if (!total || !isFinite(total)) { g.removeChild(probe); return null; }
    const N = 72;
    const pts = [];
    for (let i = 0; i <= N; i += 1) pts.push(probe.getPointAtLength((total * i) / N));
    g.removeChild(probe);

    // Longest contiguous run of samples inside the covered (sleeved) region.
    // The path starts at the throat, which is never sleeved, so the covered
    // arc never wraps around the sample seam.
    const runs = [];
    let cur = null;
    pts.forEach((p, i) => {
      if (covered(p)) {
        if (cur) cur[1] = i;
        else { cur = [i, i]; runs.push(cur); }
      } else cur = null;
    });
    if (!runs.length) return null;
    // Closed path: a covered arc spanning the sample seam splits into a
    // head run (from 0) + a tail run (to N) — stitch them back together.
    // (Can't happen for the teardrop — its start point, the throat, is
    // never sleeved — but keeps the helper honest for reuse.)
    let indices;
    const first = runs[0];
    const last = runs[runs.length - 1];
    if (runs.length > 1 && first[0] === 0 && last[1] === N) {
      indices = [];
      for (let i = last[0]; i <= last[1]; i += 1) indices.push(i);
      for (let i = first[0]; i <= first[1]; i += 1) indices.push(i);
    } else {
      const run = runs.reduce((a, b) => ((b[1] - b[0]) > (a[1] - a[0]) ? b : a));
      indices = [];
      for (let i = run[0]; i <= run[1]; i += 1) indices.push(i);
    }
    const arc = [];
    for (const i of indices) {
      const p = pts[i];
      const prev = pts[Math.max(0, i - 1)];
      const next = pts[Math.min(N, i + 1)];
      const tl = Math.hypot(next.x - prev.x, next.y - prev.y) || 1;
      let nx = -(next.y - prev.y) / tl;
      let ny = (next.x - prev.x) / tl;
      if (nx * (p.x - cx) + ny * (p.y - cy) < 0) { nx = -nx; ny = -ny; }
      arc.push([p.x + nx * offset, p.y + ny * offset]);
    }
    if (arc.length < 3) return null;

    const markerId = `bc-arcarrow-${uid}`;
    if (!defs.querySelector(`#${markerId}`)) {
      const marker = el('marker', {
        id: markerId,
        viewBox: '0 0 10 10',
        refX: 8, refY: 5,
        markerWidth: 9, markerHeight: 9,
        markerUnits: 'userSpaceOnUse',
        orient: 'auto-start-reverse',
      });
      marker.appendChild(el('path', { d: 'M 0 1.5 L 8 5 L 0 8.5 Z', class: 'bc-segment__dim-arrow' }));
      defs.appendChild(marker);
    }

    const wrap = el('g', { class: `bc-segment__dim${cls ? ` ${cls}` : ''}`, 'aria-hidden': 'true' });
    let minX = Infinity; let maxX = -Infinity; let minY = Infinity;
    arc.forEach(([x, y]) => {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
    });
    let labelX = (minX + maxX) / 2;
    if (opts.maxLabelX != null) labelX = Math.min(labelX, opts.maxLabelX);
    if (opts.minLabelX != null) labelX = Math.max(labelX, opts.minLabelX);

    // Inline label (docs figures): the value sits IN a gap in the arc's
    // top run — the Dimensions diagram's value-on-the-line construction,
    // bent. The gap is cut only from samples near the label anchor on the
    // TOP pass (the arc passes each x twice); when the cut doesn't yield
    // two segments the label falls back above the arc.
    let labelY = minY - 7;
    let segs = [arc];
    if (label != null && opts.inlineLabel) {
      const half = label.length * 3 + 8;
      const kept = [];
      let cur = null;
      arc.forEach((pt) => {
        if (Math.abs(pt[0] - labelX) < half && pt[1] < minY + 12) { cur = null; return; }
        if (!cur) { cur = []; kept.push(cur); }
        cur.push(pt);
      });
      const usable = kept.filter((s) => s.length >= 2);
      if (usable.length >= 2) {
        segs = usable;
        labelY = minY + 4;
      }
    }
    segs.forEach((seg, i) => {
      const attrs = {
        class: 'bc-segment__dim-line bc-segment__arc-dim',
        d: 'M ' + seg.map((pt) => `${pt[0].toFixed(2)} ${pt[1].toFixed(2)}`).join(' L '),
        fill: 'none',
      };
      if (i === 0) attrs['marker-start'] = `url(#${markerId})`;
      if (i === segs.length - 1) attrs['marker-end'] = `url(#${markerId})`;
      wrap.appendChild(el('path', attrs));
    });
    if (label != null) {
      wrap.appendChild(el('text', {
        class: 'bc-segment__dim-label',
        x: labelX,
        y: labelY,
        'text-anchor': 'middle',
        text: label,
      }));
    }
    g.appendChild(wrap);
    return { labelX, labelY };
  }

  // Secondary dimension (kit-scene language): witness ticks, arrowed line,
  // label above the line. `label: null` + `o.gap` draws just the frame
  // with a centre gap — the builder overlays an HTML input there.
  // `o.inline` puts the VALUE in a gap in the line instead (the
  // Dimensions diagram's construction; gap width estimated from the
  // label length at the 11px dim-label size) — when the span can't fit
  // the gap the label falls back to o.labelGap off an unbroken line.
  function drawDim(parent, x1, x2, y, label, opts) {
    const o = opts || {};
    let gap = o.gap;
    let labelGap = o.labelGap;
    if (o.inline && label != null) {
      gap = label.length * 6 + 10;
      if (x2 - x1 > gap + 24) labelGap = -4;
    }
    const g = el('g', { class: `bc-segment__dim${o.cls ? ` ${o.cls}` : ''}`, 'aria-hidden': 'true' });
    const tick = o.tick == null ? 8 : o.tick;
    if (tick > 0) {
      g.appendChild(el('line', { class: 'bc-segment__dim-ext', x1, y1: y - tick, x2: x1, y2: y + tick }));
      g.appendChild(el('line', { class: 'bc-segment__dim-ext', x1: x2, y1: y - tick, x2, y2: y + tick }));
    }
    const cx = (x1 + x2) / 2;
    if (gap > 0 && x2 - x1 > gap + 24) {
      g.appendChild(el('line', { class: 'bc-segment__dim-line', x1, y1: y, x2: cx - gap / 2, y2: y }));
      g.appendChild(el('line', { class: 'bc-segment__dim-line', x1: cx + gap / 2, y1: y, x2, y2: y }));
    } else {
      g.appendChild(el('line', { class: 'bc-segment__dim-line', x1, y1: y, x2, y2: y }));
    }
    g.appendChild(dimArrow(x1, y, -1, o.arrow || 7));
    g.appendChild(dimArrow(x2, y, 1, o.arrow || 7));
    if (label != null) {
      g.appendChild(el('text', {
        class: `bc-segment__dim-label${o.labelCls ? ` ${o.labelCls}` : ''}`,
        x: cx,
        y: y - (labelGap == null ? 6 : labelGap),
        'text-anchor': 'middle',
        text: label,
      }));
    }
    parent.appendChild(g);
    return g;
  }

  // Faint vertical extension (witness) line: a reference vertical from
  // the measured geometry down to its dimension row, drawn at 35% so it
  // reads as reference, not geometry. Shared by the docs side views and
  // the builder's zoomed end view.
  function drawExtLine(parent, x, y1, y2) {
    const g = el('g', { class: 'bc-segment__dim', 'aria-hidden': 'true' });
    g.appendChild(el('line', {
      class: 'bc-segment__dim-ext', x1: x, y1, x2: x, y2, 'stroke-opacity': 0.35,
    }));
    parent.appendChild(g);
  }

  // Sewn-loop hairpin for the zoomed end view: an OPEN path whose strands
  // are literal continuations of the straight lines — the upper strand
  // leaves the main line at the throat with a horizontal tangent, bows up
  // over the eye, turns 180° at the fold (rounded cap) and comes back into
  // the doubled-back tail. Two-tone mode strokes two concentric offset
  // hairpins: the OUTER one starts on the main line's top-half centreline
  // and ends on the tail's bottom half, the INNER one on the line's bottom
  // half and the tail's top half — exactly the split-halves construction of
  // the straight strokes, so color continuity across the throat is exact
  // by construction (the tail's flipped colors fall out of the same
  // geometry: the inside of the loop IS the underside of the webbing).
  // `mirrorY` (optional) reflects the whole path about that horizontal —
  // the scene's backup band, whose loops fold the other way (tail ABOVE
  // the line). Every y reflects and the fold cap's sweep flips with it.
  // Mirroring the numbers here rather than wrapping the result in an SVG
  // transform keeps drawHairpin's returned geometry — which the
  // dimensions, the "Edit" pill and the hotspots all hang off — in real
  // canvas coordinates.
  function hairpinPath(throatX, dir, foldX, startY, topY, botY, endY, mirrorY) {
    const capR = (botY - topY) / 2;
    const capStartX = foldX - dir * capR;
    const span = Math.abs(capStartX - throatX);
    const X = (t) => throatX + dir * t * span;
    const M = mirrorY == null ? (y) => y : (y) => 2 * mirrorY - y;
    const sweep = (dir === 1) === (mirrorY == null) ? 1 : 0;
    return `M ${X(0)} ${M(startY)} `
      + `C ${X(0.45)} ${M(startY)} ${X(0.55)} ${M(topY)} ${X(1)} ${M(topY)} `
      + `A ${capR} ${capR} 0 0 ${sweep} ${X(1)} ${M(botY)} `
      + `C ${X(0.55)} ${M(botY)} ${X(0.45)} ${M(endY)} ${X(0)} ${M(endY)}`;
  }

  // `flipY` mirrors the loop about its own line y (see hairpinPath) — one
  // END folding the other way, not the whole piece: the LINE it grows out
  // of is drawn the same either way. So the two-tone strands swap with the
  // mirror, because colour continuity at the throat is what the whole
  // split-halves construction exists for: the strand that leaves the
  // line's TOP half must be the top face's colour, and under the mirror
  // that is the other one of the pair.
  function drawHairpin(parent, opts) {
    const { throatX, dir, eyePx, lineY, t, halfH, sleeveT, topColor, lowerColor, twoTone, sleeved, gradId, flipY } = opts;
    const mirrorY = flipY ? lineY : null;
    const M = (y) => (flipY ? 2 * lineY - y : y);
    const tailY = lineY + t;
    const midY = lineY + t / 2;
    const foldX = throatX + dir * eyePx;
    const hp = (off) => hairpinPath(
      throatX, dir, foldX + dir * off,
      lineY - off, midY - halfH - off, midY + halfH + off, tailY + off, mirrorY
    );
    const g = el('g');
    const dCenter = hp(0);
    if (twoTone && lowerColor) {
      g.appendChild(el('path', { d: hp(t / 4), fill: 'none', stroke: flipY ? lowerColor : topColor, 'stroke-width': t / 2, 'stroke-linecap': 'butt' }));
      g.appendChild(el('path', { d: hp(-t / 4), fill: 'none', stroke: flipY ? topColor : lowerColor, 'stroke-width': t / 2, 'stroke-linecap': 'butt' }));
    } else {
      g.appendChild(el('path', { d: dCenter, fill: 'none', stroke: topColor, 'stroke-width': t, 'stroke-linecap': 'butt' }));
    }
    if (sleeved && gradId) {
      g.appendChild(el('path', {
        d: dCenter, fill: 'none', stroke: `url(#${gradId})`, 'stroke-width': sleeveT, 'stroke-linecap': 'butt',
      }));
    }
    parent.appendChild(g);
    // Painted extents, in canvas coordinates: under a mirror the eye's
    // "top" edge is the reflected bottom one, so they swap.
    const edgeA = M(midY - halfH);
    const edgeB = M(midY + halfH);
    return {
      g, dCenter, foldX, midY: M(midY),
      topY: Math.min(edgeA, edgeB), botY: Math.max(edgeA, edgeB),
    };
  }

  // Where a value can sit INSIDE a hairpin eye: the x window over which
  // the eye is at least `needHalf` tall (half-height from the centreline),
  // measured on the very construction drawHairpin draws — the top strand's
  // cubic up to the fold cap, then the cap's own circle. The cubic's
  // height profile is a smoothstep in the Bézier parameter u (its two
  // y-controls sit ON the endpoints' levels), so B(u) = 3u² − 2u³ and the
  // x it lands at is the Bézier's own T(u); the cap is a plain circle of
  // radius halfH. Returns null when the eye never gets that tall.
  function eyeTextWindow(o) {
    const { throatX, dir, eyePx, halfH, webT, needHalf } = o;
    const capR = halfH;
    const span = eyePx - capR;
    if (span <= 0 || needHalf > halfH) return null;
    const base = webT / 2;            // the eye's half-height AT the throat
    const q = (needHalf - base) / Math.max(0.001, halfH - base);
    let lo = null;
    if (q <= 0) lo = throatX;
    else {
      for (let i = 0; i <= 40 && lo == null; i += 1) {
        const u = i / 40;
        if (3 * u * u - 2 * u * u * u >= q) {
          const t = 1.35 * u * (1 - u) ** 2 + 1.65 * u * u * (1 - u) + u ** 3;
          lo = throatX + dir * span * t;
        }
      }
    }
    if (lo == null) return null;
    const capStartX = throatX + dir * span;
    const hi = capStartX + dir * Math.sqrt(Math.max(0, capR * capR - needHalf * needHalf));
    return { lo, hi, width: Math.abs(hi - lo), cx: (lo + hi) / 2 };
  }

  // Corner radius for folded ends on top-view strips: a rounded-corner
  // rectangle, NOT a stadium — a full half-circle cap reads as an extreme
  // bullnose, but a fold seen from above keeps almost the full width.
  // PROPORTIONAL to the strip height (ratio matching the zoomed end
  // view's look) so the full view's shorter strips don't read rounder
  // than the zoom (Jerry, 2026-07-30); the 10-unit cap keeps tall
  // strips subtle.
  function stripCapR(h) {
    return Math.min(h * 0.3, 10);
  }

  // Rounded-corner end path shared by the strip and the sleeve footprint:
  // corner arcs radius r on the capped side, square on the other. `rMax`
  // (optional) caps the radius for a path shorter than its own corners —
  // the short-sleeve footprint, see drawTopSleeve.
  function stripPath(xL, xR, y, h, roundLeft, roundRight, rMax) {
    const r = rMax == null ? stripCapR(h) : Math.min(stripCapR(h), rMax);
    let d = `M ${xL + (roundLeft ? r : 0)} ${y} L ${xR - (roundRight ? r : 0)} ${y}`;
    if (roundRight) {
      d += ` A ${r} ${r} 0 0 1 ${xR} ${y + r} L ${xR} ${y + h - r} A ${r} ${r} 0 0 1 ${xR - r} ${y + h}`;
    } else {
      d += ` L ${xR} ${y} L ${xR} ${y + h}`;
    }
    d += ` L ${xL + (roundLeft ? r : 0)} ${y + h}`;
    if (roundLeft) {
      d += ` A ${r} ${r} 0 0 1 ${xL} ${y + h - r} L ${xL} ${y + r} A ${r} ${r} 0 0 1 ${xL + r} ${y}`;
    }
    d += ' Z';
    return d;
  }

  // Top-face strip: the cross-section's TOP HALF extruded along the length
  // (see snippets/bc-segment-webbing-json.liquid for the projection rule).
  // Convention: topFace.edges[0] = cross-section LEFT cap -> the strip's
  // TOP edge (flip here + in the snippet comment if that ever changes).
  // Rounded corners (roundLeft/roundRight) mark folded loop ends; square
  // caps are cut ends. Marks: x is mm from the flat region's start
  // (= thicknessMm/2 in from the physical edge), w is the width in mm.
  function drawTopStrip(parent, opts) {
    const { x, y, len, h, topFace, widthMm, thicknessMm, clipId, roundLeft, roundRight } = opts;
    const g = el('g');
    const face = topFace || {};
    const base = face.base || '#2b2b2b';
    const dShape = stripPath(x, x + len, y, h, roundLeft, roundRight);
    const clip = el('clipPath', { id: clipId });
    clip.appendChild(el('path', { d: dShape }));
    g.appendChild(clip);
    const inner = el('g', { 'clip-path': `url(#${clipId})` });
    inner.appendChild(el('rect', { x, y, width: len, height: h, fill: base }));
    const wMm = Number(widthMm) || 0;
    const tMm = Number(thicknessMm) || 0;
    if (wMm > 0 && tMm > 0) {
      const perMm = h / wMm;
      const edgeH = (tMm / 2) * perMm;
      const edges = face.edges || [];
      if (edges[0] && edges[0] !== base) {
        inner.appendChild(el('rect', { x, y, width: len, height: edgeH, fill: edges[0] }));
      }
      if (edges[1] && edges[1] !== base) {
        inner.appendChild(el('rect', { x, y: y + h - edgeH, width: len, height: edgeH, fill: edges[1] }));
      }
      (face.marks || []).forEach((m) => {
        const my = y + edgeH + (Number(m.x) || 0) * perMm;
        const mh = Math.max(0.75, (Number(m.w) || 0.5) * perMm);
        if (my + mh > y + h) return;
        inner.appendChild(el('rect', { x, y: my, width: len, height: mh, fill: m.color || base }));
      });
    }
    g.appendChild(inner);
    g.appendChild(el('path', {
      d: dShape,
      fill: 'none', class: 'bc-segment__strip-outline',
    }));
    parent.appendChild(g);
    return g;
  }

  // Sleeve footprint on a top strip: the SAME formed loop seen from above,
  // so the visible extent is the sleeve's x-projection on the loop — the
  // identical bbox split the side-view gradient paints. Square inboard
  // edge, rounded corners only at the fold end (the sleeve ends
  // mid-webbing), matching the strip's own corner radius.
  // `dir` is the toward-the-fold sign, same convention as the loop dir:
  // +1 = fold cap on the right, −1 = on the left.
  // The sleeve is physically WIDER than the webbing it wraps (28 mm sleeve
  // webbing on 1-inch lines, 1-inch sleeve on 20 mm — Jerry, 2026-07-30),
  // so with `widthMm` the footprint overhangs the strip symmetrically.
  function drawTopSleeve(parent, opts) {
    const { foldX, dir, len, stripY, stripH, color, widthMm } = opts;
    let sy = stripY;
    let sh = stripH;
    const wMm = Number(widthMm) || 0;
    if (wMm > 0) {
      const sleeveWmm = wMm >= 23 ? 28 : (wMm >= 18 ? 25.4 : wMm * 1.2);
      sh = Math.min(stripH * 1.45, (sleeveWmm / wMm) * stripH);
      sy = stripY - (sh - stripH) / 2;
    }
    // A short sleeve can be narrower than the strip's own corner radius, so
    // the cap rounds DOWN with it (rMax = half the footprint) rather than
    // the footprint being held out to a minimum width (Jerry, 2026-07-31).
    // That old floor — stripCapR(sh) + 2, up to 12 units — froze the drawn
    // sleeve below ~50-70 mm in the overview view, where the end scale runs
    // ~0.3-0.6 px/mm, i.e. right across the bottom of the builder's sleeve
    // range. 2 units is left as the degenerate guard; nothing sleeveCoverage
    // can return reaches it.
    const w = Math.max(len, 2);
    const capR = w / 2;
    const xIn = foldX - dir * w;
    const d = dir === 1
      ? stripPath(xIn, foldX, sy, sh, false, true, capR)
      : stripPath(foldX, xIn, sy, sh, true, false, capR);
    parent.appendChild(el('path', { d, fill: color, opacity: 0.85 }));
  }

  // Sewing patch on a top-view strip: pattern artwork when present, else a
  // dashed placeholder that future pattern_image uploads replace with zero
  // code change. With a mm scale (pxPerMm — the zoomed end view) the patch
  // is sized from the pattern's sewn-area dims and anchored AT the throat
  // growing away from the loop — the model's 5 mm sits at the TAIL end,
  // between the patch and the cut edge (Jerry, 2026-07-30); without one
  // (the full view, whose tail length is itself symbolic) it fills the
  // sewn zone [lo, hi]. Width across the strip follows pattern_width_mm
  // at the strip's width scale on both paths. `centerX` (join stitches —
  // no throat to anchor to) centres the patch there instead. `bg` is the
  // hex the patch sits on (top-face base) for the contrast-aware thread
  // shade. Returns the patch box {x, y, w, h} so callers can dimension it.
  function drawStitchZone(parent, opts) {
    const { pattern, lo, hi, stripY, stripH, cy, widthMm, pxPerMm, dir, throatX, centerX, bg, simple } = opts;
    const span = hi - lo;
    if (!(span > 12)) return null;
    let stitchX = lo + 4;
    let stitchW = Math.min(span - 4, Math.max(24, span - 8));
    let stitchY = stripY + 2;
    let stitchH = stripH - 4;
    if (pxPerMm && pattern && pattern.patternLengthMm != null) {
      stitchW = Math.min(span - 4, Math.max(10, pattern.patternLengthMm * pxPerMm));
      if (centerX != null) {
        stitchX = centerX - stitchW / 2;
      } else {
        // At the throat, growing away from the loop (the 5 mm tab is at
        // the tail end).
        stitchX = dir === 1 ? throatX - stitchW : throatX;
      }
      // Bounds are the sewn zone itself — no inset (Jerry, 2026-07-31): a
      // 2-unit margin here pushed the patch off the throat it is anchored
      // to, which showed up as the top view's patch and the side view's
      // stitching disagreeing about where the sewing starts.
      stitchX = clamp(lo, stitchX, hi - stitchW);
    }
    if (pattern && pattern.patternWidthMm != null && widthMm > 0) {
      stitchH = Math.max(6, Math.min(stripH - 2, (pattern.patternWidthMm / widthMm) * stripH));
      stitchY = cy - stitchH / 2;
    }
    if (simple) {
      // Schematic stitch mark for the scene's OVERVIEW, in the needle-thread
      // shade, standing in for the machine art (which the zoomed end view
      // draws for real). The art is the wrong tool at this size — a 66 mm
      // pattern lands in ~30 units there, and a run that dense collapses
      // into a smudge that reads as damage rather than stitching. A clean
      // stand-in says "sewn here" at any size, which is all the overview is
      // claiming — but WHICH stand-in follows the pattern family by name, so
      // a bartack-sewn webbing doesn't advertise diamonds it was never sewn
      // with: bartack patterns get a couple of narrow dense bars across the
      // strip, everything else (and anything unnamed) the diamond chain.
      const ink = threadInk(pattern && pattern.needleThreadColor, bg, '#1c2733');
      const isBartack = !!(pattern && pattern.name && /bartack/i.test(pattern.name));
      if (isBartack) {
        // A bartack is a short dense zigzag run ACROSS the webbing, so the
        // bars take the strip's full pattern width (no aspect cap — being
        // tall and narrow is the whole shape) and stay sparse along it: two
        // on a typical loop patch, which is what the machine actually lays.
        const n = Math.round(clamp(2, stitchW / 14, 4));
        const cell = stitchW / n;
        // Max sized off the cell so bars can never touch, however tight the
        // patch gets (clamp's min(max, …) lets the cap win).
        const barW = clamp(2, Math.min(5, cell * 0.5), cell * 0.7);
        const yTop = stitchY + 1;
        const yBot = stitchY + stitchH - 1;
        const zig = 6;
        const gz = el('g', { 'aria-hidden': 'true' });
        for (let i = 0; i < n; i += 1) {
          const x0 = stitchX + (i + 0.5) * cell - barW / 2;
          let d = `M ${x0} ${yTop}`;
          for (let j = 1; j <= zig; j += 1) {
            d += ` L ${x0 + (j % 2 ? barW : 0)} ${yTop + ((yBot - yTop) * j) / zig}`;
          }
          gz.appendChild(el('path', {
            d, fill: 'none', stroke: ink,
            'stroke-width': clamp(0.8, stitchH * 0.05, 1.4),
            'stroke-linecap': 'round', 'stroke-linejoin': 'round',
          }));
        }
        parent.appendChild(gz);
        const drawnX = stitchX + (cell - barW) / 2;
        return {
          x: drawnX, y: yTop, w: (stitchX + stitchW - (cell - barW) / 2) - drawnX, h: yBot - yTop,
        };
      }
      // Height capped against the length so the diamonds keep their shape
      // on short patterns — the two axes are on different scales up here,
      // and filling the box outright would stretch them into pickets.
      const h = Math.min(stitchH, stitchW * 0.55);
      const yMid = stitchY + stitchH / 2;
      const yTop = yMid - h / 2;
      const yBot = yMid + h / 2;
      const n = Math.round(clamp(2, stitchW / 11, 8));
      const step = stitchW / n;
      const gz = el('g', { 'aria-hidden': 'true' });
      [yTop, yBot].forEach((peak) => {
        let d = `M ${stitchX} ${yMid}`;
        for (let i = 0; i < n; i += 1) {
          d += ` L ${stitchX + (i + 0.5) * step} ${peak}`
            + ` L ${stitchX + (i + 1) * step} ${yMid}`;
        }
        gz.appendChild(el('path', {
          d, fill: 'none', stroke: ink,
          'stroke-width': clamp(0.9, h * 0.07, 1.8),
          'stroke-linecap': 'round', 'stroke-linejoin': 'round',
        }));
      });
      parent.appendChild(gz);
      return { x: stitchX, y: yTop, w: stitchW, h };
    }
    const vec = pattern && pattern.paths && pattern.paths.paths
      && pattern.paths.paths.length ? pattern.paths : null;
    if (vec) {
      // TRUE VECTOR stitching — the machine file's sew runs in mm space
      // (sewing_pattern.pattern_paths, written by
      // upload_sewing_pattern_images.py --sync-paths), stroked in the
      // needle-thread color straight on the webbing: no backing, crisp at
      // any zoom, exactly like the rest of the diagram. Uniform scale
      // centred in the patch box (the fit the raster fallback's
      // preserveAspectRatio 'meet' gave).
      const w = Number(vec.w) || 1;
      const h = Number(vec.h) || 1;
      const s = Math.min(stitchW / w, stitchH / h);
      const ox = stitchX + (stitchW - w * s) / 2;
      const oy = stitchY + (stitchH - h * s) / 2;
      // The machine runs dense-edge-RIGHT and the dense edge belongs toward
      // the loop, so a left fold (dir −1) sees the art mirrored.
      const flip = dir === -1;
      const g2 = el('g', {
        transform: flip
          ? `translate(${ox + w * s} ${oy}) scale(${-s} ${s})`
          : `translate(${ox} ${oy}) scale(${s})`,
      });
      // Thread color: the needle thread's hue, contrast-shaded against the
      // webbing it sits on (threadInk validates too — an invalid stroke
      // value is treated as UNSET and with fill:none the stitching would
      // vanish entirely).
      const ink = threadInk(pattern.needleThreadColor, bg, '#1c2733');
      // 2.5× the machine stroke (with a 0.6 mm floor) — at true thread
      // width the stitching read too faint on the webbing; the 1px screen
      // floor keeps the tiny patches from vanishing.
      const sw = Math.max((Number(vec.sw) || 0.25) * 2.5, 0.6, 1 / s);
      vec.paths.forEach((dStr) => {
        g2.appendChild(el('path', {
          d: dStr, fill: 'none', stroke: ink, 'stroke-width': sw,
          'stroke-linecap': 'round', 'stroke-linejoin': 'round',
        }));
      });
      parent.appendChild(g2);
      return { x: stitchX, y: stitchY, w: stitchW, h: stitchH };
    } else if (pattern && pattern.patternImage && pattern.patternImage.url) {
      // Raster fallback for patterns whose pattern_paths isn't synced yet.
      // Light backing so the dark stitch ink stays legible on dark webbing
      // colors — same "diagram patch" language as the placeholder below.
      parent.appendChild(el('rect', {
        x: stitchX, y: stitchY, width: stitchW, height: stitchH,
        rx: 3, ry: 3, fill: '#fff', opacity: 0.82,
      }));
      parent.appendChild(el('image', {
        href: pattern.patternImage.url,
        x: stitchX,
        y: stitchY,
        width: stitchW,
        height: stitchH,
        preserveAspectRatio: 'xMidYMid meet',
      }));
    } else {
      const ph = el('g', { class: 'bc-segment__stitch-placeholder' });
      ph.appendChild(el('rect', {
        x: stitchX, y: stitchY, width: stitchW, height: stitchH,
        rx: 3, ry: 3, fill: 'none', 'stroke-dasharray': '4 3',
      }));
      [0.3, 0.5, 0.7].forEach((f) => {
        ph.appendChild(el('line', {
          x1: stitchX + stitchW * f, y1: stitchY + 3,
          x2: stitchX + stitchW * f, y2: stitchY + stitchH - 3,
        }));
      });
      const title = document.createElementNS(SVG_NS, 'title');
      title.textContent = 'Sewing pattern diagram coming soon';
      ph.appendChild(title);
      parent.appendChild(ph);
    }
    return { x: stitchX, y: stitchY, w: stitchW, h: stitchH };
  }

  // Faint side-view stitching through the layer stack, to scale: the
  // sewn zone starts AT the throat and runs pattern-length toward the
  // tail (the model's 5 mm tab sits between the patch end and the cut
  // edge). Needle-thread shade through the main line (top layer),
  // bobbin through the folded-back tail (bottom layer), each
  // contrast-shaded against the layer it crosses. ONE shared helper for
  // the PDP docs side figure and the builder's side views (full +
  // zoomed end) so the surfaces can't drift.
  // `flipY` mirrors the three layer boundaries about the line — the
  // stitching follows the loop it belongs to, so needle still crosses the
  // main line and bobbin the folded-back tail.
  function drawSideStitches(parent, opts) {
    const { pattern, throatX, dir, patchMm, pxPerMm, lineY, webT, topColor, lowerColor, twoTone, flipY } = opts;
    if (!pattern || patchMm == null || !pxPerMm) return;
    const zHi = throatX;
    const zLo = zHi - dir * patchMm * pxPerMm;
    const xA = Math.min(zLo, zHi);
    const xB = Math.max(zLo, zHi);
    const needle = threadInk(pattern.needleThreadColor, topColor, '#1c2733');
    const bobbin = threadInk(pattern.bobbinThreadColor, twoTone ? topColor : (lowerColor || topColor), '#1c2733');
    const M = (y) => (flipY ? 2 * lineY - y : y);
    const yTop = M(lineY - webT / 2 + 0.5);
    const yMid = M(lineY + webT / 2);
    const yBot = M(lineY + webT * 1.5 - 0.5);
    const gz = el('g', { opacity: 0.55, 'aria-hidden': 'true' });
    for (let x = xA + 3; x <= xB - 1; x += 7) {
      gz.appendChild(el('line', { x1: x, y1: yTop, x2: x, y2: yMid, stroke: needle, 'stroke-width': 1.6 }));
      gz.appendChild(el('line', { x1: x, y1: yMid, x2: x, y2: yBot, stroke: bobbin, 'stroke-width': 1.6 }));
    }
    parent.appendChild(gz);
  }

  // Vertical dimension (drawDim rotated 90°): witness ticks horizontal,
  // arrows vertical, label beside the line at mid-height. `color` paints
  // the whole frame via inline styles — needed when the dim sits ON the
  // colored strip, where the theme's foreground-mix class colors can
  // vanish (inline style outranks the bc-segment-builder.css class rules).
  function drawVDim(parent, x, y1, y2, label, opts) {
    const o = opts || {};
    const g = el('g', { class: `bc-segment__dim${o.cls ? ` ${o.cls}` : ''}`, 'aria-hidden': 'true' });
    const strokeStyle = o.color ? `stroke:${o.color}` : null;
    const fillStyle = o.color ? `fill:${o.color}` : null;
    const add = (node, style) => {
      if (style) node.setAttribute('style', style);
      g.appendChild(node);
    };
    const tick = o.tick == null ? 8 : o.tick;
    if (tick > 0) {
      add(el('line', { class: 'bc-segment__dim-ext', x1: x - tick, y1, x2: x + tick, y2: y1 }), strokeStyle);
      add(el('line', { class: 'bc-segment__dim-ext', x1: x - tick, y1: y2, x2: x + tick, y2 }), strokeStyle);
    }
    add(el('line', { class: 'bc-segment__dim-line', x1: x, y1, x2: x, y2 }), strokeStyle);
    const a = o.arrow || 7;
    add(el('path', { class: 'bc-segment__dim-arrow', d: `M ${x} ${y1} L ${x - a * 0.5} ${y1 + a} L ${x + a * 0.5} ${y1 + a} Z` }), fillStyle);
    add(el('path', { class: 'bc-segment__dim-arrow', d: `M ${x} ${y2} L ${x - a * 0.5} ${y2 - a} L ${x + a * 0.5} ${y2 - a} Z` }), fillStyle);
    if (label != null) {
      const side = o.labelSide === 'left' ? -1 : 1;
      add(el('text', {
        class: 'bc-segment__dim-label',
        x: x + side * (o.labelGap == null ? 8 : o.labelGap),
        y: (y1 + y2) / 2 + 4,
        'text-anchor': side === 1 ? 'start' : 'end',
        text: label,
      }), fillStyle);
    }
    parent.appendChild(g);
    return g;
  }

  // Persistent "this part opens an editor" affordance: a small BUTTON —
  // a rounded pill reading "Edit" — drawn inside a hotspot (the bare-end
  // hotspots keep their "+" ghost badge instead). It replaced a pencil
  // icon in a circle (Jerry, 2026-07-31): at loop-eye size the glyph was
  // guesswork, where a word is a control.
  //
  // The pill hangs BELOW the loop (Jerry, 2026-07-31 — inside the eye it
  // crowded the strands), so it no longer has to fit anything: one fixed
  // height for every loop, which also keeps the two ends' buttons reading
  // as the same control. Width follows the text: "Edit" at ~0.52em per
  // glyph plus symmetric padding; the fully-rounded corners match the
  // theme's pill buttons (.bc-segment__zoom-out).
  // `text` (optional) reuses the pill for another one-word action — the
  // zoomed end view's "+ Sleeve" button. The default keeps "Edit"'s exact
  // width arithmetic, so every pill drawn before this parameter existed
  // still lands on the same numbers.
  const EDIT_PILL_H = 18;
  function editBadge(cx, cy, h = EDIT_PILL_H, text) {
    const g = el('g', { class: 'bc-segment__edit-badge', 'aria-hidden': 'true' });
    const fs = clamp(9, h * 0.55, 12);
    const ph = Math.max(h, fs * 1.55);
    const pw = text ? fs * (0.52 * text.length + 1.3) : fs * 2.1 + fs * 1.3;
    g.appendChild(el('rect', {
      class: 'bc-segment__edit-pill',
      x: cx - pw / 2, y: cy - ph / 2, width: pw, height: ph,
      rx: ph / 2, ry: ph / 2,
    }));
    g.appendChild(el('text', {
      class: 'bc-segment__edit-pill-text',
      x: cx, y: cy + fs * 0.34,
      'text-anchor': 'middle',
      'font-size': fs,
      text: text || 'Edit',
    }));
    return g;
  }


  // Direct-manipulation grab handle for one editable dimension — drawn
  // ONLY in the builder's zoomed end view (drawEndDetail's `interactive`
  // opt, which <bc-segment-scene> passes and the PDP <bc-sewing-figure>
  // never does).
  //
  // No new CSS by construction: the group borrows the hotspot group's
  // class so it inherits that hover/focus ring for free, the dot is
  // `currentColor` (the theme's foreground, inherited by the svg), and the
  // cursor + touch-action ride inline on the hit rect — a CSS class would
  // outrank an inline cursor attribute, so the hit rect deliberately does
  // NOT carry .bc-segment__hot.
  //
  // The BEHAVIOUR is not here: <bc-segment-scene> delegates pointer events
  // from the svg root (see _bindDrag), so a re-render replacing this
  // markup mid-drag cannot drop the gesture.
  // Hit-target size in viewBox units. One unit is NOT one CSS pixel: when
  // bc-segment-builder.css's max-height cap binds, _computeVbW widens the viewBox and
  // the scene renders at ~0.66 px/unit — which would put a 28-unit target
  // at 19 px, under the 24 a finger needs. `o.hitScale` (from the scene's
  // measured px/unit — see _hitScale) grows the INVISIBLE rect to
  // compensate; the drawn dot, bar and hover ring keep their size, so the
  // affordance doesn't balloon with the target.
  const DRAG_HIT_W = 28;
  const DRAG_HIT_H = 34;
  const MIN_HIT_PX = 24;
  // Slider affordance flanking the dot (Jerry, 2026-08-01: "the sliders on
  // the loop should be a bit more obvious that they are sliders"). A pair
  // of chevrons pointing the two ways the value can travel — the dot on
  // its own read as part of the drawing rather than as a control, and the
  // ew-resize cursor only announces itself once the pointer is already on
  // it (and never on a touch screen at all).
  //
  // STROKED chevrons, not the filled triangles the dimension frames use:
  // the opening handle sits exactly on the opening dimension's own
  // arrowhead, and a second solid triangle beside it would read as one
  // confused arrow rather than as a grip. Same single `ink` as the dot and
  // the bar, so nothing here needs to know what it is sitting on.
  //
  // FIXED size, deliberately: o.hitScale grows the invisible hit rect
  // only (see above), so the affordance can never balloon with the target.
  const GRIP_DX = 10;        // chevron point, measured from the handle axis
  const GRIP_DEPTH = 3.4;    // how far the point leads its two arms
  const GRIP_HALF_H = 4.2;
  function dragGrip(x, cy, s, ink) {
    const back = x + s * (GRIP_DX - GRIP_DEPTH);
    return el('path', {
      d: `M ${back} ${cy - GRIP_HALF_H} L ${x + s * GRIP_DX} ${cy} L ${back} ${cy + GRIP_HALF_H}`,
      fill: 'none',
      stroke: ink,
      'stroke-width': 1.7,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-opacity': 0.9,
    });
  }
  function dragHandle(parent, o) {
    const hs = Math.max(1, o.hitScale || 1);
    // `o.ink` overrides currentColor for a handle drawn ON the webbing:
    // the theme foreground is near-black, which is invisible against a
    // dark webbing — which is exactly where the sleeve handle sits.
    const ink = o.ink || 'currentColor';
    const w = DRAG_HIT_W;
    const h = Math.max(DRAG_HIT_H, o.h || 0);
    const x = o.x - w / 2;
    const y = o.cy - h / 2;
    const hw = w * hs;
    const hh = h * hs;
    const g = el('g', { class: 'bc-segment__hotgroup', 'data-bc-drag': o.field });
    g.appendChild(el('rect', {
      class: 'bc-segment__hot-outline',
      x, y, width: w, height: h, rx: 8, ry: 8, fill: 'none',
    }));
    if (o.bar) {
      g.appendChild(el('line', {
        x1: o.x, y1: o.bar[0], x2: o.x, y2: o.bar[1],
        stroke: ink, 'stroke-width': 1.5, 'stroke-opacity': 0.7,
      }));
    }
    g.appendChild(el('circle', {
      cx: o.x, cy: o.cy, r: 4.5, fill: ink, 'fill-opacity': 0.9,
    }));
    // The dot marks the exact value; the chevrons say it slides. Both
    // stay inside DRAG_HIT_W, so the hover ring still frames the whole
    // affordance.
    g.appendChild(dragGrip(o.x, o.cy, -1, ink));
    g.appendChild(dragGrip(o.x, o.cy, 1, ink));
    g.appendChild(el('rect', {
      x: o.x - hw / 2, y: o.cy - hh / 2, width: hw, height: hh, fill: 'transparent',
      cursor: 'ew-resize', style: 'touch-action:none',
      role: 'button', tabindex: -1, 'aria-label': o.label,
    }));
    parent.appendChild(g);
    return g;
  }

  // A drag surface with no affordance of its own — the thing drawn under
  // it IS the affordance. The sleeve uses these: its painted overlay is
  // the biggest, most obvious target on the figure, so grabbing anywhere
  // on it resizes it (Jerry, 2026-07-31: the edge handle alone was not
  // discoverable). The hotspot group's class rings it on hover, so the
  // region announces itself the moment the pointer is over the sleeve.
  function dragRegion(parent, o) {
    const g = el('g', { class: 'bc-segment__hotgroup', 'data-bc-drag': o.field });
    const box = { x: o.x, y: o.y, width: o.w, height: o.h };
    g.appendChild(el('rect', Object.assign({
      class: 'bc-segment__hot-outline', rx: 8, ry: 8, fill: 'none',
    }, box)));
    g.appendChild(el('rect', Object.assign({
      fill: 'transparent', cursor: 'ew-resize', style: 'touch-action:none',
      role: 'button', tabindex: -1, 'aria-label': o.label,
    }, box)));
    parent.appendChild(g);
    return g;
  }

  // A circled ✕ that takes something away, drawn ONLY in the builder's
  // zoomed end view (Jerry, 2026-08-01: "There should be an X next to
  // Sleeve to remove it, and an X somewhere else to remove the loop (on
  // the diagram)"). The mirror of the full view's ghost "+" badge — same
  // circle-and-strokes language, so add and remove read as one pair.
  //
  // Like the "+ Sleeve" pill it is a ONE-SHOT action, not a drag: the
  // group carries `data-bc-end-action` and the scene's delegated click /
  // Enter / Space handler (_endAction) turns it into an event. Nothing
  // binds a listener here — every render replaces this markup.
  //
  // `danger` picks the builder's ONE red for the button that ends the
  // step. The colors live in bc-segment-builder.css (which is where that red lives);
  // only the geometry is here, exactly like editBadge.
  const REMOVE_R = 8;             // drawn circle radius
  const REMOVE_HIT = DRAG_HIT_W;  // same base as the drag handles, so the
                                  // same hitScale keeps it at MIN_HIT_PX
  const REMOVE_X_SLOT = 22;       // width a ✕ reserves beside a label:
                                  // the badge plus 3 units of air each side
  function removeBadge(parent, o) {
    const hs = Math.max(1, o.hitScale || 1);
    const hit = REMOVE_HIT * hs;
    const g = el('g', {
      class: 'bc-segment__hotgroup',
      'data-bc-end-action': o.action,
    });
    // No .bc-segment__hot-outline here — the badge INVERTS on hover/focus
    // (bc-segment-builder.css), which is feedback enough on a 16-unit circle, and it is
    // what the "+ Sleeve" pill beside it does. The dashed ring is for the
    // affordance-less surfaces that have nothing else to say.
    const badge = el('g', {
      class: `bc-segment__remove-badge${o.danger ? ' bc-segment__remove-badge--danger' : ''}`,
      'aria-hidden': 'true',
    });
    badge.appendChild(el('circle', { cx: o.cx, cy: o.cy, r: REMOVE_R }));
    const a = REMOVE_R * 0.46;
    badge.appendChild(el('line', { x1: o.cx - a, y1: o.cy - a, x2: o.cx + a, y2: o.cy + a }));
    badge.appendChild(el('line', { x1: o.cx - a, y1: o.cy + a, x2: o.cx + a, y2: o.cy - a }));
    g.appendChild(badge);
    g.appendChild(el('rect', {
      x: o.cx - hit / 2, y: o.cy - hit / 2, width: hit, height: hit,
      fill: 'transparent', cursor: 'pointer',
      role: 'button', tabindex: 0, 'aria-label': o.label,
    }));
    parent.appendChild(g);
    return g;
  }

  let uidCounter = 0;

  // End (zoom) view rows — closure scope: shared by <bc-segment-scene>'s
  // zoomed end view and the PDP sewing-docs figure (<bc-sewing-figure>),
  // both drawn by drawEndDetail below. Z_SIDE_CY leaves room ABOVE the
  // loop for the opening dimension to clear the centred caption, and
  // BELOW it for the fold row — plus, on a loop too small to name its
  // sleeve inside the eye, one more row under that for the sleeve value
  // (the deepest case the budget has to cover). Z_VB_H is this
  // composition's total height. It used to be the scene's full-view canvas
  // height as well; the two are DECOUPLED since the backup line
  // (2026-07-31), because the full view grows a band per extra line while
  // this composition — shared verbatim with the PDP <bc-sewing-figure> —
  // has to keep its fixed rows. Changing Z_VB_H changes the product pages;
  // the scene's own base height is BASE_VB_H, below.
  const Z_VB_H = 330;
  const Z_TOP_CAP_Y = 24;
  const Z_TOP_CY = 72;
  const Z_SIDE_CAP_Y = 146;
  const Z_SIDE_CY = 212;
  // (Side-view strand thickness is computed per webbing — real
  // thickness_mm at each view's mm scale, 2.5-unit floor — not a row
  // constant; see drawEndDetail / _drawFull.)
  // Cap on the drawn piece width (viewBox units). Without it the uniform
  // mm→px scale stretches with the canvas while the loop HEIGHT stays
  // fixed, flattening the teardrop into a sliver on wide screens.
  const Z_DRAW_W = 500;
  // Vertical budget for the zoomed top view's strip: the tallest it can be
  // drawn AT SCALE without crowding the "Top view" caption above or the
  // patch-length dimension into the "Side view" caption below. It is the
  // ACROSS-the-webbing half of the scale cap — see endGeometry's
  // maxPxPerMm (Jerry, 2026-07-31: the strip used to absorb the overflow
  // by clamping its own height, which silently put the two axes of the
  // top view on different scales as the opening shrank).
  const Z_STRIP_H = 60;

  // Folded-end geometry (mm + px), shared by every sewn-end composition —
  // the builder's zoomed end view and the PDP docs' split top/side views.
  // The model (Jerry, 2026-07-30):
  //   eye (fold tip -> throat)   = the OPENING SIZE (its flat length)
  //   two-layer sewn zone        = pattern length + 5 mm tail tab
  //                                (patch starts AT the throat; the 5 mm
  //                                sits between patch end and cut edge)
  //   fold (tip -> tail end)     = opening + pattern length + 5
  // In the builder the OPENING is the changeable entry and the fold
  // derives from it; the docs defaults run the same equation the other
  // way (each pattern's catalog fold is authoritative — 210 mm on
  // 502/512, 305 mm on 504/514 — and the stored opening_size is
  // pre-derived as fold − pattern length − 5 by
  // upload_sewing_pattern_images.py --sync-fold).
  // ONE uniform mm→px scale for the whole composition — both views, both
  // axes. The piece is drawn as wide as `drawWCap` allows, and two options
  // refine that (Jerry, 2026-07-31; both default OFF so the PDP split
  // figures keep their exact geometry):
  //
  //   maxPxPerMm — a hard ceiling on the scale, so a SHORT end doesn't get
  //     magnified to fill the canvas. Callers derive it from what the
  //     across-the-webbing direction can afford (Z_STRIP_H / widthMm):
  //     without it, a 25 mm opening drew its 66 mm patch at twice the
  //     px/mm of a 139 mm one while the strip height sat pinned at its
  //     clamp, so the two views of ONE figure disagreed on what a
  //     millimetre is. The drawn width then follows the end's real
  //     length instead of always filling the cap.
  //   anchorFold — park the fold cap at the outer edge of [X0, X1] instead
  //     of centring the piece in it. Centring left the whole difference
  //     between the cap and the canvas as dead margin on the FOLD side
  //     (the inboard side is never dead — the webbing continues off it),
  //     and moved the cap around whenever the opening changed.
  function endGeometry(opts) {
    const { X0, X1, vbW, dir, end, drawWCap, maxPxPerMm, anchorFold } = opts;
    const pattern = end.pattern || null;
    const openingReal = end.openingMm != null || (pattern && pattern.openingMm != null);
    const openingMm = end.openingMm != null ? end.openingMm
      : (pattern && pattern.openingMm != null ? pattern.openingMm : 150);
    const patchMm = pattern && pattern.patternLengthMm != null ? pattern.patternLengthMm : null;
    const eyeMm = eyeMmFor(end);   // same fallbacks as openingMm above, floored
    const sewnMm = patchMm != null
      ? patchMm + 5
      : Math.max(20, (end.foldLiveMm != null ? end.foldLiveMm : 210) - eyeMm);
    const foldMm = Math.round(eyeMm + sewnMm);
    const foldReal = openingReal && (patchMm != null || end.foldLiveMm != null);
    const band = X1 - X0;
    const capW = Math.min(band, drawWCap == null ? Z_DRAW_W : drawWCap);
    const totalMm = eyeMm + sewnMm;
    const pxPerMm = maxPxPerMm
      ? Math.min(capW / totalMm, maxPxPerMm)
      : capW / totalMm;
    // Actual drawn width — equal to capW unless the scale ceiling binds.
    const drawW = totalMm * pxPerMm;
    const eyePx = eyeMm * pxPerMm;
    const sewnPx = drawW - eyePx;
    const bandCx = (X0 + X1) / 2;
    const foldX = anchorFold
      ? (dir === 1 ? X1 - 2 : X0 + 2)
      : bandCx + dir * (drawW / 2 - 2);
    const throatX = foldX - dir * eyePx;
    const tailEndX = throatX - dir * sewnPx;
    const inboardX = dir === -1 ? vbW + 2 : -2;
    return {
      pattern, openingReal, openingMm, patchMm, eyeMm, sewnMm, foldMm, foldReal,
      drawW, pxPerMm, eyePx, sewnPx, foldX, throatX, tailEndX, inboardX,
    };
  }

  // Sewn-end composition: top view strip (fold cap, sleeve footprint, tail
  // seam, sewing patch) stacked over the side-view hairpin loop with the
  // opening / fold / sleeve dimensions. Extracted from the scene's zoomed
  // end view so the PDP sewing-docs figure renders the IDENTICAL
  // construction — change here, both surfaces follow.
  //
  //   svg/defs : target elements (composition appended to svg)
  //   opts     : { vbW, uid, dir (+1 fold right / −1 fold left),
  //                entry { widthMm, thicknessMm, topFace },
  //                scene { color, colorLower, twoTone, sleeveColor },
  //                end   { treatment ('naked'|'sleeved'), pattern,
  //                        openingMm, sleeveLengthMm, foldLiveMm },
  //                interactive: draw the drag handles and the one-shot
  //                  buttons — "+ Sleeve" and the two remove ✕s (BUILDER
  //                  ONLY — the PDP figure never passes it, which is what
  //                  keeps the product pages byte-identical; the sleeve
  //                  value's placement is unchanged without it too),
  //                hitScale: multiplier on the precise handles' invisible
  //                  hit rects (see dragHandle) }
  // Returns the shared end geometry so the scene can convert a drag's
  // pixels back into millimetres at exactly the scale it drew.
  function drawEndDetail(svg, defs, opts) {
    const { vbW, uid, dir, entry, scene, end, interactive, hitScale } = opts;
    const top = scene.color || '#2b2b2b';
    const lower = scene.colorLower || null;
    const twoTone = !!(scene.twoTone && lower);
    const sleeveColor = scene.sleeveColor || '#c54a2c';

    const g = el('g');
    svg.appendChild(g);
    const X0 = 42;
    const X1 = vbW - 42;

    // Captions centred over each view — they label the ROWS, so they stay
    // put while the piece itself hugs the fold-side edge. The row
    // constants keep the opening dimension clear of them.
    //
    // In the BUILDER the top view's caption also names the webbing (Jerry,
    // 2026-08-01: "can we write the name of the webbing on the top view
    // somehow, in the loop editor?"): a zoomed end is a detail figure with
    // nothing else in it that says which line is being dialed in, and on a
    // multi-line build the mainline, the backup and the extender can be
    // three different webbings — `entry` is the one this zoom was composed
    // from, so the name is always that line's. It rides the CAPTION rather
    // than taking a label of its own because the caption row is the one
    // band here guaranteed clear of the strip, the patch dimensions, the
    // sleeve footprint and the remove ✕s, so it cannot collide with any of
    // them at any opening. `interactive` gates it, which is what keeps the
    // PDP <bc-sewing-figure> byte-identical — that surface is on a page
    // that already names the product, and passes an entry with no label at
    // all.
    const capCx = (X0 + X1) / 2;
    const topCap = interactive && entry.label ? `Top view — ${entry.label}` : 'Top view';
    g.appendChild(el('text', { class: 'bc-segment__detail-caption', x: capCx, y: Z_TOP_CAP_Y, 'text-anchor': 'middle', text: topCap }));
    g.appendChild(el('text', { class: 'bc-segment__detail-caption', x: capCx, y: Z_SIDE_CAP_Y, 'text-anchor': 'middle', text: 'Side view' }));

    const widthMm = Number(entry.widthMm) || 25;
    const sleeved = end.treatment === 'sleeved';

    // Shared folded-end geometry; when the pattern carries no real value
    // the defaults shape the GEOMETRY only — the dimension labels render
    // "—" instead of passing an invented number off as a spec.
    //
    // The scale ceiling is what the TOP view's strip can afford across the
    // webbing (Z_STRIP_H), so one millimetre is the same length in both
    // views and in both axes — see endGeometry. The fold is parked at the
    // canvas edge, so it stays put while the opening is edited and the
    // dead margin on that side goes away.
    const geo = endGeometry({
      X0, X1, vbW, dir, end,
      maxPxPerMm: Z_STRIP_H / widthMm,
      anchorFold: true,
    });
    const {
      pattern, openingReal, openingMm, foldMm, foldReal,
      drawW, pxPerMm, eyePx, foldX, throatX, tailEndX, inboardX,
    } = geo;
    // Teardrop height follows the length (sy tracks sx at a ~4.7:1 loop
    // aspect, the proportions of the approved mini diagram) — unbounded sx
    // with a fixed sy cap read as a stretched-out sliver on wide screens.
    const sx = eyePx / 65;
    const sy = clamp(0.9, sx / 3, 1.5);

    const gradId = `bc-segld-grad-${uid}`;
    const coverage = sleeveCoverage(end);
    defs.appendChild(sleeveGradient(gradId, sleeveColor, dir === -1 ? 'left' : 'right', coverage));

    // ---- top view -----------------------------------------------------
    // The strip runs from the fold cap off the inboard edge (the webbing
    // continues into the rest of the segment). The folded-back tail lies
    // on the UNDERSIDE of the line, so its cut edge is deliberately NOT
    // drawn here (Jerry, 2026-07-30) — tailEndX only bounds the sewn zone.
    // TRUE SCALE across the webbing: the same pxPerMm the length runs at,
    // which the scale ceiling above guarantees lands within Z_STRIP_H. The
    // floor only bites on an extremely long end (a 400 mm-plus fold at a
    // 19 mm width), where a hairline strip would read as nothing at all.
    const stripH = clamp(22, widthMm * pxPerMm, Z_STRIP_H);
    const stripY = Z_TOP_CY - stripH / 2;
    drawTopStrip(g, {
      x: Math.min(foldX, inboardX),
      y: stripY,
      len: Math.abs(inboardX - foldX),
      h: stripH,
      topFace: entry.topFace,
      widthMm: entry.widthMm,
      thicknessMm: entry.thicknessMm,
      clipId: `bc-seg-clip-${uid}`,
      roundLeft: dir === -1,
      roundRight: dir === 1,
    });
    const sleeveTopPx = Math.min(drawW, eyePx * coverage);
    if (sleeved) {
      drawTopSleeve(g, {
        foldX, dir, len: sleeveTopPx, stripY, stripH, color: sleeveColor, widthMm,
      });
    }

    // Stitch zone over the sewn region — drawStitchZone, TO SCALE here
    // (pattern_length_mm along the webbing at the sewn-zone scale,
    // pattern_width_mm across at the strip's width scale).
    const base = (entry.topFace && entry.topFace.base) || top;
    const box = drawStitchZone(g, {
      pattern,
      lo: Math.min(throatX, tailEndX),
      hi: Math.max(throatX, tailEndX),
      stripY,
      stripH,
      cy: Z_TOP_CY,
      widthMm,
      pxPerMm,
      dir,
      throatX,
      bg: base,
    });
    // Pattern dimensions on the patch (length below the strip, width
    // beside it) — the same drawPatchDims construction as the PDP docs
    // top figure, sleeve overhang pushed clear the same way.
    const sleeveDrop = sleeved
      ? Math.min(stripH * 0.225, ((widthMm >= 23 ? 28 : 25.4) / widthMm - 1) * stripH / 2)
      : 0;
    // WHICH side of the patch the WIDTH dimension stands on. The patch is
    // anchored AT the throat and grows toward the tail, so its fold-side
    // edge is the throat — and the sleeve is painted from the fold back
    // toward that same throat, stopping 10 mm short of it at full
    // coverage. On End 2 (dir +1, fold RIGHT) the default right-hand
    // placement therefore stands the frame in the sleeve's path, and its
    // label — which reads outward, i.e. toward the fold — runs onto the
    // sleeve footprint as soon as the sleeve is long relative to the
    // opening (Jerry, 2026-08-01: "the dimension for the pattern width
    // overlaps with the sleeve when doing end 2 edits"). Moving it to the
    // LEFT puts it on the TAIL side, where nothing is drawn on either
    // view. End 1 (dir −1) is the mirror — its tail is already on the
    // right — so it keeps the default and is untouched.
    // BUILDER ONLY (`interactive`): the PDP sewing figures draw one fixed
    // catalog geometry (default opening, default sleeve) whose footprint
    // stops well clear of the label, and they are pixel-frozen surfaces —
    // see the drawEndDetail header.
    const widthSide = interactive && dir === 1 ? 'left' : null;
    drawPatchDims(g, box, pattern, stripY, stripH, sleeveDrop, base, widthSide);

    // ---- side view ----------------------------------------------------
    // Hairpin loop FIRST (under), then the straight line + tail over
    // it — the hairpin's strand ends are exact continuations of the
    // straight strokes at the throat (same split-halves offsets).
    // Strand thickness is the webbing's REAL thickness at the view's mm
    // scale (2.5-unit floor so hairline webbings stay visible) — the
    // same rule as the PDP docs side figure (Jerry, 2026-07-30).
    const webT = Math.max(2.5, (Number(entry.thicknessMm) || 3) * pxPerMm);
    const sleeveT = webT + Math.max(3, 2 * pxPerMm);
    const halfH = 20 * sy;
    const pin = drawHairpin(g, {
      throatX, dir, eyePx, lineY: Z_SIDE_CY, t: webT, halfH,
      sleeveT, topColor: top, lowerColor: lower, twoTone, sleeved, gradId,
    });
    const lineLo = Math.min(throatX, inboardX);
    const lineHi = Math.max(throatX, inboardX);
    if (twoTone) {
      g.appendChild(el('line', { x1: lineLo, y1: Z_SIDE_CY - webT / 4, x2: lineHi, y2: Z_SIDE_CY - webT / 4, stroke: top, 'stroke-width': webT / 2, 'stroke-linecap': 'butt' }));
      g.appendChild(el('line', { x1: lineLo, y1: Z_SIDE_CY + webT / 4, x2: lineHi, y2: Z_SIDE_CY + webT / 4, stroke: lower, 'stroke-width': webT / 2, 'stroke-linecap': 'butt' }));
    } else {
      g.appendChild(el('line', { x1: lineLo, y1: Z_SIDE_CY, x2: lineHi, y2: Z_SIDE_CY, stroke: top, 'stroke-width': webT, 'stroke-linecap': 'butt' }));
    }
    // Tail FLUSH with the main line's end at the throat: End 1 shares
    // the left edge, End 2 the right edge.
    drawOverlap(g, throatX, tailEndX, Z_SIDE_CY + webT, webT, top, lower, twoTone);
    // Faint stitching through the stack — same shared construction as
    // the PDP docs side figure.
    drawSideStitches(g, {
      pattern, throatX, dir, patchMm: geo.patchMm, pxPerMm,
      lineY: Z_SIDE_CY, webT, topColor: top, lowerColor: lower, twoTone,
    });

    // ---- dimensions ---------------------------------------------------
    // STATIC named values only — editing (in the builder) lives outside
    // the diagram. Values sit ON their lines throughout (the Dimensions
    // diagram's value-in-a-gap construction) with faint extension lines
    // reaching from the measured geometry to each row.
    //
    // Layout (Jerry, 2026-07-31): OPENING above the loop, FOLD below it,
    // and the SLEEVE as a PLAIN VALUE inside the eye — no arc, no
    // arrowheads, no witness ticks. The arc-length dimension went from
    // outside the loop (intrusive) to inside it (still busy) to this: the
    // sleeve is painted on the drawing already, so the value only has to
    // name it, and the words "Sleeve 200 mm" do that on their own.
    const eyeLo = Math.min(throatX, pin.foldX);
    const eyeHi = Math.max(throatX, pin.foldX);
    // Painted half-thickness of the loop (the sleeve band when sleeved) —
    // where the drawn geometry actually ends.
    const outerT = sleeved ? sleeveT / 2 : webT / 2;
    // Above the loop, but never into the "Side view" caption — a tall eye
    // on a thin webbing can push the loop top within a caption's height
    // of the row.
    const openingY = Math.max(Z_SIDE_CAP_Y + 20, pin.topY - outerT - 16);
    const foldY = pin.botY + outerT + 16;
    const extBelowY = Z_SIDE_CY + webT * 2 + 3;   // just under the tail stripe
    const extAboveY = Z_SIDE_CY - webT / 2 - 3;   // just over the main line
    // The opening's extents reach UP to it (throat from just over the main
    // line, fold tip from the eye centre — the tip is the loop's extreme
    // point, so the vertical leaves the geometry immediately); the fold
    // row keeps its two below.
    drawExtLine(g, throatX, extAboveY, openingY - 4);
    drawExtLine(g, pin.foldX, pin.midY, openingY - 4);
    drawExtLine(g, tailEndX, extBelowY, foldY + 4);
    drawExtLine(g, pin.foldX, pin.midY, foldY + 4);
    drawDim(g, eyeLo, eyeHi, openingY,
      openingReal ? `Opening ${openingMm} mm` : 'Opening —',
      { cls: 'bc-segment__dim--opening', tick: 0, arrow: 8, inline: true, labelGap: 8 });
    // Fold-to-end: derived (opening + sewn zone).
    drawDim(g, Math.min(tailEndX, pin.foldX), Math.max(tailEndX, pin.foldX), foldY,
      foldReal ? `Fold ${foldMm} mm` : 'Fold —',
      { cls: 'bc-segment__dim--fold', tick: 0, arrow: 8, inline: true, labelGap: 8 });
    // Where the sleeve's remove ✕ belongs, once the block below has placed
    // the value it rides beside. Consumed by the interactive block AFTER
    // it, not here: the sleeve's own drag surfaces cover the whole eye, and
    // a button drawn under them would never be clicked.
    let removeSleeveAt = null;
    if (sleeved) {
      // The sleeve value, as words. It goes IN the eye — over the part of
      // the loop the sleeve wraps — wherever the eye is tall enough to
      // hold a label line clear of both sleeved strands; on a short
      // opening (nothing like enough eye) it drops to its own row under
      // the fold, still a plain value, never an arrow inside the loop.
      const sleeveLabel = end.sleeveLengthMm ? `Sleeve ${end.sleeveLengthMm} mm` : 'Sleeve —';
      const labelW = sleeveLabel.length * 6;      // ~6 units/char at the 11px dim size
      // Room for the remove ✕ (Jerry, 2026-08-01) — the value and its
      // button are ONE assembly, centred where the value alone used to be,
      // so the ✕ can't shove the number off the eye it is naming. ZERO on
      // the PDP figure, which has no such button: its label placement, and
      // the fit test that chooses between the two placements, stay
      // identical to the unit.
      const rmW = interactive ? REMOVE_X_SLOT : 0;
      const win = eyeTextWindow({
        throatX, dir, eyePx, halfH, webT,
        needHalf: (16 + sleeveT) / 2,             // 12 of ink + 2 clear either side
      });
      const inEye = !!win && win.width >= labelW + rmW + 10;
      const cx = inEye ? win.cx : (eyeLo + eyeHi) / 2;
      const labelY = inEye ? pin.midY + 4 : foldY + 24;
      const gs = el('g', { class: 'bc-segment__dim bc-segment__dim--sleeve', 'aria-hidden': 'true' });
      gs.appendChild(el('text', {
        class: 'bc-segment__dim-label',
        x: cx - rmW / 2,
        y: labelY,
        'text-anchor': 'middle',
        text: sleeveLabel,
      }));
      g.appendChild(gs);
      // The ✕ takes the slot just reserved, at the label's visual centre
      // (the text y is a BASELINE — 4 units under the ink's middle).
      if (interactive) removeSleeveAt = { cx: cx + labelW / 2, cy: labelY - 4 };
    }

    // ---- drag handles ---------------------------------------------------
    // Direct manipulation of the two editable dimensions (Jerry,
    // 2026-07-31 — these REPLACED the end panel's rail sliders). Drawn
    // last so they sit over everything, and only when the caller asked:
    // the PDP figure renders this exact composition without them.
    if (interactive) {
      if (sleeved) {
        // SLEEVE — three ways to grab the same value, because the edge
        // handle alone was not discoverable (Jerry, 2026-07-31: "we should
        // be able to drag the sleeve size as well"). All three emit the
        // one `sleeve` stream; the scene doesn't care which was grabbed.
        //
        // (a) the SIDE view's painted overlay: the band the gradient
        //     strokes along the loop, from where it starts to the fold.
        const splitX = throatX + dir * eyePx * (1 - coverage);
        const sideLo = Math.min(splitX, pin.foldX + dir * (sleeveT / 2));
        const sideHi = Math.max(splitX, pin.foldX + dir * (sleeveT / 2));
        dragRegion(g, {
          x: sideLo, y: pin.topY - sleeveT / 2, w: sideHi - sideLo,
          h: (pin.botY - pin.topY) + sleeveT,
          field: 'sleeve',
          label: 'Drag the sleeve to adjust its length',
        });
        // (b) the TOP view's footprint, over the strip band it overhangs.
        const topLo = Math.min(foldX, foldX - dir * sleeveTopPx);
        dragRegion(g, {
          x: topLo, y: stripY - 8, w: Math.abs(sleeveTopPx), h: stripH + 16,
          field: 'sleeve',
          label: 'Drag the sleeve to adjust its length',
        });
        // (c) the inboard EDGE, where the sleeve actually ends — the
        // precise control, and the one that says which way to pull. It
        // sits at foldX − dir × (sleeve / 2) × pxPerMm, because a sleeve
        // folds over the fold with the webbing and so runs HALF its
        // length along the eye — which is why the scene's drag sense for
        // this field is doubled (see _dragStart). Its dot and bar take a
        // contrast ink: they sit ON the webbing, where the theme's
        // foreground can vanish.
        dragHandle(g, {
          x: foldX - dir * sleeveTopPx, cy: Z_TOP_CY, field: 'sleeve', hitScale,
          h: stripH + 16,
          bar: [stripY - 4, stripY + stripH + 4],
          ink: onStripInk(base),
          label: 'Drag to adjust the sleeve length',
        });
      } else if (end.sleeveAvailable === true) {
        // ADD SLEEVE — a naked end that COULD take one (Jerry,
        // 2026-07-31 from staging). It takes the sleeve's own would-be
        // position: inside the eye when the eye can hold it clear of both
        // strands, else the row under the fold — the same slot, and the
        // same test, the sleeve's value uses when it exists.
        //
        // WHETHER it can take one is the MODEL's call, not the scene's:
        // `sleeveAvailable` is computed by the builder with the very
        // helpers that gate the action (the 50 mm opening floor, and
        // whether the webbing offers a sleeved treatment at all), so the
        // affordance and the rule can't drift. Absent reads as false.
        const winA = eyeTextWindow({
          throatX, dir, eyePx, halfH, webT,
          needHalf: (EDIT_PILL_H + 6) / 2,
        });
        const label = '+ Sleeve';
        const pillW = 11 * (0.52 * label.length + 1.3);
        const inEyeA = !!winA && winA.width >= pillW + 10;
        const bg = el('g', {
          class: 'bc-segment__hotgroup',
          'data-bc-end-action': 'add-sleeve',
        });
        const bx = inEyeA ? winA.cx : (eyeLo + eyeHi) / 2;
        const by = inEyeA ? pin.midY : foldY + 20;
        bg.appendChild(editBadge(bx, by, EDIT_PILL_H + 2, label));
        bg.appendChild(el('rect', {
          x: bx - pillW / 2 - 8, y: by - 16, width: pillW + 16, height: 32,
          fill: 'transparent', cursor: 'pointer',
          role: 'button', tabindex: 0,
          'aria-label': 'Add a sleeve to this end',
        }));
        g.appendChild(bg);
      }
      // OPENING — the THROAT end of the opening dimension. The fold is
      // parked at the canvas edge (endGeometry's anchorFold), so the
      // throat is the end that actually travels as the opening changes; a
      // handle on the pinned end would sit still while the drawing grew
      // around it.
      // Drawn LAST on purpose: where it overlaps a sleeve surface, the
      // opening wins — it is the more common adjustment, and the one with
      // a precise handle rather than a whole painted band.
      //
      // It sits OFF the dimension line (Jerry, 2026-08-01: "can the slider
      // for the opening be separated from the dimension line a bit? the
      // arrow and the slider icon overlap and it looks a bit strange").
      // The handle used to be centred exactly on the throat arrowhead, so
      // the grip's two stroked chevrons interleaved with a filled triangle
      // pointing the same way — one confused arrow instead of a dimension
      // plus a control.
      //
      // DOWN rather than up: above the row is the "Side view" caption, and
      // openingY can come within 20 units of it, while below it the band
      // between the dimension and the webbing is the emptiest space on the
      // composition — at the throat the eye has not opened yet (the
      // hairpin leaves the main line AT throatX), so the only thing down
      // there is the opening's own faint extension line, which is exactly
      // what a handle driving that dimension should look attached to.
      // Centred in that band so it is clear of both of its edges, with a
      // 14–22 unit floor and ceiling so neither a tall eye (openingY at
      // its Z_SIDE_CAP_Y + 20 floor) nor a short one can push it onto the
      // dimension it just left or down onto the webbing. The `bar` then
      // draws the connection back up to that dimension, so separating the
      // two costs nothing in reading which value the grip moves.
      const gripY = clamp(openingY + 14, (openingY + extAboveY) / 2, openingY + 22);
      dragHandle(g, {
        x: throatX, cy: gripY, field: 'opening', hitScale,
        bar: [openingY, gripY],
        label: 'Drag to adjust the opening size',
      });

      // ---- remove buttons ------------------------------------------------
      // Drawn after EVERY drag surface (Jerry, 2026-08-01). The sleeve's
      // side region covers the whole eye, which is where the in-eye ✕
      // sits: under it the button would be a decoration the pointer never
      // reaches. Neither ✕ sits on the throat, so the opening handle keeps
      // the precedence the comment above claims for it.
      if (removeSleeveAt) {
        removeBadge(g, {
          cx: removeSleeveAt.cx, cy: removeSleeveAt.cy, hitScale,
          action: 'remove-sleeve',
          label: 'Remove the sleeve',
        });
      }
      // The LOOP's own ✕, off the fold tip on the side view: the loop's
      // extreme point and in line with it, so it reads as belonging to the
      // loop rather than to the sleeve painted on it — and it is the one
      // place clear of the throat handle, both sleeve surfaces, both
      // captions and all three dimension rows. It steps out past whatever
      // the loop is actually painted at (`outerT` grows with a sleeve),
      // capped so the badge always lands inside the canvas: the fold is
      // parked 2 units off X0/X1, which leaves 44 units of margin.
      // Red, because it ENDS the step — the same answer, and the same one
      // red, as the panel's "No loop" segment.
      removeBadge(g, {
        cx: pin.foldX + dir * clamp(16, outerT + 13, 28),
        cy: Z_SIDE_CY,
        hitScale,
        danger: true,
        action: 'remove-loop',
        label: 'Remove this loop',
      });
    }
    return geo;
  }

  // ---- <bc-segment-scene> --------------------------------------------------

  if (!customElements.get('bc-segment-scene')) {
    const UNIT_SCALE = 1.5;   // CSS px per viewBox unit (2× the PDP's 0.75)
    const MIN_VB_W = 560;     // canvas floor; sub-560px containers track 1:1 instead

    // Full view rows. SIDE VIEW ONLY, on every viewport (Jerry,
    // 2026-07-31 round 3: the plan-view strips obscured the diagram they
    // were annotating). The zoomed end view and the PDP figure keep their
    // top views — those are detail figures, and their rows are the Z_*
    // constants, untouched.
    // Top down: the caption, the mainline's length dimension (its role
    // label and its value ride this row), then the drawing.
    //
    // The caption was BETWEEN the dimension and the line until Jerry asked
    // for it on top (2026-08-01: "can the 'side view' label be moved above
    // the mainline length dimension and input?") — it names the whole
    // figure, and a heading that sits under one of the things it heads
    // reads as a label for the drawing alone.
    //
    // THE CANVAS IS v3's AGAIN (round 5, 2026-08-02). Round 4 reserved a
    // RUNG per line for a permanently-visible HTML editor floating beside
    // its dimension, which cost +8 / +50 / +125 units on the solo /
    // two-line / three-line builds. Tap-to-edit needs none of it: the
    // number is drawn as a chip in the row itself, and the ONE editor pill
    // that can be open is TRANSIENT — it floats over the drawing for as
    // long as it is up and takes nothing while it is down. So every figure
    // below is the one it had before the editors came back:
    //     solo mainline   222
    //     + backup        300
    //     + extender      300   (the band's two pieces share one row again)
    // and since bc-segment-builder.css caps the svg's height, the shorter canvas is
    // drawn BIGGER — the same currency Jerry has been asking for more loop
    // in all week.
    //
    // How the budget spends, and what each number is bought against:
    //   22   caption baseline. 12px uppercase (brand.css) is ~8 units of
    //        ink above the baseline and none below, so it opens at ~14 —
    //        about the 15-unit margin the bands use.
    //   72   dimension row. It is the TALLEST row here: the role label's
    //        ink sits 10–18 above, and the witness ticks run 19 below. So
    //        it occupies 54–91 and needs clearance on both sides. A value
    //        the span cannot hold FLOATS on this row, and floats UP (see
    //        _drawLengthDim): ink at 38–48, 6 clear of the label's own ink
    //        at 54, with its chip's hit rect at 32–52 — 2 clear of the
    //        label below it and 10 under the caption's baseline above. The
    //        everyday case is inline, ~4 units either side of the line.
    //
    //        60 -> 72 (Jerry, 2026-08-01: "the gap between the length
    //        arrow and the mainline is very big compared to the others").
    //        He is measuring 84 units of near-empty canvas against the
    //        backup row's B_DIM_GAP of 70 — and that 70 reads shorter
    //        still, because it is FILLED (loops, an "Edit" pill, the
    //        drape's scallops) where the mainline's held nothing but the
    //        two pills at the very ends. The 12 units come out of the
    //        space under the caption, which is where this budget's slack
    //        belongs: air under a heading reads as a heading, air between
    //        a dimension and the thing it measures reads as a mistake.
    //   144  the line. What comes UP off it is the ceiling this row is
    //        held away from: with a band hanging below, the mainline's
    //        "Edit" pills move above its loops (pillAbove), and a pill
    //        reaches at most 49 above the line —
    //        121 − webT − halfHFor's 20-unit ceiling − max(1.5, bodyPxPerMm),
    //        = y 95 at the fattest strand the body scale can draw (a
    //        20 × 2 mm webbing at the 40/widthMm cap, on a canvas wide
    //        enough to run the body scale to it). The LOWEST thing this
    //        row puts out is its two witness ticks at dimY + 19 (see
    //        _drawLengthDim; the arrowheads reach +4), so 72 + 19 = 91
    //        clears even that theoretical pill by 4 — 6 under the worst
    //        case actually reachable with a 25 mm webbing (a 300 mm
    //        opening on a desktop canvas puts the pill at 97), and ~17 in
    //        an ordinary build (108).
    //
    //        This is ALSO why the mainline's value floats UP where v3's
    //        dropped down: a dropped value baselines at +28 and its chip's
    //        hit area reaches +34, i.e. y 106 — straight through that
    //        pill. v3 got away with it because the value was inert ink
    //        that only overlapped in an extreme; a TARGET may not overlap
    //        another target at all. The band's rows float DOWN for the
    //        mirror-image reason: above them is the drawing (loops, "Edit"
    //        pills, the drape's scallops) and below them is DIM_FOOT's
    //        open canvas.
    //
    //        It was 94 until 2026-08-01: the LENGTH ROW HOTSPOT's hover
    //        frame reached dimY + 22, three units past the ticks, and that
    //        frame was the number this clearance was written against. That
    //        whole-row frame is gone for good (Jerry — see
    //        _drawPieceHotspots); round 5's chip is a value-sized target
    //        reaching +10 inline, well inside the ticks. 72 was NOT tuned
    //        against 94 in the first place — it is Jerry's gap judgement
    //        above — so the 3 units are slack, not a reason to move the
    //        row.
    //
    //        The ticks CAN still reach 2 units into the end hotspot's
    //        outline in that same extreme. That is a hairline over a
    //        dashed outline; the chip sits at the span's CENTRE, far from
    //        either end hotspot's x range, and is drawn BEFORE them, so an
    //        end still wins any overlap a squeezed band could produce.
    //        The loop art itself never gets above 118.
    //
    //        SOLO (no band) is looser and needs nothing of its own: the
    //        pills hang BELOW the loops there, so the highest thing over
    //        the line is a tail-up loop's own art at 118 and its hit
    //        outline at 112.
    const SIDE_CAP_Y = 22;    // "Side view" caption baseline
    const DIM_Y = 72;         // primary length dimension
    const LINE_Y = 144;       // side-view main line
    // (Loop eye half-height is derived per end — see halfHFor.)

    // Clear air between a length dimension's LINE and the NEAR EDGE of the
    // editor pill open on it. The pill floats on the side that row's value
    // floats to, so this reads two ways, and both are ~3–4 units of
    // daylight past the row's outermost ink:
    //   DOWN (the band's rows) the ticks run to +19, so a pill top at +22
    //        sits 3 units below the lowest thing the row draws.
    //   UP   (the mainline) the role label's ink starts at −18, so a pill
    //        bottom at −22 sits 4 units above the highest.
    // NO canvas is reserved for the pill beyond this offset — one is open
    // at a time, it is transient, and it deliberately floats OVER the
    // drawing (the mockup Jerry picked from centred it ON the line
    // outright; clearing the row at all is this implementation being
    // gentler than the thing he approved). The builder clamps it inside
    // the svg's own foot, so on the band's row — where DIM_FOOT leaves 34
    // units and a phone pill wants ~30 plus this gap — the clamp lifts it
    // back over the dimension it is editing. That is the accepted cost of
    // the round: the row the pill covers is the row you are editing, and
    // what it covers is the number it is holding.
    const ED_CLEAR = 22;

    // The pencil riding every editable length value (Jerry, 2026-08-02:
    // "also throw a pencil icon on those lengths to show that they are
    // editable"), and the air between it and the number. 10 units against
    // the 11px value's ~8 of figure height, so it reads as the number's
    // own size rather than as a badge parked next to it.
    //
    // 10 and not 9, which was the first try: the glyph is an OUTLINE, and
    // the feather pencil's body is only a quarter of its box across — at 9
    // units the 1.2 stroke on each long edge left under a unit of white
    // between them and the whole thing filled in and read as a slanted
    // lozenge. A unit of size is worth more here than a tenth of stroke,
    // since thinning it instead costs the glyph its presence next to
    // 600-weight digits. Same reason the gap is 3 and not 2: the nib is
    // the ink nearest the number and it sits at the number's own height.
    //
    // Together they are the amount the VALUE UNIT grew, and everything
    // sized from the value has to grow by the same 13: the hole the inline
    // dimension line leaves for it, the test for whether that hole fits at
    // all, and the chip's hit rect. The role label's clearance does NOT
    // need touching — it is measured off the hole's left edge, which moves
    // left by exactly as much as the text does (see below).
    const PEN_W = 10;
    const PEN_GAP = 3;
    const PEN_TOT = PEN_W + PEN_GAP;

    // Room below the LAST line: its loops plus the "Edit" pill hanging off
    // them; and below a length-dimension row, its ticks (+19) plus a value
    // the span was too narrow to hold inline, which floats under the line
    // (baseline +28, chip underline +31, the chip's hit rect to +34) — see
    // _drawLengthDim. 34 was v3's figure for the ink alone and still fits:
    // the chip is padding around that same ink, and it lands exactly on
    // the foot.
    const MAIN_FOOT = 78;
    const DIM_FOOT = 34;
    // Canvas height of a MAINLINE-ONLY build. The canvas is dynamic
    // (fullLayout), and this is its floor.
    const BASE_VB_H = LINE_Y + MAIN_FOOT;

    // The BACKUP band, appended below the mainline when a backup line
    // exists. The extender SHARES the band (split horizontally — see
    // _bands) AND its one dimension row: each piece dimensions its own
    // span of that row, with its own value and its own role label. A third
    // line therefore costs no canvas height at all.
    //   B_HANG_GAP    mainline line -> the backup's: the DROP its connector
    //                 ties span (see drapePath). The backup hangs JUST
    //                 under the mainline (Jerry, 2026-07-31 from staging —
    //                 it was 110, more than twice this), so the gap is only
    //                 what the two pieces' loops need: ~26 units of backup
    //                 loop above, ~26 of mainline loop below, and enough
    //                 tie left over to read as a tie. It fits because the
    //                 mainline's "Edit" pills move to the other side of its
    //                 loops whenever a band hangs below it (see _drawFull's
    //                 pillAbove) — that pill was what used to own this
    //                 space.
    //   B_DIM_GAP     that line -> its length-dimension row: clears the
    //                 backup's own loops + pill (~45 below the line) and
    //                 the drape's deepest scallop (SAG_MAX below it). It
    //                 is also the figure the MAINLINE's own row is read
    //                 against (DIM_Y sits 72 above its line to this 70
    //                 below), so a change here is a comparison the top of
    //                 the canvas makes too. It carries NO term for an
    //                 editor: round 4's permanently-visible box needed one
    //                 and could not have it (25 units of gap against a
    //                 30-unit pill — which is why the band's boxes went
    //                 BELOW their rows and had to buy rungs), and round
    //                 5's pill is transient and floats over whatever is
    //                 there.
    // (The band's role name rides its length-dimension row rather than a
    // caption row of its own — a centred caption between the two lines
    // sat exactly where the ties hang.)
    const B_HANG_GAP = 52;
    const B_DIM_GAP = 70;

    // Backup-row split when an extender is present: proportional to the
    // two pieces' lengths, clamped so neither collapses to a token stub,
    // with a gap between their facing ends.
    const SPLIT_MIN = 0.35;
    const SPLIT_MAX = 0.8;
    const SPLIT_GAP = 18;

    // ONE dimension row for the whole band, with each piece dimensioning
    // its own span of it. There is no second rung and no dropped rung: a
    // split band's two dimensions sit side by side at B_DIM_GAP, exactly
    // where an unsplit backup's single one always has.
    //
    // WHY IT CAN. Everything that ever forced a piece's dimension onto a
    // rung of its own was an HTML LENGTH EDITOR that had to be on screen
    // at the same time as the other piece's — a box at a fixed CSS width
    // dropped onto a band that is a fraction of a row that is itself a
    // fraction of the canvas. On a 390-unit phone a custom-mode pill
    // measures ~200 units, so two of them side by side want 400+ where
    // there are 390, at ANY split ratio. v2 and v4 both hit that wall and
    // both bought their way out with canvas (B_DIM_STEP, +95 units per
    // extra row).
    //
    // Round 5 dissolves the constraint rather than paying it: only ONE
    // editor is ever open, so two boxes never have to fit anywhere at all.
    // What shares this row is the VALUE — "12 m" at the 11px dim-label
    // size is ~28 units, bounded by its own span instead of by a CSS box
    // that does not shrink with it. Two of those fit side by side at any
    // split ratio on any viewport, and on the tightest real case (a
    // phone's ~390-unit canvas, an extender at the SPLIT_MAX floor of a
    // fifth of the row) the value stops fitting INLINE and floats under
    // its own line instead — which costs no canvas, since DIM_FOOT already
    // reserves the room.

    // RELATIVE LENGTHS (Jerry, 2026-07-31 from staging): the drawn spans
    // are proportional to the real ones, so editing any line's length
    // shows up against the others. The LONGEST line owns the full drawable
    // width (which is why a solo mainline — always the longest — draws
    // exactly as it always has), and every other line is scaled by its
    // ratio to it. The floor keeps a much shorter line legible: two loops,
    // a middle, and enough span for a dimension to read as one. (It no
    // longer has to hold a permanently-visible EDITOR — round 5's pill is
    // transient and clamps itself into the drawing wherever it lands — so
    // a piece under the floor only has to fit its value, which floats
    // under its own line when the span is too tight.) A line whose length
    // can't be read draws full width, as before.
    //
    // Every row is CENTRED in the canvas (Jerry, 2026-08-01). It was
    // LEFT-anchored at a common margin until then, on the reasoning that
    // the system is rigged off ONE anchor, so the loops should line up at
    // matching x and the length difference should read at the far end.
    // What killed that: the backup ROW is backup + extender, so adding an
    // extender routinely makes it LONGER than the mainline — and a row that
    // HANGS UNDER the mainline running out past the end of the line it
    // backs up reads as a drawing error, not as extra length. Centred, the
    // shorter row sits inside the longer one at both ends, and the overhang
    // reads as the slack a backup is supposed to have. Symmetric by
    // construction: the longest row always owns the full width, so
    // whichever row is shorter is the one that gets centred.
    const SPAN_MIN_SHARE = 0.3;

    const GHOST_RESERVE = 110;   // px kept clear for a bare end's ghost affordance
    // The end scale's reference: a nominal END_REF_MM fold draws at
    // END_REF_SHARE of the canvas width (see _planPieces).
    //
    // 0.155 -> 0.19 (Jerry, 2026-08-01: "lets increase the size of the
    // loops a little, so we can see the orientation better"). Orientation
    // reads off the two-tone strands of the hairpin, and the eye is what
    // separates them: the eye grows with this share, and the loop's HEIGHT
    // follows the eye through halfHFor — so this one number is what makes
    // a flipped loop legible. It is +22.6% on the drawn fold, and every
    // downstream extent (throats, tails, hit areas, pill anchors, zoom
    // windows) is derived from it, so nothing else moves by hand.
    //
    // 0.19 and not 0.20, which was the other candidate: at 0.19 a standard
    // 390-unit phone canvas is still governed by THIS share (0.349 against
    // the END_MAX_SHARE ceiling's 0.360), so a loop grows by the same
    // 22.6% at every viewport. 0.20 crosses that line (0.367 > 0.360) and
    // the phone silently falls under the ceiling instead, which buys ~1.5%
    // there against +5% on a desktop — a wider phone/desktop divergence for
    // almost nothing.
    //
    // END_MAX_SHARE stays at 0.42. It is not a second size knob, it is the
    // OUTSIZED-PAIR ceiling ("two 300 mm openings"), and raising the
    // reference did not put a nominal pair anywhere near it: on a
    // 1000-unit band a nominal pair now draws at 0.623 against a ceiling of
    // 0.97, and the two only meet at END_REF_SHARE ~0.296. Raising the
    // ceiling in proportion would only loosen the case it exists to catch.
    const END_REF_MM = 305;
    const END_REF_SHARE = 0.19;
    const END_MAX_SHARE = 0.42;

    const ROLE_NAME = { main: 'Mainline', backup: 'Backup', extender: 'Extender' };
    const roleName = (role) => ROLE_NAME[role] || role;

    // Row layout for the full view, from the model's lines: the mainline,
    // and — when a backup exists — the band that hangs under it (the
    // extender shares that band). One layout for every viewport since
    // round 3; there is no plan-view row left to drop.
    function fullLayout(lines) {
      const rows = {
        h: BASE_VB_H,
        main: { sideCapY: SIDE_CAP_Y, dimY: DIM_Y, lineY: LINE_Y },
        backup: null,
      };
      if (!lines.some((l) => l && l.role === 'backup')) return rows;
      const lineY = LINE_Y + B_HANG_GAP;
      const dimY = lineY + B_DIM_GAP;
      // ONE dimension row for the whole band, whether or not an extender
      // splits it — the two pieces dimension their own halves of it (see
      // the B_DIM_GAP block). A third line costs no canvas height at all,
      // which is what puts a three-line build back at 300.
      rows.backup = { lineY, dimY };
      rows.h = dimY + DIM_FOOT;
      return rows;
    }

    // ---- the drape ---------------------------------------------------------
    //
    // A backup line doesn't run parallel to the mainline, it HANGS from it
    // (Jerry, 2026-07-31: "kind of like a shower curtain") — tied off every
    // few metres, sagging in shallow scallops between the tie-offs. The
    // extender carries the same treatment across its band, hung from the
    // same mainline.
    //
    // Spacing is a LENGTH, not a pixel count: the piece's own metres decide
    // how many ties its band shows, and they spread evenly across it (the
    // band width is proportional to the length, so even spacing IS even
    // metres). The clamps stop a 15 m line looking bare and a 200 m one
    // turning into a comb — and TIE_MIN_GAP_PX keeps the scallops readable
    // on a phone, where the same count would land twice as tight.
    const TIE_SPACING_M = 4.5;
    const TIE_MIN_SPANS = 3;      // = 2 interior ties + the two ends
    const TIE_MAX_SPANS = 9;
    const TIE_MIN_GAP_PX = 46;    // viewBox units between ties
    const TIE_TOP_PAD = 2;        // clear of the mainline's tail stripe
    // Sag depth per scallop, proportional to the span it crosses (a longer
    // unsupported run drops further) between a floor that still reads as a
    // curve and a ceiling that keeps the deepest point well clear of the
    // length-dimension row below (B_DIM_GAP).
    // (0.18 of the span puts a typical scallop at ~4x the drawn webbing
    // thickness — deep enough to read as a hanging line at phone scale,
    // shallow enough that the drape still reads as one line rather than a
    // row of festoons.)
    const SAG_RATIO = 0.18;
    const SAG_MIN = 6;
    const SAG_MAX = 20;
    const sagFor = (span) => clamp(SAG_MIN, span * SAG_RATIO, SAG_MAX);

    // Ties are tied by hand in the field, so dead-even spacing reads as a
    // machine drawing. Interior ties get a jitter of up to ±TIE_JITTER of
    // the even span — which is also why they can never cross or collide:
    // at ±25 % the closest two neighbours can get is half a span.
    //
    // The offsets are DETERMINISTIC, seeded off the piece's identity
    // (role + length). Math.random here would be a bug, not a shortcut: a
    // drag redraws the scene on every frame, and ties that resampled would
    // dance under the pointer. Changing the LENGTH re-rolls them, which is
    // the one moment the whole line is being re-laid anyway.
    const TIE_JITTER = 0.25;
    function hashSeed(str) {
      let h = 2166136261;                       // FNV-1a, 32-bit
      for (let i = 0; i < str.length; i += 1) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      return h >>> 0;
    }
    function seededRandom(seed) {               // mulberry32
      let s = seed >>> 0;
      return () => {
        s = (s + 0x6d2b79f5) >>> 0;
        let t = Math.imul(s ^ (s >>> 15), 1 | s);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
    }

    // Tie-off x positions across [x1, x2], both ends included (those two
    // are the piece's SEWN ends and never move). Null when there is no
    // span to hang anything across (a piece squeezed until its two sewn
    // ends meet) — the caller draws it straight instead.
    function tieXs(x1, x2, lengthM, seedKey) {
      const width = x2 - x1;
      if (!(width > 0)) return null;
      const byLength = lengthM > 0 ? Math.ceil(lengthM / TIE_SPACING_M) : 4;
      const byWidth = Math.floor(width / TIE_MIN_GAP_PX);
      const spans = clamp(TIE_MIN_SPANS, Math.min(byLength, byWidth), TIE_MAX_SPANS);
      const step = width / spans;
      const rand = seededRandom(hashSeed(String(seedKey || '')));
      const out = [x1];
      for (let i = 1; i < spans; i += 1) {
        out.push(x1 + step * i + (rand() * 2 - 1) * step * TIE_JITTER);
      }
      out.push(x2);
      return out;
    }

    // The hanging line at base y: straight through each SEWN end (two
    // layers of webbing don't sag, and the stitching, the tail stripe and
    // the loop's own strands all continue from a flat line there), then one
    // cubic scallop per span. Each cubic's control points sit at the same
    // height as its ends, so the curve leaves and meets every tie
    // HORIZONTALLY — what a gathered curtain does between its rings, and
    // what keeps the drape tangent to the straight stroke at the throats.
    // Mid-span depth of that cubic is 3/4 of the control offset, hence the
    // 4/3 below.
    function drapePath(xs, y, fromX, toX) {
      let d = `M ${fromX} ${y} L ${xs[0]} ${y}`;
      for (let i = 1; i < xs.length; i += 1) {
        const a = xs[i - 1];
        const b = xs[i];
        const s = b - a;
        const c = y + (sagFor(s) * 4) / 3;
        d += ` C ${(a + s / 3).toFixed(2)} ${c.toFixed(2)} ${(b - s / 3).toFixed(2)} ${c.toFixed(2)} ${b.toFixed(2)} ${y.toFixed(2)}`;
      }
      return `${d} L ${toX} ${y}`;
    }

    // An end's loop straddles this y: the line's centre shifted half a
    // thickness toward the side its TAIL folds to. Everything that has to
    // know where an end's art actually sits — its pill, its hit area, the
    // ties passing under it — measures from here rather than assuming a
    // direction, because since round 3 each end folds independently
    // (`end.tailUp`).
    const endMidY = (lineY, webT, end) => lineY + (end && end.tailUp ? -1 : 1) * (webT / 2);

    // Teardrop height follows the eye length so long openings don't
    // read as slivers and short ones don't balloon. Held a little
    // past true proportion — a small loop needs a visible hole — but
    // only a little: the ratio has to stay near the zoomed view's
    // ~4:1, or a short loop rounds off into a ring and stops reading
    // as webbing doubled back on itself.
    const halfHFor = (eyePx) => clamp(7, eyePx * 0.16, 20);

    // An end's mm figures: the eye (= opening), the two-layer sewn zone
    // (= pattern length + the 5 mm tail tab) and the fold they add up to.
    // The same equation endGeometry runs — two places carry the +5 rule.
    const endMm = (end) => {
      const p = end.pattern || null;
      const eyeMm = eyeMmFor(end);   // shared with endGeometry + sleeveCoverage
      const patchMm = p && p.patternLengthMm != null ? p.patternLengthMm : null;
      const sewnMm = patchMm != null ? patchMm + 5
        : Math.max(20, (end.foldLiveMm != null ? end.foldLiveMm : 210) - eyeMm);
      return { eyeMm, sewnMm, foldMm: eyeMm + sewnMm };
    };

    // A line's length in METRES, for LAYOUT only (the backup/extender
    // split). `lengthM` when the model carries one; otherwise the display
    // string is parsed, because the split has to follow the numbers the
    // customer actually chose. Null when the line has no orderable length
    // yet — the caller falls back to an even split. FT_PER_M mirrors the
    // builder's own constant.
    function lineLengthM(line) {
      if (line && Number(line.lengthM) > 0) return Number(line.lengthM);
      const s = String((line && line.lengthText) || '');
      const num = parseFloat(s.replace(',', '.'));
      if (!(num > 0)) return null;
      if (/ft|feet|foot|′|'/i.test(s)) return num / 3.3;
      if (/cm/i.test(s)) return num / 100;
      return num;
    }

    // Part ids are role-scoped ('backup:end1') and the MAINLINE's are bare
    // ('end1') — the builder mints both spellings (its _partId), so ids
    // are compared normalized rather than by string equality.
    const normPart = (id) => {
      const s = String(id == null ? '' : id);
      return s.indexOf(':') === -1 ? `main:${s}` : s;
    };
    const samePart = (a, b) => a != null && b != null && normPart(a) === normPart(b);

    // 'full' -> null; '<role>:end1|end2' (bare = the mainline's) -> the
    // zoomed end it names. Anything else reads as the full view.
    function parseView(view) {
      const v = String(view || 'full');
      const i = v.indexOf(':');
      const role = i === -1 ? 'main' : v.slice(0, i);
      const part = i === -1 ? v : v.slice(i + 1);
      if (part !== 'end1' && part !== 'end2') return null;
      return { role, part, end: part === 'end1' ? 1 : 2, key: v };
    }

    class BcSegmentScene extends HTMLElement {
      // One-time SETUP is separate from the observers, which are torn down
      // on disconnect and so have to come back on every connect — a scene
      // moved in the DOM (the builder's layout does that on the mobile
      // breakpoint) would otherwise come back deaf to both resizes and the
      // media query.
      connectedCallback() {
        if (!this._init) {
          this._init = true;
          this._uid = `seg${(uidCounter += 1)}`;
          this.svg = this.querySelector('svg');
          this._vbW = 720;
          this._view = 'full';
        }
        this._bindObservers();
        this._bindDrag();
        this._onResize();
      }

      disconnectedCallback() {
        this._unbindObservers();
        // An in-flight drag dies with the element — the pointer capture
        // went with the svg, and a re-attached scene must not resume a
        // gesture whose pointer is long gone.
        this._drag = null;
        this._cancelTween();
      }

      // Idempotent: a second connect without a disconnect must not stack a
      // second observer or a second media listener.
      _bindObservers() {
        if (!this._ro && typeof ResizeObserver === 'function') {
          this._ro = new ResizeObserver(() => this._onResize());
          this._ro.observe(this);
        }
      }

      _unbindObservers() {
        if (this._ro) { this._ro.disconnect(); this._ro = null; }
      }

      // ---- direct manipulation (zoomed end view) ---------------------------
      //
      // Dragging the opening handle resizes the opening, dragging the
      // sleeve handle resizes the sleeve (Jerry, 2026-07-31 — this is what
      // the end panel's rail sliders became). The scene is a CONTROL, not a
      // model: it emits the millimetres the gesture asks for and redraws
      // only when the builder renders it back, so the handle stops at a
      // bound because the clamped state stopped, not because the scene
      // second-guessed the drag.
      //
      // Listeners live on the SVG ROOT, not the handles: every render
      // replaces the handle markup, and a drag has to survive that. The
      // root also takes the pointer capture for the same reason.
      _bindDrag() {
        const svg = this.svg;
        if (!svg || this._dragBound) return;
        this._dragBound = true;
        svg.addEventListener('pointerdown', (e) => this._dragStart(e));
        svg.addEventListener('pointermove', (e) => this._dragMove(e));
        svg.addEventListener('pointerup', (e) => this._dragEnd(e));
        svg.addEventListener('pointercancel', (e) => this._dragEnd(e));
        // One-shot buttons drawn INSIDE the zoom composition (the naked
        // end's "+ Sleeve", and the ✕s that remove the sleeve or the whole
        // loop). Delegated from the root for the same reason the drag is:
        // their markup is replaced by every render.
        svg.addEventListener('click', (e) => this._endAction(e));
        svg.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') this._endAction(e, true);
        });
      }

      // `part` is the zoomed view's id, the same vocabulary the drag and
      // the hotspots speak. The scene only reports the intent — applying
      // it (and refusing it out of bounds) is the builder's job.
      _endAction(e, isKey) {
        // Inert to a pointer that is already captured, and to the click
        // that trails the release of one (see _dragEnd).
        if (this._drag) return;
        if (!isKey && this._swallowClick) { this._swallowClick = false; return; }
        const host = e.target && e.target.closest ? e.target.closest('[data-bc-end-action]') : null;
        if (!host) return;
        const part = (this._zoomGeo && this._zoomGeo.part) || this._view;
        if (!part || part === 'full') return;
        if (isKey && e.preventDefault) e.preventDefault();
        this.dispatchEvent(new CustomEvent('bc:segment:end-action', {
          bubbles: true,
          composed: true,
          detail: { part, action: host.getAttribute('data-bc-end-action') },
        }));
      }

      // Multiplier that keeps a DRAG_HIT_W-wide target at MIN_HIT_PX on
      // screen. Reads the cached px/unit (see _measureUnitPx) — never the
      // DOM, because this runs inside every draw and a zoom view redraws
      // on every frame of a drag.
      _hitScale() {
        const unitPx = this._unitPx || 0;
        if (!(unitPx > 0)) return 1;
        return Math.max(1, MIN_HIT_PX / (DRAG_HIT_W * unitPx));
      }

      // One layout read, taken only where the geometry that feeds it just
      // changed: on resize, and on a draw that moved the viewBox.
      _measureUnitPx() {
        const scale = this._clientScale();
        if (scale > 0) this._unitPx = scale;
      }

      // CSS px per drawn viewBox unit — the svg's own letterboxed scale.
      // (The zoom host only ever translates its composition, so there is
      // no second factor to fold in.)
      _clientScale() {
        if (!this.svg || !this.svg.getBoundingClientRect) return 0;
        const r = this.svg.getBoundingClientRect();
        if (!r.width || !r.height) return 0;
        return Math.min(r.width / this._vbW, r.height / this._vbH());
      }

      _dragStart(e) {
        // Any press — handle or not — starts a fresh gesture, so it
        // clears the "the last drag ended here" guard (see _endAction).
        this._swallowClick = false;
        // Never fight the zoom tween (its viewBox is mid-flight, so the
        // px→mm scale is a moving target) and never start from anything
        // that isn't a handle — every other pointerdown belongs to the
        // hotspots' click/keyboard path. An already-live drag also can't
        // be restarted: the geometry redrawing under the pointer (the
        // sleeve and its Add button appear and disappear as an opening
        // drag crosses the sleeve floor) must not touch the gesture.
        if (this._tweening || this._drag) return;
        const host = e.target && e.target.closest ? e.target.closest('[data-bc-drag]') : null;
        if (!host) return;
        const zg = this._zoomGeo;
        const field = host.getAttribute('data-bc-drag');
        const start = zg && zg.fields ? zg.fields[field] : null;
        const scale = this._clientScale();
        if (start == null || !(zg.pxPerMm > 0) || !(scale > 0)) return;
        if (e.cancelable) e.preventDefault();
        // mm per client pixel, signed for this end and field:
        //   the throat travels AWAY from the fold as the opening grows, so
        //   dragging with −dir opens it up;
        //   the sleeve's drawn edge moves half a millimetre per millimetre
        //   of sleeve (it wraps the fold), so its sense is doubled.
        const sense = (field === 'sleeve' ? 2 : 1) * -zg.dir;
        this._drag = {
          id: e.pointerId,
          field,
          part: zg.part,
          start,
          clientX0: e.clientX,
          mmPerPx: sense / (zg.pxPerMm * scale),
          // Seeded with the start value so a gesture that hasn't moved a
          // whole millimetre yet — or has come back to where it began —
          // asks the builder for nothing.
          last: start,
        };
        try { this.svg.setPointerCapture(e.pointerId); } catch (_err) { /* no capture, still tracked */ }
      }

      // The value the gesture is asking for: ALWAYS the value at drag start
      // plus the total travel since, never an accumulation of per-move
      // deltas — so a re-render mid-drag (which is the normal case: the
      // builder redraws on every 'drag' event) can't drift the gesture.
      _dragValue(d, e) {
        return Math.round(d.start + (e.clientX - d.clientX0) * d.mmPerPx);
      }

      _dragMove(e) {
        const d = this._drag;
        if (!d || e.pointerId !== d.id) return;
        // A release the scene never saw (capture refused, or a pointerup
        // outside the element) would otherwise leave every hover move
        // dragging with no button down. Touch and pen report buttons=1 for
        // as long as they are down, so this only ever catches a mouse.
        if (e.buttons === 0) { this._dragEnd(e); return; }
        if (e.cancelable) e.preventDefault();
        const valueMm = this._dragValue(d, e);
        if (valueMm === d.last) return;   // one event per millimetre, not per pixel
        d.last = valueMm;
        this._emitDrag(d, 'drag', valueMm);
      }

      _dragEnd(e) {
        const d = this._drag;
        if (!d || e.pointerId !== d.id) return;
        const valueMm = this._dragValue(d, e);
        this._drag = null;
        // A gesture can finish anywhere — including on top of a button
        // that appeared under the pointer mid-drag. Whatever click the
        // browser synthesises from this release is not a press on that
        // button, so the next one is swallowed; the following pointerdown
        // (which any real press begins with) clears the guard again.
        this._swallowClick = true;
        try { this.svg.releasePointerCapture(e.pointerId); } catch (_err) { /* already gone */ }
        // The commit always fires, unchanged value included: it is what
        // tells the builder to run its bounds pass and rebuild the panel.
        this._emitDrag(d, 'commit', valueMm);
      }

      // `part` is the zoomed view's own id — bare 'end1' on the mainline,
      // '<role>:end<N>' otherwise — so the builder routes the value with
      // the same vocabulary it routes hotspot clicks and anchors with.
      _emitDrag(d, phase, valueMm) {
        this.dispatchEvent(new CustomEvent('bc:segment:dim-drag', {
          bubbles: true,
          composed: true,
          detail: { part: d.part, field: d.field, valueMm, phase },
        }));
      }

      _onResize() {
        const w = this.clientWidth;
        if (!w) return;
        const vbW = this._computeVbW(w, this._vbH());
        this._measureUnitPx();
        if (vbW === this._vbW) return;
        this._vbW = vbW;
        this._cancelTween();
        if (this._model) this._draw(this._view);
        else if (this.svg) this.svg.setAttribute('viewBox', `0 0 ${vbW} ${this._vbH()}`);
      }

      // viewBox WIDTH for a container width `w` at canvas height `vbH`.
      // Split out of _onResize because the height is dynamic now (a backup
      // line makes the canvas taller), so a draw has to be able to re-run
      // this without a resize event — see _draw.
      //
      // The svg is width:100% / height:auto with a max-height cap (44vh,
      // 26vh compact — bc-segment-builder.css). When the cap binds, a viewBox sized
      // from the width alone letterboxes horizontally (preserveAspectRatio
      // "meet") and wastes side margins — so widen vbW to the aspect the
      // cap actually renders, letting the drawing fill the box.
      // Below MIN_VB_W the viewBox tracks the container 1:1 (a hard
      // floor scaled the WHOLE render down on phones — a 390px column
      // drew every unit at 0.7 CSS px, dimension text at ~8px). The
      // same relaxed floor applies in the widen branch, or portrait
      // phones (cap-implied width < 560) would snap back to 560.
      _computeVbW(w, vbH) {
        const floor = Math.min(w, MIN_VB_W);
        let units = Math.max(floor, w / UNIT_SCALE);
        const maxH = this.svg ? parseFloat(getComputedStyle(this.svg).maxHeight) : NaN;
        if (maxH > 0 && (w * vbH) / units > maxH) {
          units = Math.max(floor, (w * vbH) / maxH);
        }
        return Math.round(units);
      }

      // Height of the canvas as DRAWN (set by _draw): the full view's row
      // layout, or — in a zoom view — at least the end composition's own
      // Z_VB_H. Since round 3 the full view is side-view-only and much
      // shorter than that composition, so a zoom no longer squeezes into
      // it: the detail you opened draws full size and the element grows
      // for as long as you are in it.
      _vbH() {
        return this._vbHDrawn || this._fullVbH();
      }

      // The FULL view's own height — what the tween flies through and what
      // the overlay anchors are measured in.
      _fullVbH() {
        return (this._rows && this._rows.h) || BASE_VB_H;
      }

      _reduceMotion() {
        return typeof window.matchMedia === 'function'
          && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      snapshotSVG() {
        return this.svg ? this.svg.outerHTML : '';
      }

      // The model is drawn immediately except across a full↔end view switch,
      // where the viewBox tweens into/out of the clicked loop first so the
      // composition swap reads as a camera move, not a jump cut.
      render(model) {
        this._model = model;
        if (!this.svg) return;
        const nextView = model && model.view ? model.view : 'full';
        const prevView = this._view || 'full';
        this._cancelTween();
        const canAnimate = prevView !== nextView
          && (prevView === 'full' || nextView === 'full')
          && !this._reduceMotion();
        this._view = nextView;
        if (!canAnimate) { this._draw(nextView); return; }
        // Both branches draw the full scene FIRST: it is what sets the row
        // layout (and with it the canvas height _fullVB reads) and the end
        // extents _endWindow measures against, for this model.
        if (prevView === 'full') {
          // zoom in: fly the full scene into the loop, then swap.
          this._tweening = true;
          this._draw('full');
          this._tween(this._fullVB(), this._endWindow(nextView), () => {
            this._tweening = false;
            this._draw(nextView);
          });
        } else {
          // zoom out: swap to the full scene, fly back out of the loop.
          this._tweening = true;
          this._draw('full');
          this._setVB(this._endWindow(prevView));
          this._tween(this._endWindow(prevView), this._fullVB(), () => {
            this._tweening = false;
            this._emitRendered();
          });
        }
      }

      // Fired after every composition draw and at tween completion. The
      // builder does two things with it: re-pin its open length pill off
      // the anchors this draw published, and spend the one-shot "the
      // zoomed scene has landed, re-align the panel under it" (see its
      // _alignOnSettle). `tweening` gates both — mid-tween the anchors
      // don't match the on-screen viewBox, so the builder hides the pill
      // outright until it lands. (In practice the pill is already closed
      // by then: the builder closes it on the way into an end view, which
      // is the only thing that tweens. The gate is the backstop.)
      //
      // `geometryChanged` is false on a draw that cannot have moved an
      // anchor (a colour-only redraw — a webbing switch), so the builder
      // can skip the reposition: its getBoundingClientRect right after the
      // svg rebuild forces a full synchronous style recalc + layout of the
      // whole page, for what would be a no-op.
      _emitRendered() {
        // Defaults TRUE for emits that don't come straight from a _draw
        // (tween completion restores the full viewBox, so the anchors'
        // on-screen mapping did change even without a redraw).
        const geometryChanged = this._geometryChanged !== false;
        this._geometryChanged = null;
        this.dispatchEvent(new CustomEvent('bc:segment:scene-rendered', {
          bubbles: true,
          detail: { view: this._view, tweening: !!this._tweening, geometryChanged },
        }));
      }

      // Client-pixel position of a named anchor set during the last draw
      // (viewBox coords → screen, accounting for the preserveAspectRatio
      // "meet" letterbox). Null while unset or before first render — which
      // is how the builder knows to HIDE an open pill: an end view draws
      // no length dimensions at all, so it publishes no length anchors and
      // the pill goes away with the drawing it annotates.
      //
      // `data` hands back the raw anchor, whose `side` (-1 above the
      // dimension, +1 below) tells the builder which way to grow the pill
      // from this point. The anchor is NOT on the dimension line: the
      // scene has already offset it by ED_CLEAR, so the drawing decides
      // where the pill goes and the builder only has to honour it.
      anchorPoint(name) {
        const a = this._anchors && this._anchors[name];
        if (!a || !this.svg) return null;
        const r = this.svg.getBoundingClientRect();
        if (!r.width || !r.height) return null;
        const vbH = this._vbH();
        const scale = Math.min(r.width / this._vbW, r.height / vbH);
        const ox = (r.width - this._vbW * scale) / 2;
        const oy = (r.height - vbH * scale) / 2;
        return { x: r.left + ox + a.x * scale, y: r.top + oy + a.y * scale, scale, data: a };
      }

      // Move the open-pill state onto the svg that is ALREADY on screen —
      // the one write channel this element has that isn't render().
      //
      // WHY IT EXISTS (2026-08-02, Jerry on Staging: "the custom length
      // loads very slowly with this new setup"). Round 5 made opening the
      // pill a model change, and a model change meant render() → _draw →
      // svg.replaceChildren() plus every piece's paths, gradients, loops
      // and hotspots rebuilt, on a page heavy enough that the builder
      // already coalesces its keystroke redraws to one a frame. All of
      // that to stop drawing one number.
      //
      // WHY IT IS SAFE. Nothing a redraw computes depends on `lengthOpen`:
      // the row layout, the bands, the per-piece scales and — the one that
      // matters — the published ANCHOR (x = band centre, y = the row ±
      // ED_CLEAR) are all identical open or closed. The only difference
      // was inside _drawLengthDim, which now draws the chip and BOTH
      // dimension-line variants every pass and lets a class pick between
      // them. So this toggle lands the scene in exactly the state the
      // redraw it replaces would have.
      //
      // Returns false when it CANNOT do that — nothing drawn yet, a view
      // with no length rows in it (any end zoom), or a zoom tween in
      // flight — and the builder falls back to a real render. The model is
      // updated either way, so a draw this element triggers itself (a
      // resize) still agrees.
      //
      // The TWEEN case is behavioural, not defensive: opening a length
      // editor from the build sheet while zoomed into an end zooms out
      // first, and the render that used to follow cancelled that tween
      // mid-flight — a jump rather than a fly-out, which is the honest
      // reading of "take me to a different editor". Only a render can
      // cancel a tween, so this hands the case back.
      setLengthOpen(name) {
        const anchor = name || null;
        if (this._model) this._model.lengthOpen = anchor;
        if (!this._model || !this.svg || this._tweening) return false;
        let found = false;
        this.svg.querySelectorAll('[data-bc-lenrow]').forEach((n) => {
          const on = n.getAttribute('data-bc-lenrow') === anchor;
          if (on) found = true;
          n.classList.toggle('is-lenopen', on);
        });
        // Clearing needs no row to clear (an end zoom draws none, and the
        // builder closes the pill on the way into one); OPENING one that
        // isn't drawn is the caller's cue to render properly.
        return !anchor || found;
      }

      _fullVB() {
        return [0, 0, this._vbW, this._fullVbH()];
      }

      // The zoom-tween window for one end, keyed by the view name it is
      // reached through ('end1' — the mainline's — or '<role>:end1|end2').
      // Every number comes from the last full draw (_endExtents): ends
      // render to scale, so the extent varies with opening + pattern, and
      // with the role's own band and row.
      _endWindow(endKey) {
        const vbW = this._vbW;
        const e = (this._endExtents && this._endExtents[endKey]) || null;
        const ext = e ? e.ext : 300;
        const x0 = e ? e.x0 : 0;
        const x1 = e ? e.x1 : vbW;
        const lineY = e ? e.lineY : LINE_Y;
        const w = Math.min(Math.max(240, ext + 50), vbW * 0.62);
        const h = (w * this._fullVbH()) / vbW;
        const x = /end1$/.test(endKey) ? x0 - 18 : x1 - w + 18;
        return [x, lineY + 6 - h / 2, w, h];
      }

      _setVB(vb) {
        if (this.svg) this.svg.setAttribute('viewBox', vb.map((n) => n.toFixed(2)).join(' '));
      }

      _tween(from, to, done) {
        const start = performance.now();
        const DUR = 260;
        const ease = (p) => (p < 0.5 ? 4 * p * p * p : 1 - ((-2 * p + 2) ** 3) / 2);
        const step = (now) => {
          const p = clamp(0, (now - start) / DUR, 1);
          const e = ease(p);
          this._setVB(from.map((f, i) => f + (to[i] - f) * e));
          if (p < 1) this._raf = requestAnimationFrame(step);
          else { this._raf = null; if (done) done(); }
        };
        this._raf = requestAnimationFrame(step);
      }

      // Cancelling a tween ends the TWEENING STATE with it. Without that,
      // any interruption (a resize, a breakpoint cross, a disconnect) left
      // the flag set for good: every scene-rendered afterwards claimed
      // `tweening: true`, so the builder never repositioned its overlays
      // again, and _dragStart refused every drag.
      _cancelTween() {
        if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
        this._tweening = false;
      }

      // ---- draw dispatch ---------------------------------------------------

      _draw(view) {
        if (!this.svg) return;
        const model = this._model;
        const lines = ((model && model.lines) || []).filter(Boolean);
        // Layout FIRST, before anything is written: the canvas height
        // follows the model (each extra line appends a band) and the width
        // math is derived from the height, since the max-height cap
        // decides the aspect the scene actually renders at. The computed
        // style read inside _computeVbW is why this only re-runs when the
        // height actually moved — a colour-only redraw touches nothing.
        this._rows = fullLayout(lines);
        // A zoom view naming a line the model no longer carries (a stale
        // 'backup:end1' after the backup was removed) falls back to the
        // full scene rather than drawing an empty canvas. Resolved off the
        // model's roles, since the pieces aren't planned yet.
        const named = parseView(view);
        const zoom = named && lines.some((l) => l.role === named.role)
          && (named.role !== 'extender' || lines.some((l) => l.role === 'backup'))
          ? named : null;
        // A zoom view never draws into less room than its composition
        // needs: the full view is side-view-only and shorter than the end
        // composition, so the detail draws at its own size instead of
        // being squeezed into the overview's canvas.
        const vbH = zoom ? Math.max(this._rows.h, Z_VB_H) : this._rows.h;
        if (vbH !== this._vbHUsed) {
          this._vbHUsed = vbH;
          const w = this.clientWidth;
          if (w) this._vbW = this._computeVbW(w, vbH);
          this._vbHDrawn = vbH;
          this._measureUnitPx();
        }
        this._vbHDrawn = vbH;
        this._pieces = lines.length ? this._planPieces(lines) : [];
        this.svg.setAttribute('viewBox', `0 0 ${this._vbW} ${vbH}`);
        this.svg.replaceChildren();
        // Anchors are re-published by this draw, so a view that draws no
        // length dimensions (any end zoom) leaves the map empty and every
        // floating editor is hidden by the builder for as long as it lasts.
        this._anchors = {};
        // Drag handles exist only in a sewn end's zoom view; every other
        // draw clears what a drag would be measured against. An IN-FLIGHT
        // drag keeps its own copy (see _dragStart), so a redraw mid-gesture
        // — the normal case — doesn't interrupt it.
        this._zoomGeo = null;
        if (this._pieces.length) {
          const defs = el('defs');
          this.svg.appendChild(defs);
          if (zoom) {
            const who = zoom.role === 'main' ? '' : `${roleName(zoom.role)} `;
            this.svg.setAttribute('aria-label', `${who}End ${zoom.end} sewn loop detail`);
            this._drawEnd(defs, zoom);
          } else {
            this.svg.setAttribute('aria-label', 'Configured webbing segment — select a part to edit');
            this._drawFull(defs);
          }
        }
        // Anchor GEOMETRY signature: anchor positions depend only on the
        // view, the viewBox (width AND — since the backup line — height)
        // and each piece's band. Client-rect changes are repositioned by
        // the builder's own scroll / resize / transitionend paths, so they
        // are deliberately not in here. When it is unchanged — every
        // colour-only redraw, e.g. a webbing switch — the builder skips
        // its overlay repositioning, whose forced getBoundingClientRect
        // right after this rebuild was a full synchronous style recalc +
        // layout of the whole page.
        const bands = this._pieces.map((p) => `${p.role}:${Math.round(p.x0)}-${Math.round(p.x1)}`).join(',');
        const geo = `${view}|${this._vbW}|${vbH}|${bands}`;
        this._geometryChanged = geo !== this._geoSig;
        this._geoSig = geo;
        this._emitRendered();
      }

      // ---- per-piece plan --------------------------------------------------

      // Each line's horizontal band. There are two DRAWN rows — the
      // mainline, and the backup row (the backup alone until an extender
      // joins it, at which point the two split that row end-to-end: backup
      // left, extender right, since their facing loops are End 2 and End 1,
      // which already fold toward each other, with a gap between them). An
      // extender without a backup is not a state the builder can produce
      // (C3) — ignored here rather than drawn on its own row.
      // (A split band shares its DIMENSION ROW too — each piece
      // dimensions its own span of the one row; see B_DIM_GAP.)
      //
      // Each row is sized against the LONGEST one (spanFor) and then
      // CENTRED in the canvas, so the shorter row sits inside the longer at
      // both ends instead of jutting past it — see SPAN_MIN_SHARE for why.
      _bands(lines, vbW) {
        const out = [];
        const find = (role) => lines.find((l) => l.role === role) || null;
        const main = find('main');
        const backup = find('backup');
        const ext = backup ? find('extender') : null;
        // What each ROW measures: the mainline on its own, and the backup
        // row as backup + extender — the two of them ARE one backup line,
        // so the pair is what compares against the mainline. Unknown at
        // the row level unless every piece in it has a length.
        const mainM = main ? lineLengthM(main) : null;
        const backupM = backup ? lineLengthM(backup) : null;
        const extM = ext ? lineLengthM(ext) : null;
        const rowM = !backup ? null
          : (backupM > 0 && (!ext || extM > 0) ? backupM + (extM || 0) : null);
        const known = [mainM, rowM].filter((v) => v > 0);
        const longest = known.length ? Math.max(...known) : 0;
        const spanFor = (m) => vbW * (m > 0 && longest > 0
          ? clamp(SPAN_MIN_SHARE, m / longest, 1)
          : 1);
        // A row of width w, centred: the full-width row (the longest one)
        // still starts at 0, so a solo mainline draws exactly as it always
        // has.
        const rowX0 = (w) => (vbW - w) / 2;
        if (main) {
          const w = spanFor(mainM);
          const x0 = rowX0(w);
          out.push({ role: 'main', line: main, x0, x1: x0 + w });
        }
        if (backup) {
          const rowW = spanFor(rowM);
          const bx0 = rowX0(rowW);
          if (ext) {
            const f = (backupM > 0 && extM > 0)
              ? clamp(SPLIT_MIN, backupM / (backupM + extM), SPLIT_MAX)
              : 0.5;
            const split = bx0 + rowW * f;
            out.push({ role: 'backup', line: backup, x0: bx0, x1: split - SPLIT_GAP / 2 });
            out.push({ role: 'extender', line: ext, x0: split + SPLIT_GAP / 2, x1: bx0 + rowW });
          } else {
            out.push({ role: 'backup', line: backup, x0: bx0, x1: bx0 + rowW });
          }
        }
        return out;
      }

      // The full view's draw plan: every piece's band, its two axis scales
      // and each x the drawing hangs off. Built before anything is
      // painted, because the hotspots need the same numbers the art does.
      //
      // The full view runs TWO scales, one per axis (Jerry, 2026-07-31),
      // and since 2026-08-01 both are PER PIECE.
      //
      //   bodyPxPerMm — ACROSS the webbing: strip height, strand and
      //     sleeve thickness. Per piece, so a narrow backup still reads
      //     narrower than a wide mainline.
      //   endPxPerMm  — ALONG the line: eye (= opening), sewn zone
      //     (= pattern length + 5 mm tail tab), pattern art, sleeve
      //     footprint. Deliberately SMALLER than the body scale, and each
      //     piece draws its ends as big as its OWN band affords.
      //
      // That last one was a SHARED scene-wide minimum until Jerry decided
      // otherwise on 2026-08-01, and the reasoning it replaced is worth
      // keeping: the loops are what gets compared between the lines ("is
      // the backup's eye the same as the mainline's?"), so drawing them at
      // one mm→px made that comparison honest, and taking the most
      // constrained piece's scale guaranteed no piece was ever drawn
      // bigger than its own band could hold.
      //
      // What it cost, measured: on a build with an EXTENDER the most
      // constrained piece is always the extender, by a long way. Its band
      // is a fifth of a row (the SPLIT_MAX clamp), so its candidate falls
      // out of END_MAX_SHARE rather than the reference share, and the
      // whole scene followed it down — every loop on the canvas, the
      // mainline's included, pinned to the smallest piece and sitting on
      // halfHFor's 7-unit floor. Jerry, shown a 100 m build doing exactly
      // that: "the loops still look small to me on the main diagram,
      // particularly when the lines are long." LEGIBILITY BEATS MATCHED
      // EYES — his call, made with the trade-off in front of him.
      //
      // Harmonizing WITHIN the split row was the obvious half-measure and
      // it does not survive its own numbers: on that build the extender is
      // the short piece, so tying the backup to it holds the backup at the
      // floor too — half of what he was complaining about. Fully per piece
      // it is.
      //
      // The consequence to know, since nothing in the drawing announces it:
      // two IDENTICAL sewn loops can now draw at very different sizes on
      // one diagram (on Jerry's build the mainline's end draws 3.9x the
      // extender's, same pattern, same opening). Each piece is drawn to fit
      // its own band, and a band's width is about LENGTH, not hardware. The
      // place to compare two ends against each other is the zoomed end
      // view, which is to scale and unaffected by any of this.
      //
      // No piece can overflow its band as a result: the candidate
      // _pieceMetrics computes is already bounded by that piece's own
      // bodyPxPerMm, its midMin middle reserve and END_MAX_SHARE, all
      // measured against its own width. The shared minimum was only ever
      // making pieces SMALLER than those bounds, never keeping them safe.
      //
      // The ends used to share the body scale, which drew them to true
      // proportion and ate ~80% of the canvas: two big loops with a
      // token line between them, on a diagram whose subject is a 50 m
      // segment. This view is the overview — the loops read as small
      // terminations and the LINE dominates. The to-scale, full-detail
      // end is what you get by zooming into it (_drawEnd), which sets
      // its own scale and is untouched by any of this. The middle line's
      // LENGTH was symbolic before and stays symbolic (it stands for tens
      // of meters either way; Jerry, 2026-07-30).
      _planPieces(lines) {
        return this._bands(lines, this._vbW)
          .map((b) => this._pieceMetrics(b))
          .map((m) => this._pieceGeometry(m, m.endPxPerMm));
      }

      // Scales + mm figures for one banded line, independent of the other
      // pieces — and since the shared end scale was retired (2026-08-01)
      // the end scale it works out is the one that piece actually draws at,
      // not a candidate _planPieces then minimises across the scene.
      _pieceMetrics(band) {
        const line = band.line;
        const entry = line.entry || {};
        const end1 = line.end1 || null;
        const end2 = line.end2 || null;
        const hasLoop1 = !!(end1 && end1.treatment !== 'none');
        const hasLoop2 = !!(end2 && end2.treatment !== 'none');
        const widthMm = Number(entry.widthMm) || 25;
        const mm1 = hasLoop1 ? endMm(end1) : null;
        const mm2 = hasLoop2 ? endMm(end2) : null;
        const W = band.x1 - band.x0;
        const midMin = clamp(90, W * 0.2, 200)
          + (hasLoop1 ? 0 : GHOST_RESERVE) + (hasLoop2 ? 0 : GHOST_RESERVE);
        const sumFoldMm = (mm1 ? mm1.foldMm : 0) + (mm2 ? mm2.foldMm : 0);
        const capScale = 40 / widthMm;
        const bodyPxPerMm = sumFoldMm > 0
          ? Math.min(capScale, Math.max(0.2, (W - 30 - midMin) / sumFoldMm))
          : capScale;
        // The end scale: a nominal END_REF_MM fold draws at END_REF_SHARE
        // of the band's width, everything else linear from there. Measured
        // against a band no narrower than MIN_VB_W — a phone's viewBox
        // tracks its container 1:1, so scaling the ends off the real width
        // there would take them below what you can see or tap. Bounded
        // twice on the way up: never bigger than true scale (so this can
        // only ever shrink the old drawing), and both ends together never
        // past END_MAX_SHARE of the band, which catches an outsized pair
        // (two 300 mm openings) before they crowd the line out again.
        const endPxPerMm = sumFoldMm > 0
          ? Math.min(
            bodyPxPerMm,
            (Math.max(W, MIN_VB_W) * END_REF_SHARE) / END_REF_MM,
            // Floored: a band narrower than its own 30 units of margin
            // (an extender squeezed on a tiny container) would make this
            // NEGATIVE, which would invert that piece's ends — and did so
            // for every line on the scene back when this was minimised
            // into one shared scale.
            Math.max(0.02, ((W - 30) * END_MAX_SHARE) / sumFoldMm)
          )
          : bodyPxPerMm;
        return {
          role: band.role, line, entry, end1, end2, hasLoop1, hasLoop2, widthMm,
          mm1, mm2, sumFoldMm, bodyPxPerMm, endPxPerMm, x0: band.x0, x1: band.x1,
        };
      }

      // Everything downstream of the piece's end scale: colours, the
      // per-piece ids, and every x the art and the hotspots share. The
      // scale still arrives as an argument rather than being read off `m`,
      // so the one place that decides it stays _planPieces.
      _pieceGeometry(m, endPxPerMm) {
        const p = Object.assign({}, m, { endPxPerMm });
        const scene = (p.entry && p.entry.scene) || {};
        p.top = scene.color || '#2b2b2b';
        p.lower = scene.colorLower || null;
        p.twoTone = !!(scene.twoTone && p.lower);
        p.sleeveColor = scene.sleeveColor || '#c54a2c';
        // The length exactly as the builder's own editor states it — this
        // is the dimension's VALUE (see _drawLengthDim), not just a
        // fallback for the proportional split. Empty until a length is
        // entered, which draws the frame with nothing in it.
        p.lengthText = p.line.lengthText || '';
        // The other face of this line's webbing, for a zoomed end whose own
        // orientation chose it (see _drawEnd). Never used by the full view.
        p.entryFlipped = p.line.entryFlipped || null;
        // Ids: the MAINLINE's keep the names they have always had, so a
        // solo mainline's markup is unchanged down to the clip paths.
        const sfx = p.role === 'main' ? '' : `-${p.role}`;
        p.gradL = `bc-seg-grad-left-${this._uid}${sfx}`;
        p.gradR = `bc-seg-grad-right-${this._uid}${sfx}`;
        p.clipId = `bc-seg-clip-${this._uid}${sfx}`;
        p.eye1 = p.mm1 ? p.mm1.eyeMm * endPxPerMm : 0;
        p.eye2 = p.mm2 ? p.mm2.eyeMm * endPxPerMm : 0;
        p.sewn1 = p.mm1 ? p.mm1.sewnMm * endPxPerMm : 0;
        p.sewn2 = p.mm2 ? p.mm2.sewnMm * endPxPerMm : 0;
        // Fold caps sit at the 15-unit band margin in BOTH views; throat
        // and the sewn zone's tail end follow at scale.
        p.leftThroat = p.x0 + 15 + p.eye1;
        p.rightThroat = p.x1 - 15 - p.eye2;
        p.tailEnd1 = p.leftThroat + p.sewn1;
        p.tailEnd2 = p.rightThroat - p.sewn2;
        p.lineX1 = p.hasLoop1 ? p.leftThroat : p.x0 + 15;
        p.lineX2 = p.hasLoop2 ? p.rightThroat : p.x1 - 15;
        // A bare end's ghost loop follows the same end scale + teardrop
        // rule as a real one, so "add a loop" previews the size you'd get.
        p.ghostEye = clamp(38, 150 * endPxPerMm, 80);
        p.ghostHalfH = halfHFor(p.ghostEye);
        // Side-view strand thickness is the webbing's REAL thickness at
        // the BODY scale (2.5-unit floor keeps hairline webbings visible)
        // — same rule as the zoomed view and the PDP docs figures
        // (Jerry, 2026-07-30).
        p.webT = Math.max(2.5, (Number(p.entry.thicknessMm) || 3) * p.bodyPxPerMm);
        p.sleeveT = p.webT + Math.max(3, 2 * p.bodyPxPerMm);
        // The piece's CLEAR MIDDLE: past whatever each end actually draws
        // (its tail end, or the ghost loop on a bare one). It bounds the two
        // ends' hit areas — and that middle is now DEAD space on every role
        // (the backup/extender body hotspot that used to fill it went on
        // 2026-08-01; see _drawPieceHotspots), so where the two hit areas
        // stop is the whole of what this number decides.
        p.inner1 = (p.hasLoop1 ? p.tailEnd1 : p.x0 + 15 + p.ghostEye) + 10;
        p.inner2 = (p.hasLoop2 ? p.tailEnd2 : p.x1 - 15 - p.ghostEye) - 10;
        if (p.inner1 > p.inner2) {
          // A band too narrow to hold both ends apart (an extender squeezed
          // to a fifth of a phone-width canvas): they MEET at the midpoint
          // instead of overlapping, so a tap near the middle can't open the
          // far end's editor just because that hit area was appended last.
          p.inner1 = (p.inner1 + p.inner2) / 2;
          p.inner2 = p.inner1;
        }
        return p;
      }

      // ---- full segment view ----------------------------------------------

      // SIDE VIEW ONLY since round 3: the mainline's line with its two
      // ends, and — when a backup exists — the band hanging under it, plus
      // a length-dimension row per line. Rows come from fullLayout, bands
      // and scales from _planPieces. The scene draws ONLY what the model
      // says: the pieces it was handed, in the order it was handed them.
      _drawFull(defs) {
        const model = this._model;
        const vbW = this._vbW;
        const rows = this._rows;
        const pieces = this._pieces;

        // Sleeve gradients up front, one pair per piece, in draw order.
        pieces.forEach((p) => {
          defs.appendChild(sleeveGradient(p.gradL, p.sleeveColor, 'left', sleeveCoverage(p.end1)));
          defs.appendChild(sleeveGradient(p.gradR, p.sleeveColor, 'right', sleeveCoverage(p.end2)));
        });

        const g = el('g');
        this.svg.appendChild(g);

        const main = pieces.find((p) => p.role === 'main') || null;
        const band = pieces.filter((p) => p.role !== 'main');
        const rec = model.recommendation || null;
        // Advisory only (nothing blocks): the backup + extender together
        // falling short of the recommended backup length reddens the
        // BACKUP's length dimension — the same one red the builder's
        // out-of-bounds fields use.
        const shortfall = rec && rec.shortfallM > 0 ? rec : null;
        this._endExtents = {};

        // ---- mainline: loops UNDER the line, then the line, then the
        // caption and its length dimension. Hotspots after everything.
        // Drawn in the rows' own top-down order since the caption moved
        // above the dimension (2026-08-01) — they do not overlap, so this
        // is for reading the code, not for paint order.
        if (main) {
          this._drawPieceSide(g, main, rows.main.lineY);
          g.appendChild(el('text', {
            class: 'bc-segment__detail-caption', x: vbW / 2, y: rows.main.sideCapY,
            'text-anchor': 'middle', text: 'Side view',
          }));
          this._drawLengthDim(g, main, rows.main.dimY, 'length', {
            // UP, alone among the three — which is where BOTH a floated
            // value and the editor pill go on this row. Below it is where
            // the mainline's "Edit" pills live once a band hangs under the
            // line (pillAbove puts them at y 103–108, and a floated value's
            // chip would reach 106); above it is 50 units of open canvas
            // between this row and the caption. See the DIM_Y block.
            floatDir: -1,
            // ALWAYS, solo build included (Jerry, 2026-08-01: "it doesn't
            // show when there is only a mainline, but it should"). It was
            // gated on a second line existing, by the same reasoning the
            // builder's _roleTitle uses for its panel headings — with
            // nothing to disambiguate from, a role name is noise. That
            // holds for a heading that says only "Mainline"; it stopped
            // holding once this row became the one place the WEBBING is
            // named too, since "Mainline · Mantra MK4" on a solo build is
            // still telling you what you are looking at.
            label: roleName('main'),
          });
        }

        // ---- backup band: it HANGS from the mainline (ties + scallops —
        // see drapePath), with each piece's own length dimension below.
        // The extender shares the band, x-split (see _bands).
        if (band.length && rows.backup) {
          const mainWebT = main ? main.webT : 3;
          const drape = {
            tieTopY: rows.main.lineY + mainWebT * 1.5 + TIE_TOP_PAD,
            fallbackM: main ? lineLengthM(main.line) : null,
            // How far the MAINLINE reaches: a tie has to have mainline
            // overhead to tie to. Now that the rows are centred (see
            // _bands) a backup row longer than the mainline overhangs it at
            // BOTH ends, and out there the piece hangs free — see
            // _drawPieceSide. Fold cap to fold cap, because the ties under
            // the mainline's own loops are already handled (`clear`), they
            // just start lower.
            spanX0: main ? main.x0 + 15 : -Infinity,
            spanX1: main ? main.x1 - 15 : Infinity,
            // Under the mainline's own ENDS the ceiling is lower: its loop
            // hangs below the line there, and at this hang gap a tie
            // starting at the line would stroke straight through that art.
            //
            // A zone is one end's EYE — fold cap to throat — and nothing
            // more: that is the only x range where the loop reaches below
            // the line. Over the SEWN zone past the throat there is just
            // the line and (tail-down) its tail, both of which the default
            // ceiling already clears — so a tie there stays attached to
            // the line instead of starting in mid-air under a loop that
            // isn't over it. The depth itself is measured off the end's
            // own mid line, since a tail-up end sits half a thickness
            // higher than a tail-down one.
            clear: !main ? [] : [
              main.hasLoop1 && {
                x0: main.x0 + 15, x1: main.leftThroat,
                y: endMidY(rows.main.lineY, main.webT, main.end1)
                  + halfHFor(main.eye1) + main.sleeveT / 2 + TIE_TOP_PAD,
              },
              main.hasLoop2 && {
                x0: main.rightThroat, x1: main.x1 - 15,
                y: endMidY(rows.main.lineY, main.webT, main.end2)
                  + halfHFor(main.eye2) + main.sleeveT / 2 + TIE_TOP_PAD,
              },
            ].filter(Boolean),
          };
          band.forEach((p) => this._drawPieceSide(g, p, rows.backup.lineY, drape));
          // ONE dimension row for both pieces again (round 5): each
          // dimensions its own span of it, with its own value chip and its
          // own role label. The stacked per-piece rungs v2 and v4 needed
          // existed only to keep two permanently-visible EDITORS apart, and
          // there is at most one editor open now — see the B_DIM_GAP block.
          band.forEach((p) => this._drawLengthDim(
            g, p, rows.backup.dimY, `${p.role}:length`,
            {
              // DOWN, both of them: above a band row is its own loops,
              // "Edit" pills and the drape's scallops, ~25 units before the
              // row; below is DIM_FOOT's open canvas.
              floatDir: 1,
              shortfall: p.role === 'backup' ? shortfall : null,
              label: roleName(p.role),
            }
          ));
        }

        // ---- hotspots last — the diagram IS the navigation, and the hit
        // areas have to win at every overlap. Ends only since 2026-08-01
        // (see _drawPieceHotspots), so no dimension row comes along at all:
        // the hit areas are all measured off the line.
        if (main) {
          this._drawPieceHotspots(g, main, {
            lineY: rows.main.lineY,
            // With a band hanging just under it, the space below the
            // mainline's loops belongs to the backup — so its "Edit" pills
            // move to the other side of the loops. Solo, they hang below.
            pillAbove: band.length > 0,
          });
        }
        if (band.length && rows.backup) {
          band.forEach((p) => this._drawPieceHotspots(g, p, { lineY: rows.backup.lineY }));
        }
      }

      // One piece's SIDE view: its two ends' loops (under the line), the
      // line itself, the doubled-back tails and the faint stitching
      // through each stack.
      //
      // Since round 3 the FOLD DIRECTION is per END (`end.tailUp`), not per
      // piece: a line can carry one loop folding down and one folding up,
      // because each loop independently chooses which face ends up on its
      // outside. The LINE never flips — its faces arrive already resolved
      // by the builder, top face up — so only each end's own loop, tail
      // and stitching mirror about the line.
      //
      // `drape` (band pieces only) hangs the line from the mainline
      // instead of running it straight: connector ties up to
      // drape.tieTopY, scallops between them. The SAG is always downward —
      // gravity doesn't care which way a loop folds.
      _drawPieceSide(g, p, lineY, drape) {
        const loopArgs = {
          lineY, t: p.webT, sleeveT: p.sleeveT,
          topColor: p.top, lowerColor: p.lower, twoTone: p.twoTone,
        };
        // (0) the ties, first of all so every later layer paints over
        // their ends: thin muted verticals in the scene's own geometry-
        // outline stroke, which reads as hardware rather than webbing.
        // They stop at the line, where the drape's tie-off points are.
        const lenM = drape ? (lineLengthM(p.line) || drape.fallbackM) : null;
        let xs = drape ? tieXs(p.tailEnd1, p.tailEnd2, lenM, `${p.role}|${lenM}`) : null;
        // A tie hangs off the MAINLINE, so it can only exist where there is
        // mainline above it (drape.spanX0/X1). With the rows centred a
        // longer backup row overhangs the mainline at both ends, and past
        // that overlap the piece hangs FREE from its last tie out to its
        // own sewn end — which is what it does in the field. So an
        // out-of-overlap tie point leaves the hanging PATH as well as
        // losing its stroke: left in, the drape would climb back up to the
        // line at a point with nothing holding it. The two sewn ends always
        // stay in the path (they are where it starts and finishes) but take
        // no tie of their own out there.
        const tied = (x) => !drape || (x >= drape.spanX0 && x <= drape.spanX1);
        if (xs) {
          const all = xs;
          xs = all.filter((x, i) => i === 0 || i === all.length - 1 || tied(x));
          const gt = el('g', { 'aria-hidden': 'true' });
          const zones = drape.clear || [];
          xs.forEach((x) => {
            if (!tied(x)) return;
            const under = zones.find((c) => x >= c.x0 && x <= c.x1);
            const top = under ? Math.max(drape.tieTopY, under.y) : drape.tieTopY;
            gt.appendChild(el('line', {
              class: 'bc-segment__strip-outline',
              x1: x.toFixed(2), y1: top.toFixed(2), x2: x.toFixed(2), y2: lineY,
            }));
          });
          g.appendChild(gt);
        }
        // (1) loops FIRST (under the line) — the same hairpin construction
        // as the zoomed end view, eye length at this PIECE's END scale, so
        // the strand ends continue the straight strokes exactly.
        if (p.hasLoop1) {
          drawHairpin(g, {
            ...loopArgs, throatX: p.leftThroat, dir: -1, eyePx: p.eye1, halfH: halfHFor(p.eye1),
            sleeved: p.end1.treatment === 'sleeved', gradId: p.gradL, flipY: !!p.end1.tailUp,
          });
        }
        if (p.hasLoop2) {
          drawHairpin(g, {
            ...loopArgs, throatX: p.rightThroat, dir: 1, eyePx: p.eye2, halfH: halfHFor(p.eye2),
            sleeved: p.end2.treatment === 'sleeved', gradId: p.gradR, flipY: !!p.end2.tailUp,
          });
        }

        // (2) the line (split halves when two-tone, PDP construction), or
        // the same split halves along the hanging path — a shallow curve
        // offset vertically keeps a constant stroke width, so the two-tone
        // construction survives the sag unchanged.
        if (xs) {
          const strand = (dy, color, width) => g.appendChild(el('path', {
            d: drapePath(xs, lineY + dy, p.lineX1, p.lineX2),
            fill: 'none', stroke: color, 'stroke-width': width, 'stroke-linecap': 'butt',
          }));
          if (p.twoTone) {
            strand(-p.webT / 4, p.top, p.webT / 2);
            strand(p.webT / 4, p.lower, p.webT / 2);
          } else {
            strand(0, p.top, p.webT);
          }
        } else if (p.twoTone) {
          g.appendChild(el('line', { x1: p.lineX1, y1: lineY - p.webT / 4, x2: p.lineX2, y2: lineY - p.webT / 4, stroke: p.top, 'stroke-width': p.webT / 2, 'stroke-linecap': 'butt' }));
          g.appendChild(el('line', { x1: p.lineX1, y1: lineY + p.webT / 4, x2: p.lineX2, y2: lineY + p.webT / 4, stroke: p.lower, 'stroke-width': p.webT / 2, 'stroke-linecap': 'butt' }));
        } else {
          g.appendChild(el('line', { x1: p.lineX1, y1: lineY, x2: p.lineX2, y2: lineY, stroke: p.top, 'stroke-width': p.webT, 'stroke-linecap': 'butt' }));
        }

        // (3) overlap tails, FLUSH with the line's end at the throat (the
        // hairpin's outer strand continues into the tail's centreline) and
        // on the side that end folds to. Tail length is REAL at the piece's
        // end scale (pattern length + 5 mm tab). No fold dimensions here —
        // those live in the zoomed end view only.
        // The tail's colors do NOT depend on the fold direction: either
        // way the webbing turns over on itself, so the face showing on the
        // tail's OUTER side is the line's underside. drawOverlap draws
        // exactly that.
        [
          p.hasLoop1 && { end: p.end1, dir: -1, throat: p.leftThroat, tail: p.tailEnd1 },
          p.hasLoop2 && { end: p.end2, dir: 1, throat: p.rightThroat, tail: p.tailEnd2 },
        ].filter(Boolean).forEach(({ end, dir, throat, tail }) => {
          const up = !!end.tailUp;
          drawOverlap(g, throat, tail, lineY + (up ? -p.webT : p.webT), p.webT, p.top, p.lower, p.twoTone);
          // Faint stitching through the sewn stack — the same shared
          // construction as the zoomed view and the PDP docs side figure,
          // mirrored with the end it belongs to.
          const pat = end.pattern;
          drawSideStitches(g, {
            pattern: pat, throatX: throat, dir,
            patchMm: pat && pat.patternLengthMm != null ? pat.patternLengthMm : null,
            pxPerMm: p.endPxPerMm, lineY, webT: p.webT,
            topColor: p.top, lowerColor: p.lower, twoTone: p.twoTone, flipY: up,
          });
        });
      }

      // A piece's length dimension: two witness ticks, the dimension line,
      // both arrowheads, the role label, and THE VALUE — which is also the
      // control that opens this line's length editor.
      //
      // THE DRAFTING IS v3's, restored in round 5 (Jerry's pick from an
      // interactive mockup of three options). The value sits INLINE, in a
      // text-sized gap cut out of the line, whenever the span can hold it
      // and still read as a dimension on both sides; when it cannot, the
      // line draws unbroken and the value floats CLEAR of it, on the side
      // `opts.floatDir` names — mainline UP, band rows DOWN (see the
      // callers, and the DIM_Y budget for why each way). Standard drafting
      // for a dimension too small for its own number, and it costs no
      // canvas: DIM_FOOT already reserves what a floated value lands in.
      //
      // THE VALUE IS THE CONTROL. It renders as a chip — the scene's own
      // _hotspot, so it carries the same keyboard and AT semantics every
      // other target here does — with a permanent faint underline at rest,
      // because a touch screen has no hover and an affordance that only
      // exists under a pointer does not exist on a phone. Activating it
      // opens the builder's ONE editor pill at this dimension.
      //
      // This is a length hotspot again after 2026-08-01 removed one, and
      // the two are not the same object. What went was a frame around the
      // WHOLE ROW (Jerry: "what does the clickable area on the dimension
      // bars do? Seems like nothing, so can we remove that?") whose only
      // jobs were focusing an editor already drawn inside it and choosing
      // which of two editors was on screen — both no-ops by the time he
      // asked. What is here is a value-sized target that OPENS an editor
      // which does not otherwise exist: the only pointer route to a
      // control that is not on the page until you ask for it. See
      // _drawPieceHotspots for the same note from the hit-area side.
      //
      // WHILE THIS ROW'S PILL IS OPEN (`model.lengthOpen` names this
      // anchor) the value is not SHOWN — the pill REPLACES it. Two copies
      // of one number a hand's width apart is a proofreading exercise, not
      // a diagram, and it is why round 4's "stow the value, reveal it
      // mid-tween" machinery went with the permanently-open boxes it
      // existed for. The row still publishes its ANCHOR on every draw,
      // open or not: the pill is re-pinned off it after each redraw, and
      // the span moves under it on every keystroke.
      //
      // It is a CLASS, not a branch (2026-08-02 — Jerry: "the custom
      // length loads very slowly with this new setup"). The row draws its
      // chip and BOTH dimension-line variants — the split pair an inline
      // value sits in, and the unbroken line that replaces them once the
      // value leaves — every pass, and `is-lenopen` picks (bc-segment-builder.css).
      // That is what lets setLengthOpen open and close the pill on the
      // drawn svg instead of the builder paying a full _draw to hide one
      // number. Both states are therefore always in the DOM: the initial
      // one is set here so a REAL render lands where the toggle would
      // have, and nothing else in this function may branch on `open`.
      //
      // The role LABEL is never suppressed for the pill, which was the one
      // collision worth checking: the pill's near edge stops ED_CLEAR (22)
      // off the line, so on the mainline its BOTTOM sits at y−22, four
      // units above the label's ink top at y−18, and on a band row it is
      // below the line entirely with the label above it. They cannot meet.
      //
      // With NO overlay layer (`model.lengthEditors` false — any embed of
      // this element outside the builder) the value renders as plain
      // drafting text with no chip and no target: there is nothing for it
      // to open, and a control that does nothing is worse than none.
      //
      // `opts.shortfall` (backup only) reddens the FRAME — and only that;
      // the words that go with the red are the builder's, see below.
      //
      // `opts.label` is THE label for its line, and the only one on the
      // canvas (Jerry, 2026-08-01: "the mainline and backup labels are
      // doubled now... the one on the dimension is the one that should
      // stay"). A second set was briefly written on the drawn lines
      // themselves, on the theory that a name over the art reads faster
      // than one on a row across the canvas; two names per line read as a
      // bug instead, and this is the pair he kept. So a hung piece still
      // can't carry a centred caption of its own — that band is where its
      // connector ties are — and the name belongs with the length anyway.
      //
      // It carries the WEBBING with the role ("MAINLINE · MANTRA MK4",
      // the name in the lighter half), because a build whose three lines
      // can be three different products makes "what each webbing is" two
      // questions at once, and this row is the only place either gets
      // answered. The name degrades away on a band with no room for it —
      // the role never does.
      //
      // Every line takes one, INCLUDING a solo mainline. An earlier round
      // gated the mainline's on there being another line to disambiguate
      // it from; Jerry reversed that when he chose this label ("it doesn't
      // show when there is only a mainline, but it should"), and he is
      // right that the row is not only saying which line — with the
      // webbing on it, it is saying what the thing is at all.
      _drawLengthDim(g, p, y, anchor, opts) {
        const o = opts || {};
        const { shortfall, label } = o;
        // -1 = a floated value (and the editor pill) go ABOVE this row,
        // +1 = below. Defaults to below, the side with open canvas under
        // it; every caller is explicit (see _drawFull).
        const floatDir = o.floatDir === -1 ? -1 : 1;
        // Is there an overlay layer for a chip to open a pill INTO?
        const editable = !!(this._model && this._model.lengthEditors);
        // ...and is THIS row's pill the one that is open? Then the pill is
        // holding the number and this row's chip starts out hidden. It is
        // the INITIAL state only — setLengthOpen owns it from here.
        const open = editable && this._model.lengthOpen === anchor;
        const dimG = el('g', {
          class: `bc-segment__dimension${shortfall ? ' is-warn' : ''}${open ? ' is-lenopen' : ''}`,
        });
        // The handle setLengthOpen toggles by. On the dimension GROUP and
        // on the chip group both, since the two are siblings under `g`
        // (paint order: the chip has to land over the row).
        if (editable) dimG.setAttribute('data-bc-lenrow', anchor);
        const lx = p.x0 + 15;
        const rx = p.x1 - 15;
        const cx = (p.x0 + p.x1) / 2;
        // THE VALUE. Sized at drawDim's own 6-units-per-character estimate
        // for the 11px dim-label size, plus the pencil where the row is
        // editable, plus 5 units of air either side. Drawn from the model
        // whether the pill is open or not — an open row hides its chip, it
        // does not draw a different row.
        const stated = String(p.lengthText || '');
        // A length CAN be unset — clear a custom-length field and commit and
        // `lengthText` is empty — and an empty row would then have no chip,
        // which would leave the pointer with no way back into the editor at
        // all (only the build sheet's Length row). So an editable row with
        // no value states an em dash instead: the drafting convention for a
        // dimension not yet decided, and something to tap. A row with no
        // overlay behind it draws nothing here, as it always has: there is
        // nothing to open, so an em dash would be a promise it can't keep.
        const placeholder = editable && !stated;
        const text = placeholder ? '—' : stated;
        const w = text.length * 6;
        // Does this value carry the pencil? Every EDITABLE one does, and
        // only those: a read-only embed's value is drafting text, and a
        // glyph promising an editor there would be a lie. The em-dash
        // placeholder emphatically does — it is the one row where the
        // number is missing and the cue is all there is to go on.
        //
        // It is redundantly true inside the chip branch below (that branch
        // IS editable-with-text). The guards there are kept anyway so that
        // every number sized for the glyph — the hole, the chip's width,
        // the text's offset, the glyph itself — is written off ONE flag and
        // cannot half-apply if the rule for carrying a pencil ever narrows.
        const pen = editable && !!text;
        // Ink width of the value UNIT (number + gap + pencil), which is
        // what the row has to make room for from here on.
        const inkW = w + (pen ? PEN_TOT : 0);
        const gap = text ? inkW + 10 : 0;
        // INLINE = the span can hold the value AND still read as a
        // dimension on both sides of it: 12 units per side, being the
        // 8-unit arrowhead plus 4 of line. It is the everyday case,
        // including a short extender on a phone, because "12 m" is ~28
        // units of ink and ~41 with its pencil — which is the whole reason
        // the band's two pieces can share one row again. (The mockup
        // arrived at the same threshold from the other end, measuring ink
        // width + 26 for the two arrowheads' clearance; this is v3's
        // arithmetic, ink + 34, so it floats a hair earlier and never
        // later.) Measured against `inkW`, so the pencil's 13 units count:
        // a span that would only fit the bare number now drops the value
        // below the line instead of hanging the glyph over an arrowhead.
        // The threshold itself is unchanged — the ink it measures grew.
        const inline = !!text && (rx - lx) - gap >= 24;
        // Baselines. INLINE (+4) centres 11px ink on the dimension line,
        // the same nudge drawDim's inline labels use. FLOATED is either
        //   +28  below the row: ink to +30, underline +31, chip hit rect
        //        +14..+34, which lands exactly on DIM_FOOT's 34;
        //   −26  above it: ink −34..−24, so 6 clear of the role label's ink
        //        at −18, with the chip's hit rect −40..−20 clearing it by 2.
        //        Only the mainline's row floats up, and there its chip top
        //        sits at y 32 against the caption's baseline at 22.
        const valY = inline ? y + 4 : y + (floatDir === -1 ? -26 : 28);
        dimG.appendChild(el('line', { class: 'bc-segment__dim-ext', x1: lx, y1: y - 10, x2: lx, y2: y + 19 }));
        dimG.appendChild(el('line', { class: 'bc-segment__dim-ext', x1: rx, y1: y - 10, x2: rx, y2: y + 19 }));
        if (inline) {
          dimG.appendChild(el('line', { class: 'bc-segment__dim-line bc-segment__dim-line--split', x1: lx, y1: y, x2: Math.max(lx, cx - gap / 2), y2: y }));
          dimG.appendChild(el('line', { class: 'bc-segment__dim-line bc-segment__dim-line--split', x1: Math.min(rx, cx + gap / 2), y1: y, x2: rx, y2: y }));
          // ...and the same row with its pill OPEN: the value has left the
          // line, so the hole it sat in closes up. Drawn now and hidden by
          // CSS until then, which is the whole of what makes opening a
          // pill a class toggle rather than a redraw. Only where a chip
          // can open one — a non-editable embed has no second state.
          if (editable) {
            dimG.appendChild(el('line', { class: 'bc-segment__dim-line bc-segment__dim-line--solid', x1: lx, y1: y, x2: rx, y2: y }));
          }
        } else {
          // One unbroken line beats two zero-length stubs (drawDim's own
          // rule, applied here). Also the floated case and the
          // no-length-yet case — neither has anything to leave a hole for,
          // and this line serves the open state as it stands.
          dimG.appendChild(el('line', { class: 'bc-segment__dim-line', x1: lx, y1: y, x2: rx, y2: y }));
        }
        dimG.appendChild(dimArrow(lx, y, -1, 8));
        dimG.appendChild(dimArrow(rx, y, 1, 8));
        if (label) {
          // The label sits at the left end of the span it names, ink at
          // y−18 to y−10. It can only ever meet the VALUE, and only when
          // that value is inline (the `room` test below); the editor pill
          // is 22 units clear of the line on whichever side and cannot
          // reach it.
          //
          // Clamped only against the CANVAS edge: the extender's band ends
          // where the row does, so on a full-width row its label is the one
          // thing here that can run off the right of the viewBox.
          const labX = Math.min(lx + 8, Math.max(0, this._vbW - 4 - label.length * 6.6));
          // Room before the label would run into the value. Only an INLINE
          // value is in its way — floated, the number is off the line and
          // the whole span is the label's.
          //
          // Measured against the CLOSED row, always: `inline` no longer
          // collapses while the pill is open (it is the same drawn row
          // either way now), so a label that could not fit its webbing
          // name beside the value no longer gains one for as long as the
          // pill is up and loses it again on Done. The label is a fixed
          // thing about the line; it should not flicker because you
          // touched the number.
          //
          // Untouched by the pencil, and it did not need to be: the test
          // is written off the HOLE's left edge (cx − gap/2), and the hole
          // and the text both moved left by the same 6.5 when the glyph
          // was added right of the number. The 8-unit clearance it asks
          // for is still 8 units of clearance from the same ink.
          const room = (inline ? cx - gap / 2 - 8 : rx) - labX;
          // ~6.6 units per character at the 9.5px uppercase size (~0.65 em
          // of advance plus the 0.04em letter-spacing), rounded UP:
          // over-estimating drops the name a little early, while
          // under-estimating runs it under the value. Same estimate-don't-
          // measure trick drawDim uses — a getBBox here would be
          // synchronous layout on a surface that redraws per keystroke.
          // The size itself is set in brand.css, and it is what it is so
          // that this test passes in as many builds as geometry allows.
          const name = (p.entry && p.entry.label) || '';
          const t = el('text', {
            class: 'bc-segment__dim-role',
            x: labX, y: y - 10, text: label,
          });
          if (name && (`${label} · ${name}`).length * 6.6 <= room) {
            t.appendChild(el('tspan', {
              class: 'bc-segment__dim-role-name', text: ` · ${name}`,
            }));
          }
          g.appendChild(t);
        }
        // `shortfall` reddens the frame and NOTHING ELSE (Jerry,
        // 2026-08-01: "the error that shows up under the backup bleeds onto
        // the 'recommended' length indicator below the length box. Maybe
        // the recommended text below the backup length can turn into that
        // error?"). This used to write its own "Shorter than the
        // recommended N m" under the row, which is the same strip of canvas
        // the builder's recommendation line occupies. Two statements about
        // one number, drawn by two layers that cannot see each other,
        // landing on top of each other. They are ONE slot now, and it is
        // the builder's: it already recomputes that line per keystroke and
        // is the side that knows the validation state. It rides the
        // backup's editor pill, under the input the number is about —
        // which means it is only visible while that pill is OPEN, and the
        // red frame here is what carries the warning the rest of the time
        // (with the build sheet's own "N m short right now" note, which is
        // always on screen). The scene keeps the frame, which is the part
        // that is geometry.
        g.appendChild(dimG);
        if (text) {
          if (!editable) {
            // No overlay layer, so nothing to open: plain drafting text.
            dimG.appendChild(el('text', {
              class: 'bc-segment__dim-label', x: cx, y: valY,
              'text-anchor': 'middle', text,
            }));
          } else {
            // The chip, appended AFTER dimG so it paints over the row, and
            // BEFORE the end hotspots (_drawFull draws those last), so an
            // end still wins any overlap a squeezed band could produce.
            //
            // The hit rect is padded well past the ink on both axes: 20
            // tall × (ink + 18) wide, which on a phone — where the viewBox
            // tracks the container 1:1, so a unit is a CSS px — is a ~20 ×
            // 55 target for "30 m" (46 before the pencil widened the ink).
            // Not the 44px square the touch
            // guidelines ask for, and it cannot be: this is an annotation
            // inside a drawing, and a taller box would cross the witness
            // ticks inline and the canvas foot floated. The build sheet's
            // Length row is the full-size target for the same action, and
            // it is the reason this one is allowed to be small.
            // The em-dash placeholder is ONE character wide, so its chip
            // would be a ~24-unit target — under half what a real value
            // gets, on the one row where the customer has the least idea
            // what to do. Floored at the width of "00 m" so an unset
            // length is no harder to hit than a set one.
            //
            // The pencil is INSIDE this rect, not beside it: the floor
            // applies to the number alone and the glyph's 13 units are
            // added after, so the padding either side is still 9 and the
            // whole unit — number, gap, pencil — is one target. (It has to
            // be. A pencil you can see and cannot press would be the worst
            // of both.)
            const hw = Math.max(w, 24) + (pen ? PEN_TOT : 0) + 18;
            const hx = cx - hw / 2;
            const hy = valY - 14;
            // CENTRING. The value unit is number + gap + pencil, and it is
            // the UNIT that centres on cx — the text shifts left by half
            // the pencil's total, the pencil hangs off its right. The
            // alternative (text stays on cx, pencil hangs outside) was
            // rejected because cx is not decoration here: it is the middle
            // of the span being measured, the middle of the hole the
            // dimension line opens, and the middle of the chip's own tint
            // and underline. Hanging the glyph off one side would throw
            // the ink off all three at once and the row would read as
            // measuring something 6.5 units left of what it measures.
            //
            // Falls out symmetric: the hole grows by 13 (PEN_TOT above),
            // so its left edge moves left by exactly the 6.5 the text did,
            // and both ends keep the 5 units of air they always had.
            const tx = pen ? cx - PEN_TOT / 2 : cx;
            const chipG = this._hotspot(g, {
              x: hx, y: hy, w: hw, h: 20,
              part: anchor,
              // "Mainline length 30 m — edit": the role first (three of
              // these can be on the canvas at once), then the value, then
              // what activating does. An unset one says so in words — an
              // em dash read aloud is nothing at all.
              label: placeholder
                ? `${label ? `${label} ` : ''}length not set — edit`
                : `${label ? `${label} ` : ''}length ${text} — edit`,
              // NOT the dashed end-hotspot outline. At this size a dashed
              // rectangle reads as a drawing element rather than an
              // affordance, and it only appears on hover anyway — which is
              // the one thing this control cannot rely on. Tint + a
              // permanent underline instead.
              outline: false,
              extra: (hg) => {
                hg.appendChild(el('rect', {
                  class: 'bc-segment__lenchip-bg',
                  x: hx, y: hy, width: hw, height: 20, rx: 6,
                }));
                hg.appendChild(el('text', {
                  class: `bc-segment__dim-label${placeholder ? ' bc-segment__dim-label--unset' : ''}`,
                  x: tx, y: valY, 'text-anchor': 'middle', text,
                }));
                // The pencil, on the number's right with PEN_GAP of air,
                // centred on the FIGURES' optical middle rather than the
                // baseline: 11px digits stand ~8 units tall, so their
                // middle is 4 above valY and the glyph's ink runs
                // valY−8.7 .. valY+1 — clear of the underline at +3 and
                // well inside the hit rect at valY−14 .. valY+6.
                //
                // NOT dimmed on the placeholder row the way the em dash
                // is: the dash is faint because it is standing in for a
                // number that isn't there, while the pencil is saying the
                // same thing it says everywhere else, and that row needs
                // it said loudest.
                if (pen) {
                  hg.appendChild(penIcon(
                    tx + w / 2 + PEN_GAP, valY - 4 - PEN_W / 2, PEN_W,
                  ));
                }
                // The permanent affordance: faint enough at rest to read
                // as part of the drafting, darker on hover/focus. It has
                // to say "control" with no pointer anywhere near it, and
                // since 2026-08-02 it says it with the pencil above rather
                // than alone — a line under a number is an affordance you
                // have to already know, a pencil names the verb.
                //
                // It spans the whole UNIT (it is derived from hx/hw, which
                // grew with the glyph), so it runs under number AND
                // pencil. Deliberate: stopping at the text would put an
                // off-centre line inside a centred tint, and the two marks
                // are one control — the pencil sitting ON the line is the
                // reading we want, not the pencil beside it.
                hg.appendChild(el('line', {
                  class: 'bc-segment__lenchip-ul',
                  x1: hx + 9, y1: valY + 3, x2: hx + hw - 9, y2: valY + 3,
                }));
              },
            });
            // The other half setLengthOpen toggles. Hidden (bc-segment-builder.css)
            // rather than absent while this row's pill holds the number —
            // display:none takes it out of hit testing and out of the AT
            // tree too, so it is as gone as it used to be by not being
            // drawn, and it comes back carrying the CURRENT value because
            // every draw since has kept it up to date underneath.
            chipG.setAttribute('data-bc-lenrow', anchor);
            if (open) chipG.classList.add('is-lenopen');
          }
        }
        // The anchor is the PILL's spot, not the dimension's: the value's
        // own x (cx), and ED_CLEAR off the line on the side this row
        // floats to. `side` tells the builder which way the pill grows
        // from here — it puts the pill's NEAR edge on this point — so the
        // gap is exactly ED_CLEAR whatever the pill's height turns out to
        // be, and a phone's taller pill and a desktop's shorter one hang
        // off one rule.
        this._anchors[anchor] = { x: cx, y: y + floatDir * ED_CLEAR, side: floatDir };
      }

      // A piece's hit areas: its two ENDS, and nothing else — the same set
      // for every role since 2026-08-01, when the backup/extender body
      // hotspot and then every line's length-row hotspot were dropped (see
      // below). Ids are role-scoped, the mainline's bare (normPart treats
      // the two as one). Each end is measured on its own: since round 3
      // the two can fold opposite ways, so their loops (and the "Edit"
      // pills hanging off them) sit at different heights.
      _drawPieceHotspots(g, p, o) {
        const { lineY, pillAbove } = o;
        const id = (part) => (p.role === 'main' ? part : `${p.role}:${part}`);
        const who = p.role === 'main' ? '' : `${roleName(p.role)} `;
        const pillSide = pillAbove ? -1 : 1;
        // One end's drawn extent: its loop straddles its own mid line, and
        // the "Edit" pill hangs off it on the pill's side (5-unit gap +
        // pill + breathing room).
        const geomFor = (hasLoop, eye, end) => {
          const mid = endMidY(lineY, p.webT, end);
          const half = hasLoop ? halfHFor(eye) : p.ghostHalfH;
          const loopEdge = half + p.sleeveT / 2;
          const pillCy = mid + pillSide * (loopEdge + 5 + EDIT_PILL_H / 2);
          return {
            mid,
            half,
            pillCy,
            top: Math.min(mid - loopEdge, pillCy - EDIT_PILL_H / 2) - 6,
            bot: Math.max(mid + loopEdge, pillCy + EDIT_PILL_H / 2) + 6,
          };
        };
        const g1 = geomFor(p.hasLoop1, p.eye1, p.end1);
        const g2 = geomFor(p.hasLoop2, p.eye2, p.end2);
        // End hit areas span each end's DRAWN extent (fold cap → tail end,
        // or the ghost loop on a bare end); they bound the middle of the
        // piece between them. They live on the piece (_pieceGeometry) from
        // when the role label and the body hotspot read the same two
        // numbers; both have since moved off (the label onto the length
        // dimension row, the hotspot deleted), so this is their only
        // reader today — inline them if that stays true.
        const inner1 = p.inner1;
        const inner2 = p.inner2;
        // Zoom-tween windows cover each end's drawn extent, measured from
        // the piece's own band edge (see _endWindow).
        this._endExtents[id('end1')] = {
          ext: (p.hasLoop1 ? p.tailEnd1 : p.x0 + 15 + p.ghostEye) - p.x0,
          x0: p.x0, x1: p.x1, lineY,
        };
        this._endExtents[id('end2')] = {
          ext: p.x1 - (p.hasLoop2 ? p.tailEnd2 : p.x1 - 15 - p.ghostEye),
          x0: p.x0, x1: p.x1, lineY,
        };
        // The MIDDLE of a line is deliberately NOT clickable, on EVERY
        // role. The mainline's went first (Jerry, 2026-07-31): it used to
        // open the orientation panel, but that choice moved into each
        // end's own editor (round 3), and the webbing sits behind the
        // "Change webbing" button beside the stage and on the build sheet
        // — a third route through the middle bought nothing and made the
        // whole line light up on hover. A backup / extender kept a body
        // hotspot onto its webbing only while it had no button of its own;
        // the stage row now carries a Change pill with a fused × per line
        // (Jerry, 2026-08-01: "I dont like the clickable webbing on the
        // backup and extender. Since we have the change and remove as a
        // button now, that can be removed from both"), so the body is art
        // on all three. The build sheet's per-role Webbing row is still
        // the keyboard/AT route to the picker.
        //
        // The LENGTH ROW's whole-row hotspot went the same day, and it is
        // NOT what came back in round 5 (Jerry, 2026-08-01: "what does the
        // clickable area on the dimension bars do? Seems like nothing, so
        // can we remove that?"). It framed the ENTIRE row in a rounded
        // hover outline (dimY−24 .. dimY+22, stretched wherever the editor
        // stepped off the row) and did exactly two things: focus that
        // role's length input, and — on a stage too narrow for both of a
        // split band's editors — choose which of the two was on screen.
        // The first was a no-op wherever you could SEE the frame, because
        // the editor it focused was sitting inside it; the second stopped
        // existing before the day was out, when both editors became
        // permanently visible. So it framed a control with a control
        // already in it, which is what "seems like nothing" was measuring,
        // and it stays gone.
        //
        // What round 5 adds is a different object at a different size:
        // the VALUE ITSELF is a chip (see _drawLengthDim), ~20 × 55 units
        // around the number rather than a frame across the row, and it
        // opens an editor that is NOT on screen until it does. Where the
        // old frame was redundant with what it contained, this is the only
        // pointer route to the pill. It lives in _drawLengthDim rather
        // than here because it is drawn from the value's own position, and
        // it is drawn BEFORE these end hotspots so an end wins any overlap.
        [
          { hasLoop: p.hasLoop1, part: 'end1', dir: -1, throat: p.leftThroat, eyePx: p.eye1, inner: inner1, gm: g1 },
          { hasLoop: p.hasLoop2, part: 'end2', dir: 1, throat: p.rightThroat, eyePx: p.eye2, inner: inner2, gm: g2 },
        ].forEach(({ hasLoop, part, dir, throat, eyePx, inner, gm }) => {
          const x = dir === -1 ? p.x0 + 4 : inner;
          const w = dir === -1 ? inner - (p.x0 + 4) : (p.x1 - 4) - inner;
          const eye = hasLoop ? eyePx : p.ghostEye;
          const ghostThroat = hasLoop ? throat
            : (dir === -1 ? p.x0 + 15 + p.ghostEye : p.x1 - 15 - p.ghostEye);
          const end = part === 'end1' ? p.end1 : p.end2;
          const up = !!(end && end.tailUp);
          this._hotspot(g, {
            x, y: gm.top, w, h: gm.bot - gm.top,
            part: id(part),
            label: hasLoop
              ? `${who}End ${part === 'end1' ? 1 : 2} sewn loop — edit and zoom in`
              : `${who}End ${part === 'end1' ? 1 : 2} — add a sewn loop`,
            // Looped ends get the "Edit" pill hanging clear of the loop
            // (Jerry, 2026-07-31 — in the eye it crowded the strands),
            // centred on the eye; bare ends keep the ghost-loop "+"
            // affordance below.
            extra: hasLoop ? (hg) => {
              hg.appendChild(editBadge(throat + dir * (eye / 2), gm.pillCy));
            } : (hg) => {
              // Ghost affordance on a bare end: dashed hairpin that shows
              // on hover/focus + an always-visible "+" badge. Drawn in the
              // UNFLIPPED frame and mirrored by hairpinPath, like the real
              // loops.
              const gMid = lineY + p.webT / 2;
              hg.appendChild(el('path', {
                class: 'bc-segment__ghost-loop',
                d: hairpinPath(ghostThroat, dir, ghostThroat + dir * p.ghostEye,
                  lineY, gMid - p.ghostHalfH, gMid + p.ghostHalfH, lineY + p.webT,
                  up ? lineY : null),
                fill: 'none',
              }));
              const cx = ghostThroat + dir * (p.ghostEye / 2);
              const cy = up ? 2 * lineY - gMid : gMid;
              // Sized to the ghost eye, same as the "Edit" pill is to a
              // real one — a fixed radius outgrew the loop once it shrank.
              const r = clamp(7, p.ghostHalfH * 0.9, 11);
              const arm = r * 0.45;
              const badge = el('g', { class: 'bc-segment__ghost-plus' });
              badge.appendChild(el('circle', { cx, cy, r }));
              badge.appendChild(el('line', { x1: cx - arm, y1: cy, x2: cx + arm, y2: cy }));
              badge.appendChild(el('line', { x1: cx, y1: cy - arm, x2: cx, y2: cy + arm }));
              hg.appendChild(badge);
            },
          });
        });
      }

      // A clickable + keyboard-operable region dispatching part-click. The
      // dashed outline is the default hover/focus/active affordance
      // (styled in bc-segment-builder.css); `opts.outline === false` skips it for a
      // target that brings its own — today the length VALUE chip, which is
      // the size of the number it sits on and would read as a drawing
      // element inside a dashed box. Everything else the function does —
      // the hotgroup class the affordance CSS hangs off, the is-active
      // state, the role/tabindex/aria-label, the pointer and keyboard
      // activation with its `via` handoff — is what a new target wants and
      // is why the chip goes through here rather than rolling its own.
      // NB: the identifying attribute is data-bc-hotspot, NOT data-bc-part —
      // the builder click-delegates on [data-bc-part] (summary rows), and a
      // matching attribute here would double-activate every hotspot click.
      // The symptom that found it was the toggle-on-open webbing picker
      // closing instantly. That matters again with the length chip, which
      // IS a toggle (a second tap on an open row's value closes its pill).
      _hotspot(parent, opts) {
        const { x, y, w, h, part, label, extra } = opts;
        const active = !!this._model && samePart(this._model.activePart, part);
        const g = el('g', { class: `bc-segment__hotgroup${active ? ' is-active' : ''}`, 'data-bc-hotspot': part });
        if (opts.outline !== false) {
          g.appendChild(el('rect', {
            class: 'bc-segment__hot-outline',
            x, y, width: w, height: h, rx: 10, ry: 10, fill: 'none',
          }));
        }
        if (extra) extra(g);
        const hit = el('rect', {
          class: 'bc-segment__hot',
          x, y, width: w, height: h, fill: 'transparent',
          role: 'button', tabindex: 0, 'aria-label': label,
        });
        // `via` lets the builder move focus into the opened panel on
        // keyboard activation — this rect is destroyed by the re-render,
        // so without a handoff focus would drop to <body>.
        const fire = (via) => this.dispatchEvent(new CustomEvent('bc:segment:part-click', {
          bubbles: true, detail: { part, via },
        }));
        hit.addEventListener('click', () => fire('pointer'));
        hit.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fire('key'); }
        });
        g.appendChild(hit);
        parent.appendChild(g);
        return g;
      }

      // ---- zoomed end view -------------------------------------------------

      // The zoom composition is drawn at the Z_* rows (Z_VB_H tall — the
      // same rows the PDP sewing figure uses) and CENTRED in the canvas,
      // which _draw guarantees is at least that tall. Nothing scales it:
      // this is the surface the drag handles live on, so it draws at its
      // own size and its targets stay the size they are drawn.
      _zoomHost() {
        const dy = (this._vbH() - Z_VB_H) / 2;
        if (!dy) return this.svg;
        const g = el('g', { transform: `translate(0 ${dy.toFixed(2)})` });
        this.svg.appendChild(g);
        return g;
      }

      // `zoom` is parseView's {role, part, end}: the detail figure for ONE
      // end of ONE line. Role-agnostic by design — a backup's end zooms to
      // the same UNFLIPPED composition as the mainline's, because the zoom
      // is a detail figure, not a position diagram.
      _drawEnd(defs, zoom) {
        const vbW = this._vbW;
        const line = this._pieces.find((p) => p.role === zoom.role);
        if (!line) return;
        const end = line[zoom.part];
        if (!end) return;
        // Orientation is per sewn loop, so the DETAIL of one end can be
        // the entry's other face even though the line it grows out of is
        // drawn from `entry` (round 3). The builder resolves both and
        // flags the end; a model that carries the flag without the flipped
        // entry falls back rather than drawing the wrong face silently.
        const entry = (end.zoomFlipped && line.entryFlipped) || line.entry;
        if (!entry) return;
        const endNo = zoom.end;
        // End 1 keeps its fold on the LEFT, mirroring its place in the full
        // view; End 2 folds right (the old loop-detail orientation).
        const dir = endNo === 1 ? -1 : 1;

        const scene = entry.scene || {};
        const host = this._zoomHost();

        if (end.treatment !== 'none') {
          // Sewn end: the shared composition (also drawn by the PDP
          // sewing-docs figure — keep all construction changes in
          // drawEndDetail). `interactive` is what separates the two
          // surfaces: only the builder's zoom view gets drag handles.
          const geo = drawEndDetail(host, defs, {
            vbW, uid: this._uid, dir, entry, scene, end, interactive: true,
            hitScale: this._hitScale(),
          });
          // What a drag on those handles means, in the scale this draw
          // used. `fields` carries each handle's value at render time —
          // the drag adds its travel to THAT, so the gesture is immune to
          // the re-renders it causes.
          this._zoomGeo = {
            part: zoom.key,
            dir,
            pxPerMm: geo.pxPerMm,
            fields: {
              opening: end.openingMm != null ? end.openingMm : geo.openingMm,
              sleeve: end.treatment === 'sleeved' ? (end.sleeveLengthMm || 200) : null,
            },
          };
          return;
        }

        const top = scene.color || '#2b2b2b';
        const lower = scene.colorLower || null;
        const twoTone = !!(scene.twoTone && lower);

        const g = el('g');
        host.appendChild(g);
        const X0 = 42;
        const X1 = vbW - 42;

        // Captions centred over each view — the row constants keep the
        // sleeve arc label (which rises above the loop) clear of them.
        // The top one names the webbing exactly as the sewn composition's
        // does (see drawEndDetail): this is the same zoomed end view, and a
        // loop-less end is the one state where the drawing says least about
        // which line you are looking at.
        const capCx = (X0 + X1) / 2;
        g.appendChild(el('text', { class: 'bc-segment__detail-caption', x: capCx, y: Z_TOP_CAP_Y, 'text-anchor': 'middle', text: entry.label ? `Top view — ${entry.label}` : 'Top view' }));
        g.appendChild(el('text', { class: 'bc-segment__detail-caption', x: capCx, y: Z_SIDE_CAP_Y, 'text-anchor': 'middle', text: 'Side view' }));

        const widthMm = Number(entry.widthMm) || 25;

        {
          // Bare cut end: square-capped strip + line running off the inboard
          // edge. Still a live preview — picking a treatment grows the loop.
          const cutX = dir === -1 ? X0 + 8 : X1 - 8;
          const inX = dir === -1 ? vbW + 2 : -2;
          // Same 1.6 px/mm scale sizes the strip AND the side-view line
          // thickness (true webbing thickness, 2.5-unit floor).
          const stripH = clamp(22, widthMm * 1.6, 46);
          const webT = Math.max(2.5, (Number(entry.thicknessMm) || 3) * 1.6);
          drawTopStrip(g, {
            x: Math.min(cutX, inX),
            y: Z_TOP_CY - stripH / 2,
            len: Math.abs(inX - cutX),
            h: stripH,
            topFace: entry.topFace,
            widthMm: entry.widthMm,
            thicknessMm: entry.thicknessMm,
            clipId: `bc-seg-clip-${this._uid}`,
            roundLeft: false,
            roundRight: false,
          });
          if (twoTone) {
            g.appendChild(el('line', { x1: Math.min(cutX, inX), y1: Z_SIDE_CY - webT / 4, x2: Math.max(cutX, inX), y2: Z_SIDE_CY - webT / 4, stroke: top, 'stroke-width': webT / 2, 'stroke-linecap': 'butt' }));
            g.appendChild(el('line', { x1: Math.min(cutX, inX), y1: Z_SIDE_CY + webT / 4, x2: Math.max(cutX, inX), y2: Z_SIDE_CY + webT / 4, stroke: lower, 'stroke-width': webT / 2, 'stroke-linecap': 'butt' }));
          } else {
            g.appendChild(el('line', { x1: Math.min(cutX, inX), y1: Z_SIDE_CY, x2: Math.max(cutX, inX), y2: Z_SIDE_CY, stroke: top, 'stroke-width': webT, 'stroke-linecap': 'butt' }));
          }
          g.appendChild(el('text', {
            class: 'bc-segment__detail-caption',
            x: X0, y: Z_SIDE_CY + 48,
            text: `No sewn loop on End ${endNo} — pick a treatment below.`,
          }));
          return;
        }
      }
    }

    customElements.define('bc-segment-scene', BcSegmentScene);
  }

  // ---- <bc-sewing-figure> ---------------------------------------------------

  // Docs figure layout rows. The top view is a standalone, LARGER figure
  // (own viewBox, patch dimensions on it); the side view gets the same
  // full-width treatment, rendered DIRECTLY under the top view (Jerry,
  // 2026-07-30) with the written rows below both.
  const J_VB_H = 170;   // join top view
  const J_CAP_Y = 24;
  const J_CY = 84;
  const T_VB_H = 190;   // loop top view
  const T_CAP_Y = 24;
  const T_CY = 92;
  const S_VB_W = 480;   // loop side view (displayed ~360px wide)
  const S_VB_H = 204;   // opening + fold rows below the loop, clear of the sleeve arc
  const S_CAP_Y = 20;
  const S_CY = 92;
  const JS_VB_H = 118;  // join side view
  const JS_CAP_Y = 20;
  const JS_CY = 62;

  // Black/white overlay ink for dimension frames drawn ON the colored
  // strip (the theme's foreground-mix dim colors can vanish there).
  function onStripInk(baseHex) {
    const rgb = hexRgb(baseHex);
    if (!rgb) return 'rgba(255,255,255,0.92)';
    return relLum(rgb) < 0.5 ? 'rgba(255,255,255,0.92)' : 'rgba(28,39,51,0.85)';
  }

  // Patch dimensions (pattern length below the strip, width beside the
  // patch) — shared by the loop and join top views. `box` is
  // drawStitchZone's returned patch box.
  // `widthSide` moves the WIDTH dimension to the other side of the patch
  // ('left'); the default keeps it on the right, which is where every
  // surface drew it before the builder needed the swap (see drawEndDetail).
  function drawPatchDims(g, box, pattern, stripY, stripH, sleeveDrop, baseHex, widthSide) {
    if (!box || !pattern) return;
    if (pattern.patternLengthMm != null) {
      drawDim(g, box.x, box.x + box.w, stripY + stripH + sleeveDrop + 16,
        `${pattern.patternLengthMm} mm`, { tick: 4, arrow: 8, inline: true, labelGap: 6 });
    }
    if (pattern.patternWidthMm != null) {
      const wLabel = `${pattern.patternWidthMm} mm`;
      const wOpts = { tick: 4, arrow: 8, labelGap: 6, color: onStripInk(baseHex) };
      if (widthSide === 'left') {
        // LEFT of the patch. The label reads OUTWARD from the frame
        // (text-anchor 'end'), so it needs its own width of clear canvas
        // to the left of the dimension line — and on this side the patch
        // can sit close to the viewBox edge, which is exactly what the
        // right-hand default was avoiding: the strip runs off-canvas
        // inboard, and with the fold parked at the far edge the piece can
        // fill the whole band (a narrow phone canvas leaves the patch's
        // left edge only ~43 units in). So the frame is pulled toward the
        // patch rather than the number being clipped. It still lands left
        // of box.x in every reachable geometry — the sewn zone's 5 mm tail
        // tab sits between the patch and the strip's own bound, which is
        // what keeps the gap open at the tightest scale.
        const labelW = wLabel.length * 6;   // ~6 units/char at the 11px dim size
        drawVDim(g, Math.max(box.x - 12, labelW + 10), box.y, box.y + box.h,
          wLabel, Object.assign({ labelSide: 'left' }, wOpts));
        return;
      }
      // Right of the patch — the left side can sit at the viewBox edge on
      // the loop view (the strip runs off-canvas inboard).
      drawVDim(g, box.x + box.w + 12, box.y, box.y + box.h,
        wLabel, Object.assign({ labelSide: 'right' }, wOpts));
    }
  }

  // Join-stitch top view: one continuous strip (the two joined pieces run
  // off both edges), the sewing patch centred, and the TOP piece's cut
  // edge as a seam line LEFT of the stitch — two pieces of the same
  // webbing overlap at the join and the upper one ends there (the lower
  // piece's own end hides under the upper layer, so one seam is all a
  // top view shows). The overlap model (Jerry, 2026-07-30) is pattern
  // length + 10 — a 5 mm tab each end, synced onto overlap_length(_mm)
  // by upload_sewing_pattern_images.py --sync-fold — so the seam (the
  // overlap's left end, stitch centred) sits 5 mm before the stitching;
  // the patch-length fallback below encodes the same 5 mm directly.
  function drawJoinTop(svg, opts) {
    const { vbW, uid, web, pattern } = opts;
    const g = el('g');
    svg.appendChild(g);
    const X0 = 42;
    const X1 = vbW - 42;
    g.appendChild(el('text', { class: 'bc-segment__detail-caption', x: (X0 + X1) / 2, y: J_CAP_Y, 'text-anchor': 'middle', text: 'Top view' }));

    const widthMm = Number(web.widthMm) || 25;
    // Same proportional language as the end view: ONE mm→px scale sizes
    // the strip height and the patch. Zoomed right in — the stitching
    // spans ~half the diagram width (Jerry, 2026-07-30) — with a strip
    // height cap so very narrow patches can't blow the strip out of the
    // viewBox.
    const patchMm = pattern && pattern.patternLengthMm != null ? pattern.patternLengthMm : 60;
    const pxPerMm = Math.min((vbW / 2) / patchMm, 72 / widthMm);
    const stripH = clamp(22, widthMm * pxPerMm, 72);
    const stripY = J_CY - stripH / 2;
    drawTopStrip(g, {
      x: -2,
      y: stripY,
      len: vbW + 4,
      h: stripH,
      topFace: web.topFace,
      widthMm: web.widthMm,
      thicknessMm: web.thicknessMm,
      clipId: `bc-seg-clip-${uid}`,
      roundLeft: false,
      roundRight: false,
    });

    const cx = (X0 + X1) / 2;
    const overlapMm = pattern && pattern.overlapMm != null ? pattern.overlapMm : null;
    const seamOffMm = overlapMm != null && overlapMm > patchMm ? overlapMm / 2 : patchMm / 2 + 5;
    const seamX = cx - seamOffMm * pxPerMm;
    // The top piece's cut end with ROUNDED corners (Jerry, 2026-07-30):
    // the piece continues to the right, so the seam's ends curve into the
    // strip edges on that side — a real webbing end's corner radius
    // (~5 mm) at the strip scale.
    const seamR = Math.min(stripH / 3, 5 * pxPerMm);
    g.appendChild(el('path', {
      class: 'bc-segment__strip-outline',
      fill: 'none',
      d: `M ${seamX + seamR} ${stripY} A ${seamR} ${seamR} 0 0 0 ${seamX} ${stripY + seamR}`
        + ` L ${seamX} ${stripY + stripH - seamR}`
        + ` A ${seamR} ${seamR} 0 0 0 ${seamX + seamR} ${stripY + stripH}`,
    }));

    // Patch centred on the strip; the placeholder (no pattern art yet)
    // fills the same bounded zone instead of the whole band.
    const zoneHalf = (patchMm * pxPerMm) / 2 + 20;
    const base = (web.topFace && web.topFace.base) || (web.scene && web.scene.color);
    const box = drawStitchZone(g, {
      pattern,
      lo: cx - zoneHalf,
      hi: cx + zoneHalf,
      stripY,
      stripH,
      cy: J_CY,
      widthMm,
      pxPerMm,
      dir: 1,
      throatX: 0,
      centerX: cx,
      bg: base,
    });
    drawPatchDims(g, box, pattern, stripY, stripH, 0, base);
  }

  // Loop TOP view (docs): the strip from the fold cap, the wider sleeve
  // footprint, the patch with its L × W dimensions. Fold RIGHT (End 2
  // orientation): the machine pattern art runs dense-edge-right, and the
  // dense edge belongs toward the loop — a left fold showed the pattern
  // backwards (Jerry, 2026-07-30). The folded-back tail lies on the
  // UNDERSIDE, so its cut edge draws no seam.
  function drawLoopTopDocs(svg, opts) {
    const { vbW, uid, web, end } = opts;
    const g = el('g');
    svg.appendChild(g);
    const X0 = 42;
    const X1 = vbW - 42;
    const dir = 1;
    g.appendChild(el('text', { class: 'bc-segment__detail-caption', x: (X0 + X1) / 2, y: T_CAP_Y, 'text-anchor': 'middle', text: 'Top view' }));

    const widthMm = Number(web.widthMm) || 25;
    const geo = endGeometry({ X0, X1, vbW, dir, end, drawWCap: 500 });
    const stripH = clamp(22, Math.min(84, widthMm * geo.pxPerMm), 84);
    const stripY = T_CY - stripH / 2;
    drawTopStrip(g, {
      x: Math.min(geo.foldX, geo.inboardX),
      y: stripY,
      len: Math.abs(geo.inboardX - geo.foldX),
      h: stripH,
      topFace: web.topFace,
      widthMm: web.widthMm,
      thicknessMm: web.thicknessMm,
      clipId: `bc-seg-clip-${uid}`,
      roundLeft: false,
      roundRight: true,
    });
    const scene = web.scene || {};
    const coverage = sleeveCoverage(end);
    let sleeveDrop = 0;
    if (end.treatment === 'sleeved') {
      drawTopSleeve(g, {
        foldX: geo.foldX,
        dir,
        len: Math.min(geo.drawW, geo.eyePx * coverage),
        stripY,
        stripH,
        color: scene.sleeveColor || '#c54a2c',
        widthMm,
      });
      // the wider sleeve overhangs the strip — push the length dim clear
      sleeveDrop = Math.min(stripH * 0.225, ((widthMm >= 23 ? 28 : 25.4) / widthMm - 1) * stripH / 2);
    }
    const base = (web.topFace && web.topFace.base) || scene.color;
    const box = drawStitchZone(g, {
      pattern: geo.pattern,
      lo: Math.min(geo.throatX, geo.tailEndX),
      hi: Math.max(geo.throatX, geo.tailEndX),
      stripY,
      stripH,
      cy: T_CY,
      widthMm,
      pxPerMm: geo.pxPerMm,
      dir,
      throatX: geo.throatX,
      bg: base,
    });
    drawPatchDims(g, box, geo.pattern, stripY, stripH, sleeveDrop, base);
  }

  // Loop SIDE view (docs): the hairpin composition with the opening / fold
  // / sleeve dimensions, plus a faint, dimensions-aware representation of
  // the stitching through the layer stack — needle-thread shade through
  // the main line (top half), bobbin-thread shade through the folded-back
  // tail (bottom half), each contrast-shaded against the layer it crosses.
  function drawLoopSideDocs(svg, defs, opts) {
    const { uid, web, end } = opts;
    const vbW = S_VB_W;
    const g = el('g');
    svg.appendChild(g);
    const X0 = 30;
    const X1 = vbW - 30;
    const dir = 1;
    g.appendChild(el('text', { class: 'bc-segment__detail-caption', x: (X0 + X1) / 2, y: S_CAP_Y, 'text-anchor': 'middle', text: 'Side view' }));

    const scene = web.scene || {};
    const top = scene.color || '#2b2b2b';
    const lower = scene.colorLower || null;
    const twoTone = !!(scene.twoTone && lower);
    const sleeveColor = scene.sleeveColor || '#c54a2c';
    const sleeved = end.treatment === 'sleeved';
    const geo = endGeometry({ X0, X1, vbW, dir, end, drawWCap: 400 });
    const { pattern, openingReal, openingMm, foldMm, foldReal, eyePx, foldX, throatX, tailEndX, inboardX } = geo;
    const sx = eyePx / 65;
    const sy = clamp(0.9, sx / 3, 1.5);

    // TRUE-SCALE thickness (Jerry, 2026-07-30): the strands are the
    // webbing's real thickness at the view's mm scale (floored so hairline
    // profiles stay visible); the sleeve overlay adds ~1 mm of sleeve
    // webbing per face on top of that.
    const webT = Math.max(2.5, (Number(web.thicknessMm) || 3) * geo.pxPerMm);
    const sleeveT = webT + Math.max(3, 2 * geo.pxPerMm);

    const gradId = `bc-segld-grad-${uid}`;
    const coverage = sleeveCoverage(end);
    defs.appendChild(sleeveGradient(gradId, sleeveColor, 'right', coverage));

    const halfH = 20 * sy;
    const pin = drawHairpin(g, {
      throatX, dir, eyePx, lineY: S_CY, t: webT, halfH,
      sleeveT, topColor: top, lowerColor: lower, twoTone, sleeved, gradId,
    });
    const lineLo = Math.min(throatX, inboardX);
    const lineHi = Math.max(throatX, inboardX);
    if (twoTone) {
      g.appendChild(el('line', { x1: lineLo, y1: S_CY - webT / 4, x2: lineHi, y2: S_CY - webT / 4, stroke: top, 'stroke-width': webT / 2, 'stroke-linecap': 'butt' }));
      g.appendChild(el('line', { x1: lineLo, y1: S_CY + webT / 4, x2: lineHi, y2: S_CY + webT / 4, stroke: lower, 'stroke-width': webT / 2, 'stroke-linecap': 'butt' }));
    } else {
      g.appendChild(el('line', { x1: lineLo, y1: S_CY, x2: lineHi, y2: S_CY, stroke: top, 'stroke-width': webT, 'stroke-linecap': 'butt' }));
    }
    drawOverlap(g, throatX, tailEndX, S_CY + webT, webT, top, lower, twoTone);

    // Faint stitching through the stack — shared construction, see
    // drawSideStitches.
    drawSideStitches(g, {
      pattern, throatX, dir, patchMm: geo.patchMm, pxPerMm: geo.pxPerMm,
      lineY: S_CY, webT, topColor: top, lowerColor: lower, twoTone,
    });

    // Dimensions (Jerry, 2026-07-30): opening + fold stacked BELOW the
    // loop, pushed clear of the sleeve arc (which wraps under the loop),
    // every value ON its dimension line — the Dimensions diagram's
    // value-in-a-gap construction, including the sleeve arc. Extension
    // lines replace the per-dim ticks: one per left end (throat / tail
    // cut edge), and a SINGLE shared right line at the fold tip reaching
    // up to the loop end. All drawn faint (Jerry, 2026-07-30) so the
    // long verticals — especially the right one crossing the sleeve arc
    // — read as reference, not geometry.
    const eyeLo = Math.min(throatX, pin.foldX);
    const eyeHi = Math.max(throatX, pin.foldX);
    const arcClear = sleeved ? sleeveT / 2 + 18 : webT / 2;
    const dimY1 = pin.botY + arcClear + 16;
    const dimY2 = dimY1 + 26;
    const extTopY = S_CY + webT * 2 + 3;
    drawExtLine(g, eyeLo, extTopY, dimY1 + 4);                  // throat -> opening
    drawExtLine(g, Math.min(tailEndX, pin.foldX), extTopY, dimY2 + 4); // tail cut -> fold
    drawExtLine(g, eyeHi, pin.midY, dimY2 + 4);                 // loop end -> both rows
    drawDim(g, eyeLo, eyeHi, dimY1,
      openingReal ? `Opening ${openingMm} mm` : 'Opening —',
      { cls: 'bc-segment__dim--opening', tick: 0, arrow: 8, inline: true, labelGap: 8 });
    drawDim(g, Math.min(tailEndX, pin.foldX), Math.max(tailEndX, pin.foldX), dimY2,
      foldReal ? `Fold ${foldMm} mm` : 'Fold —',
      { cls: 'bc-segment__dim--fold', tick: 0, arrow: 8, inline: true, labelGap: 8 });
    if (sleeved) {
      const sleeveLabel = end.sleeveLengthMm ? `Sleeve ${end.sleeveLengthMm} mm` : 'Sleeve —';
      const splitX = throatX + dir * eyePx * (1 - coverage);
      const res = drawArcDim(g, defs, {
        d: pin.dCenter,
        cx: (throatX + pin.foldX) / 2,
        cy: pin.midY,
        covered: (p) => p.x >= splitX,
        // Clear of the sleeve band (further out than the builder's
        // tighter offset).
        offset: sleeveT / 2 + 18,
        label: sleeveLabel,
        uid,
        cls: 'bc-segment__dim--sleeve',
        maxLabelX: vbW - 64,
        inlineLabel: true,
      });
      if (!res) {
        const s1 = pin.foldX - Math.min(geo.drawW, eyePx * coverage);
        drawDim(g, s1, pin.foldX, pin.topY - 18, sleeveLabel,
          { cls: 'bc-segment__dim--sleeve', tick: 4, arrow: 8 });
      }
    }
  }

  // Join-stitch SIDE view (docs): the two joined pieces meet in the middle
  // — the left piece dips DOWN and the right piece rides UP at the joint,
  // and the stacked (bumped) region is exactly the overlap length; each
  // piece's cut end sits at the far end of the overlap, drawn ROUNDED
  // (the same clip construction as the loop tail's cap). Strand thickness
  // is the webbing's real thickness at the view's scale (same rule as the
  // loop side view). Stitching runs through both layers with the model's
  // 5 mm margin at each end (overlap = pattern length + 10), and the
  // overlap length is dimensioned below the stack. SCALE + CENTRE are the
  // join top view's exact formula (Jerry, 2026-07-30) so the two stacked
  // figures line up — stitch spans and seams at identical x fractions.
  function drawJoinSideDocs(svg, opts) {
    const { vbW, web, pattern } = opts;
    const g = el('g');
    svg.appendChild(g);
    const X0 = 42;
    const X1 = vbW - 42;
    g.appendChild(el('text', { class: 'bc-segment__detail-caption', x: (X0 + X1) / 2, y: JS_CAP_Y, 'text-anchor': 'middle', text: 'Side view' }));

    const scene = web.scene || {};
    const top = scene.color || '#2b2b2b';
    const lower = scene.colorLower || null;
    const twoTone = !!(scene.twoTone && lower);

    // MUST mirror drawJoinTop's scale (patch spans ~half the width, capped
    // by the strip-height rule) — the side view renders at the same
    // display width directly underneath, so equal px/mm + equal centre
    // keeps seams and stitching vertically aligned between the views.
    const widthMm = Number(web.widthMm) || 25;
    const patchMm = pattern && pattern.patternLengthMm != null ? pattern.patternLengthMm : 60;
    const pxPerMm = Math.min((vbW / 2) / patchMm, 72 / widthMm);
    const overlapMm = pattern && pattern.overlapMm != null ? pattern.overlapMm : null;
    const overlapGeoMm = overlapMm != null && overlapMm > patchMm ? overlapMm : patchMm + 10;
    const webT = Math.max(2.5, (Number(web.thicknessMm) || 3) * pxPerMm);
    const cx = (X0 + X1) / 2;
    const oLo = cx - (overlapGeoMm * pxPerMm) / 2;
    const oHi = cx + (overlapGeoMm * pxPerMm) / 2;
    const ramp = clamp(10, webT * 4, 26);

    // Each piece runs on the shared centreline until the OTHER piece's cut
    // end, then S-ramps half a thickness onto its overlap level. Two-tone
    // uses the same split-halves construction as the straight line (no
    // color flip — neither piece is turned over in a lap join).
    const strandPath = (from, rampStart, rampEnd, level, to, dy) => (
      `M ${from} ${JS_CY + dy} L ${rampStart} ${JS_CY + dy}`
      + ` C ${rampStart + (rampEnd - rampStart) * 0.45} ${JS_CY + dy}`
      + ` ${rampStart + (rampEnd - rampStart) * 0.55} ${level + dy}`
      + ` ${rampEnd} ${level + dy}`
      + ` L ${to} ${level + dy}`
    );
    // Rounded cut end: clip the strand to everything on its own side of
    // the straight overlap zone PLUS a strip rounded only at the cut end
    // (clipPath children union), so the two-tone halves share one smooth
    // cap — drawOverlap's technique adapted to the ramped strand.
    const strandClip = (side) => {
      const id = `bc-seg-jsclip-${(uidCounter += 1)}`;
      const cp = el('clipPath', { id });
      if (side === 'left') {
        cp.appendChild(el('rect', { x: -10, y: -10, width: oLo + 2 + 10, height: JS_VB_H + 20 }));
        cp.appendChild(el('path', { d: stripPath(oLo + 2, oHi, JS_CY, webT, false, true) }));
      } else {
        cp.appendChild(el('rect', { x: oHi - 2, y: -10, width: vbW + 12 - (oHi - 2), height: JS_VB_H + 20 }));
        cp.appendChild(el('path', { d: stripPath(oLo, oHi - 2, JS_CY - webT, webT, true, false) }));
      }
      g.appendChild(cp);
      return id;
    };
    const paint = (d, clipId) => {
      const grp = el('g', { 'clip-path': `url(#${clipId})` });
      if (twoTone) {
        grp.appendChild(el('path', { d: d(-webT / 4), fill: 'none', stroke: top, 'stroke-width': webT / 2, 'stroke-linecap': 'butt' }));
        grp.appendChild(el('path', { d: d(webT / 4), fill: 'none', stroke: lower, 'stroke-width': webT / 2, 'stroke-linecap': 'butt' }));
      } else {
        grp.appendChild(el('path', { d: d(0), fill: 'none', stroke: top, 'stroke-width': webT, 'stroke-linecap': 'butt' }));
      }
      g.appendChild(grp);
    };
    // Left piece first (under), right piece second so it overdraws on top.
    paint((dy) => strandPath(-2, oLo - ramp, oLo, JS_CY + webT / 2, oHi, dy), strandClip('left'));
    paint((dy) => strandPath(vbW + 2, oHi + ramp, oHi, JS_CY - webT / 2, oLo, dy), strandClip('right'));

    // Faint stitching through both layers, 5 mm in from each overlap end:
    // needle-thread shade through the upper piece, bobbin through the
    // lower — the loop side view's construction.
    if (pattern) {
      const m = 5 * pxPerMm;
      const xA = oLo + m;
      const xB = oHi - m;
      const needle = threadInk(pattern.needleThreadColor, top, '#1c2733');
      const bobbin = threadInk(pattern.bobbinThreadColor, lower || top, '#1c2733');
      const yTop = JS_CY - webT + 0.5;
      const yMid = JS_CY;
      const yBot = JS_CY + webT - 0.5;
      const gz = el('g', { opacity: 0.55, 'aria-hidden': 'true' });
      for (let x = xA + 2; x <= xB - 1; x += 7) {
        gz.appendChild(el('line', { x1: x, y1: yTop, x2: x, y2: yMid, stroke: needle, 'stroke-width': 1.6 }));
        gz.appendChild(el('line', { x1: x, y1: yMid, x2: x, y2: yBot, stroke: bobbin, 'stroke-width': 1.6 }));
      }
      g.appendChild(gz);
    }

    drawDim(g, oLo, oHi, JS_CY + webT + 18,
      overlapMm != null ? `Overlap ${overlapMm} mm` : 'Overlap —',
      { cls: 'bc-segment__dim--fold', tick: 4, arrow: 8, inline: true, labelGap: -15 });
  }

  // PDP sewing-docs figure (snippets/bc-sewing.liquid): the sewing pattern
  // drawn ON the webbing instead of as flat artwork.
  //   join, view=side : drawJoinSideDocs (lap-joint stack + overlap dim)
  //   join, else      : drawJoinTop (top view + patch dims)
  //   loop, view=top  : drawLoopTopDocs (large top view + patch dims)
  //   loop, view=side : drawLoopSideDocs (companion for the written dims)
  //   loop, no view   : the builder's combined zoomed end composition
  //                     (drawEndDetail) — legacy/parity path.
  // Config: the per-section <script data-bc-sewing-figure-data> JSON —
  // `webbing` visuals from snippets/bc-sewing-webbing-json.liquid plus
  // `sewing.patterns`, the SAME parsed entries the builder consumes (both
  // render the shared emitter snippets/bc-sewing-patterns-json.liquid), so
  // the two surfaces can't drift. Resolved from the enclosing .bc-sewing
  // first (the PDP shape), else the page's single document-level copy (the
  // batch report page, whose groups share one config). On any config/lookup
  // failure the element leaves its fallback children (the flat pattern
  // <img>) untouched.
  //
  // Variant color awareness: bc-sewing.liquid also renders the recolor
  // system's per-value color map (script[data-bc-variant-color-map], the
  // shared bc-diagram-variant-color-map emitter) next to the config, and
  // the webbing config can carry per-value "variantTopFaces" (whole
  // webbing_pattern top-face swaps — the custom.diagram cross_sections
  // contract, e.g. Jelly PRO Classic/Desert). The figure resolves the
  // watched option's selected value from the MAIN product form (quick-add
  // pickers excluded, same rule as bc-variant-recolor) and re-renders in
  // that variant's colors on every variant change — a full re-render, so
  // the contrast-shaded thread/dimension inks recompute against the new
  // webbing color for free.
  if (!customElements.get('bc-sewing-figure')) {
    class BcSewingFigure extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;
        // The config script tag can be a LATER sibling inside .bc-sewing —
        // defer to DOM-ready while the parser may not have reached it.
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this._render(), { once: true });
        } else {
          this._render();
        }
      }

      _render() {
        let cfg = null;
        const holder = this.closest('.bc-sewing');
        // The enclosing .bc-sewing's own config wins (the PDP shape); a
        // wrapper without one falls back to the page's single document-level
        // copy — the batch report page emits ONE config for all its groups
        // rather than repeating the (identical, several-KB) JSON per group.
        const tag = (holder && holder.querySelector('script[data-bc-sewing-figure-data]'))
          || document.querySelector('script[data-bc-sewing-figure-data]');
        try { cfg = tag ? JSON.parse(tag.textContent) : null; } catch (_e) { cfg = null; }
        if (!cfg || !cfg.webbing) return; // fail safe: keep the flat-art fallback
        this._cfg = cfg;
        let vmap = null;
        const vtag = holder && holder.querySelector('script[data-bc-variant-color-map]');
        try { vmap = vtag ? JSON.parse(vtag.textContent) : null; } catch (_e) { vmap = null; }
        this._vmap = vmap && typeof vmap.colors === 'object' ? vmap : null;
        this._draw(this._variantValue());
      }

      // Selected value of the watched variant option, or null when the
      // product has no variant color data (or the resolver isn't loaded —
      // sections/bc-product-docs.liquid loads bc-variant-resolve.js
      // whenever a color map / pane-swap map exists). `memo` (optional,
      // keyed by option name) lets one refresh pass share the DOM read
      // across the section's figures instead of re-walking per instance.
      _variantValue(memo) {
        const option = (this._vmap && this._vmap.option)
          || (this._cfg.webbing && this._cfg.webbing.variantOption) || null;
        if (!option) return null;
        if (memo && option in memo) return memo[option];
        const value = window.bcVariantResolve?.readSelectedOption(document, option, { exclude: 'quick-add-dialog' }) || null;
        if (memo) memo[option] = value;
        return value;
      }

      // Re-render only when the watched value actually changed (variant
      // changes on other options — Length — keep the current render).
      _refresh(memo) {
        if (!this._cfg) return;
        const value = this._variantValue(memo);
        if (value === this._lastValue) return;
        this._draw(value);
      }

      // The static config overlaid with the selected value's colors:
      //  - variantTopFaces[value] swaps the whole top face (marks and all);
      //  - else a mapped hex recolors the top-face base, taking with it
      //    only the edges that matched the base — the same follow-only-
      //    if-equal membership rule the Dimensions cross-section applies;
      //  - the side-view scene gets the hex as line color, with
      //    colorsLower[value] as the underside when the product maps
      //    per-variant undersides (no entry collapses to single-tone,
      //    mirroring bc-diagrams); a STATIC two-tone underside stays put.
      _effectiveWeb(value) {
        const web = this._cfg.webbing;
        const vmap = this._vmap;
        const hex = value && vmap && typeof vmap.colors[value] === 'string' ? vmap.colors[value] : null;
        const face = value && web.variantTopFaces ? web.variantTopFaces[value] : null;
        if (!hex && !face) return web;
        const scene = Object.assign({}, web.scene);
        const out = Object.assign({}, web, { scene });
        if (hex) {
          const lower = vmap.colorsLower && typeof vmap.colorsLower[value] === 'string'
            ? vmap.colorsLower[value] : null;
          if (lower && lower !== hex) {
            scene.colorLower = lower;
            scene.twoTone = true;
          } else if (vmap.colorsLower) {
            scene.colorLower = null;
            scene.twoTone = false;
          }
          scene.color = hex;
        }
        if (face) {
          out.topFace = face;
        } else if (hex) {
          const f = web.topFace || {};
          out.topFace = Object.assign({}, f, {
            base: hex,
            edges: (f.edges || []).map((e) => (e === f.base ? hex : e)),
          });
        }
        return out;
      }

      // `value` is the watched option's selected value (may be null);
      // callers resolve it so a refresh pass reads the DOM only once.
      _draw(value) {
        const cfg = this._cfg;
        this._lastValue = value;
        const web = this._effectiveWeb(value);
        const patterns = (cfg.sewing && cfg.sewing.patterns) || [];
        const handle = this.getAttribute('data-pattern-handle') || '';
        const pattern = patterns.find((p) => p.handle === handle) || null;
        const kind = (pattern && pattern.kind) || this.getAttribute('data-kind') || 'loop';
        const view = this.getAttribute('data-view') || '';

        const uid = `sewfig${(uidCounter += 1)}`;
        const isJoin = kind === 'join';
        const end = {
          treatment: web.sleeved ? 'sleeved' : 'naked',
          pattern,
          // The pattern's own opening (the builder's default end state), so
          // the figure's eye — and with it the sleeve wrap, which is
          // sleeve/2 over that same eye — matches the builder's default.
          // Passing it explicitly is belt and braces: eyeMmFor falls back to
          // `pattern.openingMm` anyway (Jerry, 2026-07-31).
          openingMm: pattern && pattern.openingMm != null ? pattern.openingMm : null,
          sleeveLengthMm: web.sleeved ? (Number(web.sleeveDefaultMm) || 200) : null,
        };

        let svg;
        if (isJoin && view === 'side') {
          // Same viewBox width as the join top view — the two figures
          // stack at the same display width, so equal viewBox + equal
          // scale keeps their features vertically aligned.
          svg = el('svg', {
            viewBox: `0 0 560 ${JS_VB_H}`,
            role: 'img',
            'aria-label': 'Join stitch, side view with the overlap length',
          });
          drawJoinSideDocs(svg, { vbW: 560, web, pattern });
        } else if (isJoin) {
          svg = el('svg', {
            viewBox: `0 0 560 ${J_VB_H}`,
            role: 'img',
            'aria-label': 'Join stitch shown on the webbing, top view',
          });
          drawJoinTop(svg, { vbW: 560, uid, web, pattern });
        } else if (view === 'side') {
          svg = el('svg', {
            viewBox: `0 0 ${S_VB_W} ${S_VB_H}`,
            role: 'img',
            'aria-label': 'Sewn loop, side view with opening and fold dimensions',
          });
          const defs = el('defs');
          svg.appendChild(defs);
          drawLoopSideDocs(svg, defs, { uid, web, end });
        } else if (view === 'top') {
          svg = el('svg', {
            viewBox: `0 0 560 ${T_VB_H}`,
            role: 'img',
            'aria-label': 'Sewn loop shown on the webbing, top view',
          });
          drawLoopTopDocs(svg, { vbW: 560, uid, web, end });
        } else {
          svg = el('svg', {
            viewBox: `0 0 560 ${Z_VB_H}`,
            role: 'img',
            'aria-label': 'Sewn loop shown on the webbing, top and side view',
          });
          const defs = el('defs');
          svg.appendChild(defs);
          drawEndDetail(svg, defs, {
            vbW: 560,
            uid,
            dir: 1,
            entry: { widthMm: web.widthMm, thicknessMm: web.thicknessMm, topFace: web.topFace },
            scene: web.scene || {},
            end,
          });
        }
        this.replaceChildren(svg);
        // (No stitch-count caption — the count already lives in the
        // written Stitches spec row; drawing it under the figure too was
        // redundant. Removed 2026-07-30.)
      }
    }
    customElements.define('bc-sewing-figure', BcSewingFigure);

    // Variant color awareness: one document-level hook re-renders every
    // figure when the selection changes — the same two signals
    // bc-variant-recolor listens on ('change' fires on radio/dropdown
    // picks before any morph; 'variant:update' covers picker-section
    // morphs that replace the inputs). Guarded by the customElements
    // check above, so a double asset load can't stack listeners.
    const refreshSewingFigures = () => {
      const memo = {}; // one DOM read per watched option per pass
      document.querySelectorAll('bc-sewing-figure').forEach((fig) => {
        if (typeof fig._refresh === 'function') fig._refresh(memo);
      });
    };
    document.addEventListener('change', (e) => {
      if (e.target?.closest?.('fieldset.variant-option, .variant-option--dropdowns')) refreshSewingFigures();
    });
    document.addEventListener('variant:update', refreshSewingFigures);
  }
})();
